import React, { useState, useEffect, useRef, useCallback, useMemo, memo, forwardRef, useImperativeHandle } from 'react';
import ProfileMenu from './ProfileMenu';
import EmojiPicker from '@emoji-mart/react';
import emojiData from '@emoji-mart/data/sets/15/apple.json';
import NotificationPanel from './NotificationPanel';
// Escala compacta del panel (Layout: html.tk-esc80 = zoom 80 %): las coordenadas de pantalla se dividen por este factor
const escZ = () => (document.documentElement.classList.contains('tk-esc80') ? 0.8 : 1);

// Transportadoras del modal de guía: solo se pueden elegir las que Nexus cotizó con valores reales
// y que no fallaron al generar en este pedido (cada intento fallido deja un "Por generar" repetido).
const GUIDE_CARRIERS = ['coordinadora','servientrega','interrapidisimo','envia'];
// Sin recaudo (sr): no hay depósito (value_deposited = 0) y Coordinadora no se permite.
const usableQuote = (quotes, failed, id, sr) =>
  !(failed || {})[id] && (quotes || []).find(q => q.carrier === id && Number(q.flete) > 0 && (sr
    ? id !== 'coordinadora'
    : Number(q.value_deposited) > 0));

// ── Emojis estilo Apple (imágenes de emoji-datasource-apple; si falla, queda el emoji del sistema) ──
const APPLE_EMOJI_CDN = 'https://cdn.jsdelivr.net/npm/emoji-datasource-apple@15.0.1/img/apple/64/';
let _emojiRe = null;
const emojiRe = () => {
  if (_emojiRe) return _emojiRe;
  try {
    _emojiRe = new RegExp('(\\p{Regional_Indicator}{2}|[#*0-9]\\uFE0F?\\u20E3|\\p{Extended_Pictographic}(?:\\uFE0F|\\p{Emoji_Modifier})?(?:\\u200D\\p{Extended_Pictographic}(?:\\uFE0F|\\p{Emoji_Modifier})?)*)', 'gu');
  } catch { _emojiRe = false; }
  return _emojiRe;
};
const emojiCode = (e, keepFe0f) => [...e].map(ch => ch.codePointAt(0).toString(16)).filter(h => keepFe0f || h !== 'fe0f').join('-');
const AppleEmoji = ({ e }) => (
  <img className="ae" alt={e} draggable={false} src={`${APPLE_EMOJI_CDN}${emojiCode(e, true)}.png`}
    onError={ev => {
      const img = ev.currentTarget;
      if (!img.dataset.retry) { img.dataset.retry = '1'; img.src = `${APPLE_EMOJI_CDN}${emojiCode(e, false)}.png`; }
      else { img.replaceWith(document.createTextNode(e)); }
    }} />
);
const withAppleEmoji = (text) => {
  const re = emojiRe();
  if (!re || typeof text !== 'string' || !text) return text;
  const parts = text.split(re);
  if (parts.length === 1) return text;
  return parts.map((p, i) => (i % 2 === 1 ? <AppleEmoji key={i} e={p} /> : p));
};
const withAppleEmojiParts = (parts) => (Array.isArray(parts) ? parts : [parts]).flatMap((p, i) =>
  typeof p === 'string' ? [].concat(withAppleEmoji(p)).map((x, j) => (typeof x === 'string' ? x : React.cloneElement(x, { key: `${i}-${j}` }))) : [p]);

const ChatInput = memo(forwardRef(({ onSend, sending }, ref) => {
  const [text, setText] = useState('');
  const taRef = useRef(null);
  useEffect(() => { const el = taRef.current; if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }, [text]);
  useImperativeHandle(ref, () => ({
    clear: () => setText(''),
    getValue: () => text,
    append: (s) => setText(p => p + s),
    set: (s) => { setText(s); setTimeout(() => taRef.current?.focus(), 30); },
  }));
  return (
    <div className="chat-input-pill">
      <textarea ref={taRef} rows={1} className="ci-input" placeholder="Escribe un mensaje..." value={text} onChange={e => setText(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); if (text.trim() && text.length <= 4096) { onSend(text); setText(''); } } }} />
      {text.length > 0 && <span className={`ci-count${text.length > 4096 ? ' over' : ''}`}>{text.length.toLocaleString('es-CO')} / 4.096</span>}
      <button className="ci-send" disabled={sending || text.length > 4096} onClick={() => { if (text.trim() && text.length <= 4096) { onSend(text); setText(''); } }}>
        {sending ? <div className="spinner" style={{ width: 14, height: 14, borderTopColor: '#fff', borderColor: 'rgba(255,255,255,0.3)' }} /> : <>Enviar <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></svg></>}
      </button>
    </div>
  );
}));

const NAV_ITEMS = [
  { id: 'Dashboard', label: 'Dashboard', icon: (a) => <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><rect x="3" y="3" width="7" height="7" rx="2" fill={a?'#22c55e':'none'} stroke={a?'#22c55e':'#aaa'} strokeWidth="1.6"/><rect x="14" y="3" width="7" height="7" rx="2" fill={a?'#22c55e':'none'} stroke={a?'#22c55e':'#aaa'} strokeWidth="1.6"/><rect x="3" y="14" width="7" height="7" rx="2" fill={a?'#22c55e':'none'} stroke={a?'#22c55e':'#aaa'} strokeWidth="1.6"/><rect x="14" y="14" width="7" height="7" rx="2" fill={a?'#22c55e':'none'} stroke={a?'#22c55e':'#aaa'} strokeWidth="1.6"/></svg> },
  { id: 'Conversaciones', label: 'Conversaciones', icon: (a) => <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" fill={a?'#22c55e':'none'} stroke={a?'#22c55e':'#aaa'} strokeWidth="1.6" strokeLinejoin="round"/></svg> },
  { id: 'Pedidos', label: 'Pedidos', icon: (a) => <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" fill={a?'#22c55e':'none'} stroke={a?'#22c55e':'#aaa'} strokeWidth="1.6" strokeLinejoin="round"/><line x1="3" y1="6" x2="21" y2="6" stroke={a?'#16a34a':'#aaa'} strokeWidth="1.6"/><path d="M16 10a4 4 0 0 1-8 0" stroke={a?'#16a34a':'#aaa'} strokeWidth="1.6" strokeLinecap="round"/></svg> },
  { id: 'Contactos', label: 'Contactos', icon: (a) => <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="8" r="4" fill={a?'#22c55e':'none'} stroke={a?'#22c55e':'#aaa'} strokeWidth="1.6"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" stroke={a?'#22c55e':'#aaa'} strokeWidth="1.6" strokeLinecap="round"/></svg> },
  { id: 'Flujos', label: 'Flujos', icon: (a) => <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><rect x="3" y="3" width="6" height="5" rx="1.5" fill={a?'#22c55e':'none'} stroke={a?'#22c55e':'#aaa'} strokeWidth="1.6"/><rect x="15" y="9" width="6" height="5" rx="1.5" fill={a?'#22c55e':'none'} stroke={a?'#22c55e':'#aaa'} strokeWidth="1.6"/><rect x="3" y="16" width="6" height="5" rx="1.5" fill={a?'#22c55e':'none'} stroke={a?'#22c55e':'#aaa'} strokeWidth="1.6"/><path d="M9 5.5h3a3 3 0 0 1 3 3v3" stroke={a?'#22c55e':'#aaa'} strokeWidth="1.6" strokeLinecap="round"/><path d="M9 18.5h3a3 3 0 0 0 3-3v-3" stroke={a?'#22c55e':'#aaa'} strokeWidth="1.6" strokeLinecap="round"/></svg> },
  { id: 'Marketing', label: 'Marketing', icon: (a) => <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" fill={a?'#22c55e':'none'} stroke={a?'#22c55e':'#aaa'} strokeWidth="1.6" strokeLinejoin="round"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14" stroke={a?'#22c55e':'#aaa'} strokeWidth="1.6" strokeLinecap="round"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07" stroke={a?'#22c55e':'#aaa'} strokeWidth="1.6" strokeLinecap="round"/></svg> },
];

const WaIcon = () => <svg width="12" height="12" viewBox="0 0 24 24" fill="#22c55e"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>;
const IgIcon = () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none"><rect x="2" y="2" width="20" height="20" rx="5" fill="url(#ig)" /><circle cx="12" cy="12" r="4.5" stroke="#fff" strokeWidth="1.5" fill="none"/><circle cx="17.5" cy="6.5" r="1" fill="#fff"/><defs><linearGradient id="ig" x1="2" y1="22" x2="22" y2="2" gradientUnits="userSpaceOnUse"><stop stopColor="#f9ce34"/><stop offset="0.3" stopColor="#ee2a7b"/><stop offset="1" stopColor="#6228d7"/></linearGradient></defs></svg>;
const FbIcon = () => <svg width="12" height="12" viewBox="0 0 24 24" fill="#1877f2"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>;

const DEPTO_POR_CIUDAD = {
  'bogotá':'Cundinamarca','bogota':'Cundinamarca','medellín':'Antioquia','medellin':'Antioquia',
  'cali':'Valle del Cauca','barranquilla':'Atlántico','cúcuta':'Norte de Santander','cucuta':'Norte de Santander',
  'bucaramanga':'Santander','pereira':'Risaralda','santa marta':'Magdalena','cartagena':'Bolívar',
  'ibagué':'Tolima','ibague':'Tolima','pasto':'Nariño','manizales':'Caldas','neiva':'Huila',
  'villavicencio':'Meta','armenia':'Quindío','valledupar':'Cesar','montería':'Córdoba','monteria':'Córdoba',
  'sincelejo':'Sucre','popayán':'Cauca','popayan':'Cauca','tunja':'Boyacá','florencia':'Caquetá',
  'riohacha':'La Guajira','quibdó':'Chocó','quibdo':'Chocó','mocoa':'Putumayo','yopal':'Casanare',
  'arauca':'Arauca','leticia':'Amazonas','soacha':'Cundinamarca','palmira':'Valle del Cauca',
  'bello':'Antioquia','envigado':'Antioquia','itagüí':'Antioquia','itagui':'Antioquia',
  'floridablanca':'Santander','girón':'Santander','giron':'Santander','dosquebradas':'Risaralda',
  'cartago':'Valle del Cauca','buenaventura':'Valle del Cauca','barrancabermeja':'Santander',
  'duitama':'Boyacá','sogamoso':'Boyacá','ocaña':'Norte de Santander','pamplona':'Norte de Santander',
  'maicao':'La Guajira','uribia':'La Guajira','manaure':'La Guajira','fonseca':'La Guajira',
  'san juan del cesar':'La Guajira','villanueva':'La Guajira','albania':'La Guajira',
  'soledad':'Atlántico','sabanalarga':'Atlántico','malambo':'Atlántico',
  'zipaquirá':'Cundinamarca','zipaquira':'Cundinamarca','fusagasugá':'Cundinamarca',
  'fusagasuga':'Cundinamarca','facatativá':'Cundinamarca','facatativa':'Cundinamarca',
  'chía':'Cundinamarca','chia':'Cundinamarca','cajicá':'Cundinamarca','cajica':'Cundinamarca',
  'madrid':'Cundinamarca','funza':'Cundinamarca','mosquera':'Cundinamarca','girardot':'Cundinamarca',
  'sabaneta':'Antioquia','rionegro':'Antioquia','apartadó':'Antioquia','turbo':'Antioquia',
  'tulua':'Valle del Cauca','tuluá':'Valle del Cauca','buga':'Valle del Cauca',
  'piedecuesta':'Santander','lorica':'Córdoba','cereté':'Córdoba','sahagún':'Córdoba',
  'la virginia':'Risaralda','chiquinquirá':'Boyacá',
};
const inferDepto = (city) => city ? (DEPTO_POR_CIUDAD[(city||'').toLowerCase().trim()] || '') : '';

const initials = (name) => {
  if (!name) return '#';
  const clean = name.trim();
  if (!clean) return '#';
  // Si es solo dígitos/símbolos de teléfono, no mostrar números
  if (/^\+?[\d\s()-]+$/.test(clean)) return '?';
  return clean.split(' ').slice(0,2).map(w=>w[0]).join('').toUpperCase();
};
const fmtPhone = (phone) => {
  if (!phone) return '';
  const d = phone.replace(/\D/g, '');
  if (d.length === 12) return `+${d.slice(0,2)} ${d.slice(2,5)} ${d.slice(5,8)} ${d.slice(8)}`;
  if (d.length === 11) return `+${d.slice(0,1)} ${d.slice(1,4)} ${d.slice(4,7)} ${d.slice(7)}`;
  if (d.length === 10) return `${d.slice(0,3)} ${d.slice(3,6)} ${d.slice(6)}`;
  return phone;
};
const AVATAR_COLORS = ['#6ee7b7','#93c5fd','#fca5a5','#fcd34d','#c4b5fd','#86efac','#fdba74','#67e8f9'];
const CARRIERS_MAP = {
  coordinadora:    { label:'Coordinadora',     color:'#e30613', img:'/coordinadora-logo.webp' },
  servientrega:    { label:'Servientrega',     color:'#00529b', img:'/servientrega-logo.jpg' },
  interrapidisimo: { label:'Inter Rapidísimo', color:'#00a651', img:'/interrapidisimo-logo.jpg' },
  tcc:             { label:'TCC',              color:'#e4002b', img:'/tcc-logo.jpg' },
  envia:           { label:'Envía',            color:'#ff6600', img:'/envia-logo.jpg' },
};
const PAYMENT_MAP = {
  cod:            { label:'Contra entrega', img:null },
  cash_on_delivery: { label:'Contra entrega', img:null },
  addi:           { label:'Addi',           img:'/payment-logos/addi.png' },
  sistecredito:   { label:'Sistecrédito',   img:'/payment-logos/sistecredito.png' },
};

const fmtTime = (ts) => {
  if (!ts) return '';
  try {
    const s = String(ts).replace(' ', 'T');
    // DB stores Colombia time (UTC-5) as naive timestamp — append -05:00, not Z
    const d = new Date(s.includes('+') || s.endsWith('Z') ? s : s + '-05:00');
    return d.toLocaleTimeString('es-CO', {hour:'2-digit', minute:'2-digit', timeZone:'America/Bogota'});
  } catch(e) { return ''; }
};

const fmtCOP = (v) => v > 0 ? new Intl.NumberFormat('es-CO',{style:'currency',currency:'COP',maximumFractionDigits:0}).format(v) : '—';

const API = 'https://talku.online';
const authH = (extra = {}) => ({ 'Authorization': `Bearer ${localStorage.getItem('talku_token') || ''}`, ...extra });



const TemplateModal = memo(function TemplateModal({ tpl, onSend, onClose, sending, err }) {
  const [vars, setVars] = useState(() => {
    const bodyComp = tpl.components?.find(c=>c.type==='BODY');
    const matches = bodyComp ? [...new Set((bodyComp.text||'').match(/\{\{(\d+)\}\}/g)||[])].sort() : [];
    return matches.map(()=>'');
  });
  const [headerUrl, setHeaderUrl] = useState('');
  const hasImageHeader = !!(tpl.components?.find(c=>c.type==='HEADER' && c.format==='IMAGE'));
  const allFilled = vars.every(v=>v.trim()) && (!hasImageHeader || headerUrl.trim());
  const bodyText = (tpl.components?.find(c=>c.type==='BODY')?.text||'');

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{width:440}} onClick={e=>e.stopPropagation()}>
        <div className="modal-hdr">
          <div className="modal-title">Completar variables</div>
          <div className="modal-close" onClick={onClose}>{"×"}</div>
        </div>
        <div style={{padding:'4px 0 14px',fontSize:12,color:'#888',fontStyle:'italic',flexShrink:0}}>
          Plantilla: <strong style={{color:'#0a0a0a',fontFamily:'monospace'}}>{tpl.name}</strong>
        </div>
        <div style={{background:'#f8f8f6',borderRadius:10,padding:'12px 14px',fontSize:13,lineHeight:1.6,color:'#444',marginBottom:14,flexShrink:0,borderLeft:'3px solid #e5e5e3'}}>
          {bodyText.replace(/\{\{(\d+)\}\}/g,(m,n)=>{
            const v = vars[parseInt(n)-1];
            return v ? `[${v}]` : m;
          })}
        </div>
        {hasImageHeader && (
          <div style={{marginBottom:12,flexShrink:0}}>
            <div style={{fontSize:12,color:'#888',marginBottom:6,fontWeight:500}}>Imagen del encabezado</div>
            <input
              style={{width:'100%',height:36,padding:'0 10px',border:'1.5px solid #e5e5e3',borderRadius:8,fontSize:13,outline:'none',fontFamily:'inherit',boxSizing:'border-box'}}
              placeholder="URL de la imagen (https://...)"
              value={headerUrl}
              onChange={e=>setHeaderUrl(e.target.value)}
            />
          </div>
        )}
        <div style={{display:'flex',flexDirection:'column',gap:10,flexShrink:0}}>
          {vars.map((v,i)=>(
            <div key={i} style={{display:'flex',alignItems:'center',gap:10}}>
              <span style={{fontSize:12,color:'#888',whiteSpace:'nowrap',minWidth:50,fontFamily:'monospace'}}>{'{{'+(i+1)+'}}'}</span>
              <input
                style={{flex:1,height:36,padding:'0 10px',border:'1.5px solid #e5e5e3',borderRadius:8,fontSize:13,outline:'none',fontFamily:'inherit'}}
                placeholder={i===0?'Ej. Juan Pérez':i===1?'Ej. 98765':'Valor...'}
                value={v}
                onChange={e=>{ const a=[...vars]; a[i]=e.target.value; setVars(a); }}
              />
            </div>
          ))}
        </div>
        {err && <div style={{marginTop:12,fontSize:12,color:'#ef4444',padding:'8px 12px',background:'#fef2f2',borderRadius:8,flexShrink:0}}>{err}</div>}
        <div style={{display:'flex',gap:10,marginTop:18,flexShrink:0}}>
          <button onClick={onClose} style={{flex:1,height:38,borderRadius:9,border:'1.5px solid #e5e5e3',background:'#fff',color:'#555',fontSize:13,cursor:'pointer',fontFamily:'inherit'}}>Cancelar</button>
          <button
            disabled={sending || !allFilled}
            onClick={()=>onSend(tpl, vars, headerUrl)}
            style={{flex:2,height:38,borderRadius:9,border:'none',background:allFilled?'#22c55e':'#ccc',color:'#fff',fontSize:13,fontWeight:700,cursor:allFilled?'pointer':'default',fontFamily:'inherit',display:'flex',alignItems:'center',justifyContent:'center',gap:6}}>
            {sending ? <><div className="spinner" style={{width:13,height:13,borderTopColor:'#fff',borderColor:'rgba(255,255,255,0.3)'}}/>Enviando...</> : '📤 Enviar plantilla'}
          </button>
        </div>
      </div>
    </div>
  );
});

const NOVEDAD_COLORS = {
  'En reparto': '#3b82f6', 'Entregado': '#16a34a', 'En tránsito': '#f59e0b',
  'Llegó a ciudad destino': '#16a34a', 'Dirección incorrecta': '#ef4444',
};

let _activeAudio = null;

const AudioPlayer = memo(function AudioPlayer({ src, isOut, time, onError }) {
  const audioRef = useRef(null);
  const waveRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const bars = useMemo(() => {
    let seed = 0;
    for (let i = 0; i < src.length; i++) seed = ((seed << 5) - seed + src.charCodeAt(i)) | 0;
    return Array.from({ length: 32 }, (_, i) => {
      seed = (seed * 16807 + 7) & 0x7fffffff;
      return 0.15 + (seed % 1000) / 1000 * 0.85;
    });
  }, [src]);

  const fmtDur = (s) => {
    if (!s || !isFinite(s)) return '0:00';
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return m + ':' + (sec < 10 ? '0' : '') + sec;
  };

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) { a.pause(); }
    else {
      if (_activeAudio && _activeAudio !== a) {
        _activeAudio.pause();
        _activeAudio.currentTime = 0;
        _activeAudio.dispatchEvent(new Event('ended'));
      }
      _activeAudio = a;
      a.play().catch(() => {});
    }
  };

  const seek = (e) => {
    const a = audioRef.current;
    const w = waveRef.current;
    if (!a || !w || !duration) return;
    const rect = w.getBoundingClientRect();
    const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
    a.currentTime = (x / rect.width) * duration;
    setCurrentTime(a.currentTime);
  };

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onMeta = () => setDuration(a.duration);
    const onTime = () => setCurrentTime(a.currentTime);
    const onEnd = () => { setPlaying(false); setCurrentTime(0); _activeAudio = null; };
    const onErr = () => { setPlaying(false); if (onError) onError(); };
    a.addEventListener('play', onPlay);
    a.addEventListener('pause', onPause);
    a.addEventListener('loadedmetadata', onMeta);
    a.addEventListener('timeupdate', onTime);
    a.addEventListener('ended', onEnd);
    a.addEventListener('error', onErr);
    return () => {
      a.removeEventListener('play', onPlay);
      a.removeEventListener('pause', onPause);
      a.removeEventListener('loadedmetadata', onMeta);
      a.removeEventListener('timeupdate', onTime);
      a.removeEventListener('ended', onEnd);
      a.removeEventListener('error', onErr);
      if (_activeAudio === a) _activeAudio = null;
    };
  }, [onError]);

  const progress = duration > 0 ? currentTime / duration : 0;

  return (
    <div className={`audio-player ${isOut ? 'out' : 'in'}`}>
      <audio ref={audioRef} src={src} preload="metadata" />
      <button className="ap-play" onClick={toggle} aria-label={playing ? 'Pausar' : 'Reproducir'}>
        {playing ? (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16" rx="1"/><rect x="14" y="4" width="4" height="16" rx="1"/></svg>
        ) : (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
        )}
      </button>
      <div className="ap-body">
        <div className="ap-wave" ref={waveRef} onClick={seek}>
          {bars.map((h, i) => (
            <div key={i} className="ap-bar" style={{
              height: `${h * 100}%`,
              background: i / bars.length <= progress
                ? (isOut ? '#075e54' : '#25d366')
                : (isOut ? 'rgba(7,94,84,0.3)' : 'rgba(0,0,0,0.18)')
            }} />
          ))}
        </div>
        <div className="ap-info">
          <span className="ap-dur">{fmtDur(playing || currentTime > 0 ? currentTime : duration)}</span>
          {time && <span className="ap-time">{time}</span>}
        </div>
      </div>
    </div>
  );
});

const MediaExpired = ({ type }) => (
  <div style={{display:'flex',alignItems:'center',gap:8,padding:'8px 12px',background:'rgba(255,255,255,0.08)',borderRadius:8,fontSize:12,color:'#aaa'}}>
    <span>{type==='audio'?'🎵':type==='video'?'🎬':type==='document'?'📄':'🖼️'}</span>
    <span>{type==='audio'?'[Audio]':type==='video'?'[Video]':type==='document'?'[Documento]':'[Imagen]'} — no disponible</span>
  </div>
);

// ── Plantillas de WhatsApp en el chat: se dibujan tal cual están en Meta ──
const tplComp = (tpl, type) => (tpl?.components || []).find(c => (c.type || '').toUpperCase() === type);
const tplVarRe = /\{\{\s*(\w+)\s*\}\}/g;
const tplReCache = new Map();
// Regex del BODY con {{n}} como comodín (para reconocer mensajes ya renderizados)
const tplBodyRe = (tpl) => {
  const key = `${tpl.id || tpl.name}|${tpl.language || ''}`;
  if (tplReCache.has(key)) return tplReCache.get(key);
  const body = (tplComp(tpl, 'BODY')?.text || '').trim();
  const order = [];
  let re = null;
  if (body.replace(tplVarRe, '').replace(/\s/g, '').length >= 12) {
    const srcRe = body.split(/(\{\{\s*\w+\s*\}\})/).map(part => {
      const v = part.match(/^\{\{\s*(\w+)\s*\}\}$/);
      if (v) { order.push(v[1]); return '([\\s\\S]*?)'; }
      return part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/\s+/g, '\\s*');
    }).join('');
    try { re = new RegExp(`^${srcRe}$`); } catch { re = null; }
  }
  const out = { re, order };
  tplReCache.set(key, out);
  return out;
};
// Devuelve { tpl, body } si el contenido corresponde a una plantilla de Meta
const matchTemplate = (content, defs) => {
  if (!content || !defs || !defs.length) return null;
  const text = String(content).trim();
  const tagged = text.match(/^\[([\w.-]+)\](?:\s+([\s\S]*))?$/);
  if (tagged) {
    // Formato de respaldo del backend: "[nombre_plantilla] valor1 | valor2"
    const tpl = defs.find(t => t.name === tagged[1]);
    if (!tpl) return null;
    const params = tagged[2] ? tagged[2].split(' | ') : [];
    const bodyTpl = tplComp(tpl, 'BODY')?.text || '';
    const vars = [...new Set([...bodyTpl.matchAll(tplVarRe)].map(x => x[1]))];
    if (vars.every(v => /^\d+$/.test(v))) vars.sort((a, b) => a - b);
    const values = {}; vars.forEach((v, i) => { values[v] = params[i] ?? ''; });
    return { tpl, body: bodyTpl.replace(tplVarRe, (_, v) => values[v] ?? '') };
  }
  for (const tpl of defs) {
    const { re } = tplBodyRe(tpl);
    if (re && re.test(text)) return { tpl, body: text };
  }
  return null;
};
// Formato de WhatsApp: *negrita*, _cursiva_, ~tachado~
const waFormat = (text) => {
  const out = [];
  const re = /(^|[\s(¡¿"'])(\*[^*\n]+\*|_[^_\n]+_|~[^~\n]+~)(?=$|[\s.,;:!?)"'])/g;
  let last = 0, k = 0, mt;
  const plain = (s) => { if (s) out.push(<React.Fragment key={`t${k++}`}>{withAppleEmojiParts(s)}</React.Fragment>); };
  while ((mt = re.exec(text))) {
    plain(text.slice(last, mt.index) + mt[1]);
    const s = mt[2], kids = withAppleEmojiParts(s.slice(1, -1));
    out.push(s[0] === '*' ? <strong key={`f${k++}`}>{kids}</strong> : s[0] === '_' ? <em key={`f${k++}`}>{kids}</em> : <s key={`f${k++}`}>{kids}</s>);
    last = re.lastIndex;
  }
  plain(text.slice(last));
  return out;
};
const TPL_BTN_ICON = {
  URL: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>,
  PHONE_NUMBER: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.4 1.8.7 2.7a2 2 0 0 1-.5 2.1L8 9.8a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.7.7a2 2 0 0 1 1.7 2z"/></svg>,
  QUICK_REPLY: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 17 4 12 9 7"/><path d="M20 18v-2a4 4 0 0 0-4-4H4"/></svg>,
  COPY_CODE: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>,
};
const TemplateBubble = ({ tpl, body, mediaUrl, onOpenMedia, renderText }) => {
  const [imgErr, setImgErr] = useState(false);
  const h = tplComp(tpl, 'HEADER'), f = tplComp(tpl, 'FOOTER'), b = tplComp(tpl, 'BUTTONS');
  const fmt = (h?.format || '').toUpperCase();
  const img = mediaUrl || (fmt === 'IMAGE' ? (h?.example?.header_handle || [])[0] : null);
  const ph = (label) => <div className="tplb-ph">{label}</div>;
  return (
    <div className="tplb">
      {fmt === 'IMAGE' && (img && !imgErr
        ? <img className="tplb-media" src={img} alt="" onClick={() => onOpenMedia(img)} onError={() => setImgErr(true)} />
        : ph('Imagen'))}
      {fmt === 'VIDEO' && (mediaUrl ? <video className="tplb-media" controls src={mediaUrl} /> : ph('Video'))}
      {fmt === 'DOCUMENT' && ph('Documento')}
      {fmt === 'LOCATION' && ph('Ubicación')}
      {fmt === 'TEXT' && h.text && <div className="tplb-h">{withAppleEmojiParts(h.text.replace(tplVarRe, '…'))}</div>}
      {body && <div className="tplb-body">{renderText(body)}</div>}
      {f?.text && <div className="tplb-foot">{f.text}</div>}
      {b?.buttons?.length > 0 && (
        <div className="tplb-btns">
          {b.buttons.map((x, i) => <div key={i} className="tplb-btn">{TPL_BTN_ICON[(x.type || '').toUpperCase()] || null}<span>{x.text}</span></div>)}
        </div>
      )}
    </div>
  );
};

export default function Conversations({ user, onNav, onLogout, initialPhone }) {
  useEffect(() => { document.title = 'Talku — Conversaciones'; }, []);
  const uKey = (key) => `${key}_${user?.id || 'default'}`;
  const [activeNav, setActiveNav] = useState('Conversaciones');
  const [showNotifs, setShowNotifs] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [filter, setFilter] = useState('Todos');
  const [searchQuery, setSearchQuery] = useState('');
  const chatInputRef = useRef(null);
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [hasMoreMsgs, setHasMoreMsgs] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [notes, setNotes] = useState([]);
  const [noteSaving, setNoteSaving] = useState(false);
  const noteRef = useRef(null);
  const notesSecRef = useRef(null);
  const [noteInput, setNoteInput] = useState('');
  const [activeTab, setActiveTab] = useState('cliente');
  const [showInfoPanel, setShowInfoPanel] = useState(false);
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const [showOptionsMenu, setShowOptionsMenu] = useState(false);
  const [showCallMenu, setShowCallMenu] = useState(false);
  const [convStatuses, setConvStatuses] = useState({});
  const [shopifyContact, setShopifyContact] = useState(null);
  const [shopifyNames, setShopifyNames] = useState({});
  const [shopifyOrders, setShopifyOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [intTemplates, setIntTemplates] = useState([]);
  const [showOrderSelect, setShowOrderSelect] = useState(false);
  const [showOrderActions, setShowOrderActions] = useState(false);
  const [showAssign, setShowAssign] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [templateSearch, setTemplateSearch] = useState('');
  const [templateTab, setTemplateTab] = useState('meta');
  const [metaTemplates, setMetaTemplates] = useState([]);
  const [metaTplLoading, setMetaTplLoading] = useState(false);
  const [tplPick, setTplPick] = useState(null); // plantilla de Meta en vista previa
  const [tplVals, setTplVals] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionMsg, setActionMsg] = useState('');
  const [shopifyNoteInput, setShopifyNoteInput] = useState('');
  const [shopifyTagInput, setShopifyTagInput] = useState('');
  const [recording, setRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [teamMembers, setTeamMembers] = useState([]);
  const [assignSearch, setAssignSearch] = useState('');
  const [botStatus, setBotStatus] = useState({});
  const [imgModal, setImgModal] = useState(null);
  const [mediaErrors, setMediaErrors] = useState({});
  const [pendingMedia, setPendingMedia] = useState(null); // [{ file, previewUrl, type }] (máx. 10)
  const [mediaProgress, setMediaProgress] = useState(null);
  const [nxShowData, setNxShowData] = useState(false); // datos de envío plegados en el modal Nexus
  const [nxConfirm, setNxConfirm] = useState(false);   // confirmación antes de generar la guía
  const recPeakRef = useRef(0); // nivel máximo captado al grabar (0..1; -1 = sin medidor) // { done, total } mientras se envían varios
  const [sendError, setSendError] = useState('');
  const [showNexusModal, setShowNexusModal] = useState(false);
  const [nexusOrderInfo, setNexusOrderInfo] = useState(null);
  const [nexusForm, setNexusForm] = useState({});
  const [nexusLoading, setNexusLoading] = useState(false);
  const [nexusMsg, setNexusMsg] = useState('');
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [cityQuery, setCityQuery] = useState('');
  const [citySearching, setCitySearching] = useState(false);
  const cityTimeoutRef = useRef(null);
  const contentPrefillRef = useRef(''); // contenido con que abrió el modal (para saber si el usuario lo editó)
  const [shippingQuotes, setShippingQuotes] = useState([]);
  const [quotesLoading, setQuotesLoading] = useState(false);
  const [selectedCarrier, setSelectedCarrier] = useState('coordinadora');
  const [failedCarriers, setFailedCarriers] = useState({});
  // Guía sin recaudo: el cliente no paga al recibir; Nexus cobra producto + envío de la billetera del seller
  const [sinRecaudo, setSinRecaudo] = useState(false);
  const [srForced, setSrForced] = useState(false); // Nexus exige sin recaudo (pedido ya pagado)

  // Si la transportadora elegida no se puede usar, pasar a Coordinadora o a la más económica disponible
  useEffect(() => {
    if (quotesLoading || usableQuote(shippingQuotes, failedCarriers, selectedCarrier, sinRecaudo)) return;
    const opts = GUIDE_CARRIERS.map(id => usableQuote(shippingQuotes, failedCarriers, id, sinRecaudo)).filter(Boolean);
    const pick = usableQuote(shippingQuotes, failedCarriers, 'coordinadora', sinRecaudo)
      || (opts.length ? opts.reduce((m, q) => (Number(q.total || 0) < Number(m.total || 0) ? q : m)) : null);
    const next = pick ? pick.carrier : '';
    if (next !== selectedCarrier) setSelectedCarrier(next);
  }, [shippingQuotes, failedCarriers, quotesLoading, sinRecaudo]); // eslint-disable-line react-hooks/exhaustive-deps
  const [nexusProductImg, setNexusProductImg] = useState(null);
  // Variantes reales de Nexus (selector de color/talla). null = Nexus no las dio → campo de texto de siempre
  const [nxVariants, setNxVariants] = useState(null);
  const [nxVarSel, setNxVarSel] = useState({});   // "item_id:par" → variant_id elegido (combos: un selector por par)
  const [nxVarSaving, setNxVarSaving] = useState(false);
  const [nexusProductTitle, setNexusProductTitle] = useState('');
  const [shipCarrier, setShipCarrier] = useState('coordinadora');
  const [shipGuia, setShipGuia] = useState('');
  const [shipLoading, setShipLoading] = useState(false);
  const [shipMsg, setShipMsg] = useState('');
  const [orderImages, setOrderImages] = useState({});
  const [trackingTimeline, setTrackingTimeline] = useState(null);
  const [trackingTimelineLoading, setTrackingTimelineLoading] = useState(false);
  const [trackingTimelineOpen, setTrackingTimelineOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [exitingNotifs, setExitingNotifs] = useState(new Set());

  // Reply con cita
  const [replyTo, setReplyTo] = useState(null); // { id, content, sender, wamid }

  // Emoji picker
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  // Búsqueda global de mensajes
  const [globalResults, setGlobalResults] = useState([]); // [{ conv_id, conv_name, conv_phone, message_id, content, sent_at, role }]
  const [globalSearchLoading, setGlobalSearchLoading] = useState(false);
  const [searchConvs, setSearchConvs] = useState([]); // chats por nombre / teléfono / pedido en todo el historial
  const extraConvsRef = useRef([]); // chats antiguos abiertos desde la búsqueda (se conservan entre refrescos)
  const globalSearchTimerRef = useRef(null);
  const [storeDomain, setStoreDomain] = useState('');
  useEffect(() => { fetch(`${API}/stores/`, { headers: authH() }).then(r=>r.json()).then(d=>{ if (Array.isArray(d) && d.length) setStoreDomain(d[0].domain); }).catch(()=>{}); }, []); // eslint-disable-line

  // Búsqueda interna del chat (Ctrl+F)
  const [showChatSearch, setShowChatSearch] = useState(false);
  const [chatSearchTerm, setChatSearchTerm] = useState('');
  const [chatSearchIdx, setChatSearchIdx] = useState(0);
  const chatSearchRef = useRef(null);
  const msgRefs = useRef({});

  // Panel izquierdo
  const [listTab, setListTab] = useState('todos'); // 'todos' | 'abiertos' | 'asignados' | 'resueltos'
  const [favConvs, setFavConvs] = useState(() => { try { return new Set(JSON.parse(localStorage.getItem(uKey('talku_favs')) || '[]')); } catch { return new Set(); } });
  const [convLabels, setConvLabels] = useState(() => { try { return JSON.parse(localStorage.getItem(uKey('talku_labels')) || '{}'); } catch { return {}; } });
  const [labelFilter, setLabelFilter] = useState(null); // null | 'pedido' | 'novedad' | 'garantia'
  const [showLabelFilter, setShowLabelFilter] = useState(false);
  const [orderFilter, setOrderFilter] = useState(null);
  const [showOrderFilter, setShowOrderFilter] = useState(false);
  const [sourceFilter, setSourceFilter] = useState(null);
  const [folder, setFolder] = useState('todas'); // carpeta de la columna 1
  const [tenantLabels, setTenantLabels] = useState([]); // etiquetas propias del tenant
  const [labelForm, setLabelForm] = useState(null);     // null | { id?, name, color }
  const labelMap = Object.fromEntries(tenantLabels.map(l => [l.key, l]));
  const [showSourceFilter, setShowSourceFilter] = useState(false);
  const [contextMenu, setContextMenu] = useState(null); // { x, y, convId }

  const toggleFav = (convId) => {
    setFavConvs(prev => {
      const next = new Set(prev);
      next.has(convId) ? next.delete(convId) : next.add(convId);
      localStorage.setItem(uKey('talku_favs'), JSON.stringify([...next]));
      return next;
    });
  };

  const patchConv = async (convId, updates) => {
    try {
      await fetch(`${API}/whatsapp/conversations/${convId}`, {
        method: 'PATCH',
        headers: authH({'Content-Type':'application/json'}),
        body: JSON.stringify(updates)
      });
    } catch {}
  };

  const setLabel = (convId, label) => {
    setConvLabels(prev => {
      const next = { ...prev, [convId]: label };
      localStorage.setItem(uKey('talku_labels'), JSON.stringify(next));
      return next;
    });
    patchConv(convId, { label: label || null });
    setContextMenu(null);
  };

  const toggleBot = async (convId) => {
    const current = botStatus[convId] !== false;
    setBotStatus(prev => ({ ...prev, [convId]: !current }));
    try {
      const res = await fetch(`${API}/whatsapp/conversations/${convId}/toggle-bot`, { method: 'POST', headers: authH() });
      const data = await res.json();
      if (data.ok) setBotStatus(prev => ({ ...prev, [convId]: data.bot_enabled }));
    } catch {}
  };

  // Nueva conversación modal
  const [showNewConv, setShowNewConv] = useState(false);
  const [newConvPhone, setNewConvPhone] = useState('');
  const [newConvName, setNewConvName] = useState('');
  const [newConvContactSearch, setNewConvContactSearch] = useState('');
  const [newConvSelectedContact, setNewConvSelectedContact] = useState(null);
  const [newConvTemplates, setNewConvTemplates] = useState([]);
  const [newConvTemplateSearch, setNewConvTemplateSearch] = useState('');
  const [newConvSelectedTemplate, setNewConvSelectedTemplate] = useState(null);
  const [newConvVariables, setNewConvVariables] = useState([]);
  const [newConvSending, setNewConvSending] = useState(false);
  const [newConvError, setNewConvError] = useState('');
  const [newConvSuggest, setNewConvSuggest] = useState([]);

  // Panel de plantilla Meta para ventana 24h
  const [metaTplToSend, setMetaTplToSend] = useState(null);   // plantilla seleccionada para enviar
  const [metaTplVars, setMetaTplVars]     = useState([]);      // valores de variables {{1}},{{2}}...
  const [metaTplSending, setMetaTplSending] = useState(false);
  const [metaTplErr, setMetaTplErr]       = useState('');
  const [metaTplHeaderUrl, setMetaTplHeaderUrl] = useState('');

  const messagesAreaRef = useRef(null);
  const hasLoadedMoreRef = useRef(false);
  const pollRef = useRef(null);
  const optionsRef = useRef(null);
  const fileInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  const recordingCancelledRef = useRef(false);
  const activeConvRef = useRef(null);
  const activePhoneRef = useRef(null);
  const conversationsRef = useRef([]);
  const prevConvsRef = useRef({});
  const initialPhoneAppliedRef = useRef(null);
  const notifTimersRef = useRef({});
  const swRegRef = useRef(null);

  const STATUS_OPTIONS = [
    { id:'open', label:'Abierto', color:'#16a34a', bg:'#f0fdf4' },
    { id:'waiting_client', label:'Esperando cliente', color:'#d97706', bg:'#fffbeb' },
    { id:'escalated', label:'Escalado a asesor', color:'#ef4444', bg:'#fef2f2' },
    { id:'resolved', label:'Resuelto', color:'#6b7280', bg:'#f4f4f2' },
  ];


  // ── Detecta si la conversación activa es WA fuera de ventana 24h ──
  const isWa24hExpired = useMemo(() => {
    const conv = conversations.find(c => c.id === activeConv);
    if (!conv) return false;
    const ch = (conv.channel || '').toLowerCase();
    if (['instagram','ig','messenger','fb','tiktok'].includes(ch)) return false;
    if (messages.length === 0) return false; // todavía cargando
    const lastIn = [...messages].reverse().find(m => m.role === 'user');
    if (!lastIn) return true; // cliente nunca respondió = siempre fuera de ventana
    const s = String(lastIn.sent_at || '').replace(' ','T');
    const d = new Date(s.includes('+')||s.endsWith('Z') ? s : s+'-05:00');
    return (Date.now() - d.getTime()) > 24 * 60 * 60 * 1000;
  }, [activeConv, messages, conversations]);

  // Plantillas de Meta para dibujar en el chat las que ya se enviaron
  const [tplDefs, setTplDefs] = useState([]);
  const setTplDefsSorted = (list) => setTplDefs([...(list || [])].sort((a, b) => (b.status === 'APPROVED') - (a.status === 'APPROVED')));
  useEffect(() => {
    let off = false;
    fetch(`${API}/whatsapp/templates`, { headers: authH() }).then(r => r.json()).then(d => { if (!off) setTplDefsSorted(d.data); }).catch(() => {});
    return () => { off = true; };
  }, []);
  const tplMatches = useMemo(() => {
    const map = {};
    if (!tplDefs.length) return map;
    messages.forEach(m => {
      const isOut = m.role === 'outbound' || m.role === 'assistant' || m.direction === 'outbound';
      if (!isOut || !m.content) return;
      const hit = matchTemplate(m.content, tplDefs);
      if (hit) map[m.id] = hit;
    });
    return map;
  }, [messages, tplDefs]);

  const sendMetaTemplate = async (tpl, vars, headerUrl) => {
    const conv = conversations.find(c => c.id === activeConv);
    if (!conv || !tpl) return;
    setMetaTplSending(true); setMetaTplErr('');
    try {
      const res = await fetch(`${API}/whatsapp/send-template`, {
        method: 'POST', headers: authH({'Content-Type':'application/json'}),
        body: JSON.stringify({
          phone: conv.contact_phone,
          contact_name: conv.contact_name || '',
          template_name: tpl.name,
          language_code: tpl.language || 'es_CO',
          variables: vars || [],
          header_image_url: headerUrl || metaTplHeaderUrl || '',
        }),
      });
      const data = await res.json();
      if (data.ok) {
        const bodyText = tpl.components?.find(c=>c.type==='BODY')?.text || `[Plantilla: ${tpl.name}]`;
        const preview = (vars||[]).reduce((t,v,i)=>t.replace(`{{${i+1}}}`,v||`{{${i+1}}}`), bodyText);
        const hdrUrl = headerUrl || metaTplHeaderUrl || '';
        setMessages(prev => [...prev, { id:'temp_'+Date.now(), role:'outbound', content:preview, sent_at:new Date().toISOString(), status:'sent', source:'template', media_url: hdrUrl || null, media_type: hdrUrl ? 'image' : null }]);
        setShowTemplates(false); setTplPick(null); setMetaTplToSend(null); setMetaTplVars([]); setMetaTplErr(''); setMetaTplHeaderUrl('');
      } else {
        { const e = data.error; const m = (e && (e.error?.error_data?.details || e.error?.message || e.error_user_msg || e.message)) || (typeof e === 'string' ? e : ''); setMetaTplErr(m ? `WhatsApp rechazó el envío: ${String(m).slice(0,160)}` : 'No se pudo enviar. Intenta de nuevo.'); }
      }
    } catch { setMetaTplErr('Error de conexión'); }
    setMetaTplSending(false);
  };

  const handleMetaTplClick = (tpl) => {
    if (tpl.status !== 'APPROVED') return;
    const bodyComp = tpl.components?.find(c=>c.type==='BODY');
    const headerComp = tpl.components?.find(c=>c.type==='HEADER');
    const vars = bodyComp ? [...new Set((bodyComp.text||'').match(/\{\{(\d+)\}\}/g)||[])].sort() : [];
    const hasImageHeader = headerComp?.format === 'IMAGE';
    if (vars.length > 0 || hasImageHeader) {
      setMetaTplToSend(tpl);
      setMetaTplVars(vars.map(()=>''));
      setMetaTplHeaderUrl('');
      setMetaTplErr('');
    } else {
      sendMetaTemplate(tpl, []);
    }
  };

  // Notas internas por conversación (backend /whatsapp/conversations/{id}/notes)
  useEffect(() => {
    setNotes([]);
    if (!activeConv || String(activeConv).startsWith('temp')) return;
    let cancel = false;
    fetch(`${API}/whatsapp/conversations/${activeConv}/notes`, { headers: authH() })
      .then(r => (r.ok ? r.json() : { notes: [] }))
      .then(d => { if (!cancel) setNotes(d.notes || []); })
      .catch(() => {});
    return () => { cancel = true; };
  }, [activeConv]); // eslint-disable-line
  const saveNote = async () => {
    const t = noteInput.trim(); if (!t || !activeConv) return;
    setNoteSaving(true);
    try {
      const r = await fetch(`${API}/whatsapp/conversations/${activeConv}/notes`, { method: 'POST', headers: authH({ 'Content-Type': 'application/json' }), body: JSON.stringify({ text: t }) });
      const d = await r.json();
      if (r.ok && d.note) { setNotes(prev => [...prev, d.note]); setNoteInput(''); }
    } catch {}
    setNoteSaving(false);
  };
  const deleteNote = async (n) => {
    if (!window.confirm('¿Eliminar esta nota?')) return;
    try { await fetch(`${API}/whatsapp/conversations/${activeConv}/notes/${n.id}`, { method: 'DELETE', headers: authH() }); } catch {}
    setNotes(prev => prev.filter(x => x.id !== n.id));
  };

  const handleNav = (id) => { setActiveNav(id); if (onNav) onNav(id); };

  useEffect(() => {
    const close = () => { setContextMenu(null); setShowLabelFilter(false); setShowEmojiPicker(false); };
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, []);

  // Ctrl+F abre búsqueda interna
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
        e.preventDefault();
        setShowChatSearch(v => { if (!v) setChatSearchTerm(''); return !v; });
        setTimeout(() => chatSearchRef.current?.focus(), 50);
      }
      if (e.key === 'Escape') { setShowChatSearch(false); setChatSearchTerm(''); setShowEmojiPicker(false); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Derived: matching messages for search
  const searchMatches = chatSearchTerm.length >= 2
    ? messages.filter(m => (m.content || '').toLowerCase().includes(chatSearchTerm.toLowerCase()))
    : [];

  const scrollToMatch = useCallback((idx) => {
    const m = searchMatches[idx];
    if (!m) return;
    const el = msgRefs.current[m.id];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [searchMatches]);

  useEffect(() => {
    setChatSearchIdx(0);
    if (searchMatches.length > 0) scrollToMatch(0);
  }, [chatSearchTerm, searchMatches.length]);

  const goSearchPrev = () => {
    const next = (chatSearchIdx - 1 + searchMatches.length) % searchMatches.length;
    setChatSearchIdx(next);
    scrollToMatch(next);
  };
  const goSearchNext = () => {
    const next = (chatSearchIdx + 1) % searchMatches.length;
    setChatSearchIdx(next);
    scrollToMatch(next);
  };

  // Búsqueda global debounced
  useEffect(() => {
    clearTimeout(globalSearchTimerRef.current);
    if (searchQuery.trim().length < 2) { setGlobalResults([]); setSearchConvs([]); setGlobalSearchLoading(false); return; }
    globalSearchTimerRef.current = setTimeout(async () => {
      setGlobalSearchLoading(true);
      try {
        const res = await fetch(`${API}/whatsapp/search?q=${encodeURIComponent(searchQuery)}`, { headers: authH() });
        const data = await res.json();
        setGlobalResults(data.results || []);
        setSearchConvs(data.conversations || []);
      } catch(e) { setGlobalResults([]); setSearchConvs([]); }
      setGlobalSearchLoading(false);
    }, 350);
    return () => clearTimeout(globalSearchTimerRef.current);
  }, [searchQuery]);

  const highlightText = (text, term) => {
    if (!term || term.length < 2 || !text) return text;
    const parts = text.split(new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'));
    return parts.map((part, i) =>
      part.toLowerCase() === term.toLowerCase()
        ? <mark key={i} style={{background:'#fef08a',borderRadius:3,padding:'0 1px',color:'#0a0a0a'}}>{part}</mark>
        : part
    );
  };

  // Celular colombiano sin indicativo (10 dígitos que empiezan por 3) → se antepone 57
  const normPhone = (raw) => {
    let d = String(raw || '').replace(/\D/g, '');
    if (d.length === 10 && d.startsWith('3')) d = '57' + d;
    return d ? '+' + d : '';
  };
  const tplHeader = (tpl) => (tpl?.components || []).find(c => c.type === 'HEADER');
  // Solo se ofrecen plantillas que el panel puede enviar: sin encabezado multimedia o con imagen
  const tplSupported = (tpl) => { const h = tplHeader(tpl); return !h || h.format === 'TEXT' || h.format === 'IMAGE'; };

  const openNewConvModal = async () => {
    setShowNewConv(true);
    setNewConvPhone(''); setNewConvName(''); setNewConvContactSearch(''); setNewConvSelectedContact(null);
    setNewConvSelectedTemplate(null); setNewConvVariables([]); setNewConvError(''); setNewConvTemplateSearch('');
    try {
      const res = await fetch(`${API}/whatsapp/templates`, { headers: authH() });
      const data = await res.json();
      setNewConvTemplates((data.data || []).filter(t => t.status === 'APPROVED' && tplSupported(t)));
    } catch (e) { setNewConvTemplates([]); }
  };

  // Sugerencias de contactos (todo el historial) mientras se escribe en "Para"
  useEffect(() => {
    if (!showNewConv) return;
    const q = newConvContactSearch.trim();
    if (q.length < 2 || newConvSelectedContact) { setNewConvSuggest([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await fetch(`${API}/whatsapp/search?q=${encodeURIComponent(q)}`, { headers: authH() });
        const d = await r.json();
        setNewConvSuggest((d.conversations || []).slice(0, 6));
      } catch { setNewConvSuggest([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [newConvContactSearch, showNewConv, newConvSelectedContact]); // eslint-disable-line

  const selectNewConvTemplate = (tpl) => {
    setNewConvSelectedTemplate(tpl);
    setNewConvError('');
    const text = (tpl.components || []).find(c => c.type === 'BODY')?.text || '';
    const matches = [...text.matchAll(/\{\{(\d+)\}\}/g)];
    const maxIdx = matches.length > 0 ? Math.max(...matches.map(m => parseInt(m[1]))) : 0;
    setNewConvVariables(Array(maxIdx).fill(''));
  };

  const sendNewConversation = async () => {
    const phone = newConvSelectedContact ? normPhone(newConvSelectedContact.contact_phone) : normPhone(newConvContactSearch);
    const name = newConvSelectedContact ? (newConvSelectedContact.contact_name || '') : newConvName.trim();
    if (phone.replace(/\D/g, '').length < 11) { setNewConvError('Escribe un número válido (ej: 300 123 4567) o elige un contacto.'); return; }
    if (!newConvSelectedTemplate) { setNewConvError('Elige una plantilla.'); return; }
    if (newConvVariables.some(v => !String(v).trim())) { setNewConvError('Completa todas las variables de la plantilla.'); return; }
    const h = tplHeader(newConvSelectedTemplate);
    const headerImage = h && h.format === 'IMAGE' ? (h.example?.header_handle || [])[0] : undefined;
    setNewConvSending(true);
    setNewConvError('');
    try {
      const res = await fetch(`${API}/whatsapp/send-template`, {
        method: 'POST',
        headers: authH({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          phone, contact_name: name,
          template_name: newConvSelectedTemplate.name,
          language_code: newConvSelectedTemplate.language,
          variables: newConvVariables,
          ...(headerImage ? { header_image_url: headerImage } : {}),
        }),
      });
      const data = await res.json();
      if (data.ok) {
        setShowNewConv(false);
        await loadConversations();
        if (data.conv_id) setActiveConv(data.conv_id);
      } else {
        const e = data.error;
        const msg = (e && (e.error?.error_data?.details || e.error?.message || e.message)) || (typeof e === 'string' ? e : '');
        setNewConvError(msg ? `WhatsApp rechazó el envío: ${String(msg).slice(0, 160)}` : 'No se pudo enviar. Intenta de nuevo.');
      }
    } catch (e) {
      setNewConvError('Error de red. Intenta de nuevo.');
    }
    setNewConvSending(false);
  };
  useEffect(() => { activeConvRef.current = activeConv; setReplyTo(null); }, [activeConv]);
  useEffect(() => {
    if (!initialPhone || conversations.length === 0) return;
    // Auto-seleccionar solo una vez por cada valor de initialPhone (apertura desde otra
    // pantalla). El polling de conversations no debe re-seleccionar: una selección manual
    // posterior del usuario siempre prevalece.
    if (initialPhoneAppliedRef.current === initialPhone) return;
    initialPhoneAppliedRef.current = initialPhone;
    const digits = initialPhone.replace(/\D/g, '');
    const match = conversations.find(c => c.contact_phone && c.contact_phone.replace(/\D/g, '').endsWith(digits.slice(-10)));
    if (match) setActiveConv(match.id);
  }, [initialPhone, conversations]);


  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js').then(reg => { swRegRef.current = reg; }).catch(() => {});
      const handler = (e) => { if (e.data?.type === 'OPEN_CONV') setActiveConv(e.data.convId); };
      navigator.serviceWorker.addEventListener('message', handler);
      return () => navigator.serviceWorker.removeEventListener('message', handler);
    }
  }, []);

  // Chat abierto (para que Layout no avise de él) y apertura desde la campana o los avisos
  useEffect(() => { window.__talkuActiveConv = activeConv; }, [activeConv]);
  useEffect(() => {
    const onOpen = (e) => { if (e.detail?.convId) setActiveConv(e.detail.convId); };
    window.addEventListener('talku:open-conv', onOpen);
    return () => { window.removeEventListener('talku:open-conv', onOpen); window.__talkuActiveConv = null; };
  }, []);

  const playNotifSound = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      [[880, 0, 0.15], [1108, 0.12, 0.28]].forEach(([freq, start, end]) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.type = 'sine';
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0, ctx.currentTime + start);
        gain.gain.linearRampToValueAtTime(0.22, ctx.currentTime + start + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + end);
        osc.start(ctx.currentTime + start);
        osc.stop(ctx.currentTime + end);
      });
    } catch(e) {}
  };

  const SOCIAL_CHANNELS = ['instagram', 'messenger', 'tiktok'];

  const loadConversations = async () => {
    try {
      const res = await fetch(`${API}/whatsapp/conversations`, { headers: authH() });
      const data = await res.json();
      if (data.conversations) {
        const crmConvs = data.conversations.filter(c => !SOCIAL_CHANNELS.includes(c.channel || ''));
        // Los avisos (sonido, aviso flotante, notificación del sistema) ahora los da Layout para todo el panel
        window.dispatchEvent(new CustomEvent('talku:convs', { detail: data.conversations }));
        crmConvs.forEach(c => { prevConvsRef.current[c.id] = c.unread_count || 0; });
        setBotStatus(prev => {
          const next = { ...prev };
          crmConvs.forEach(c => { if (next[c.id] === undefined) next[c.id] = c.bot_enabled !== false; });
          return next;
        });
        const extras = extraConvsRef.current.filter(e => !crmConvs.some(c => c.id === e.id));
        setConversations(extras.length ? [...crmConvs, ...extras] : crmConvs);
        if (!activeConvRef.current && crmConvs.length > 0) setActiveConv(crmConvs[0].id);
      }
    } catch (e) {}
    setLoading(false);
  };

  const loadMessages = async (convId, silent = false) => {
    if (!convId) return;
    if (!silent) setLoadingMsgs(true);
    try {
      const res = await fetch(`${API}/whatsapp/conversations/${convId}/messages?limit=50`, { headers: authH() });
      const data = await res.json();
      if (data.messages) {
        if (!hasLoadedMoreRef.current) setHasMoreMsgs(!!data.has_more);
        setMessages(prev => {
          const temps = prev.filter(m => {
            if (!String(m.id).startsWith('temp_')) return false;
            if (m.media_type) return !data.messages.some(db => db.role === 'outbound' && db.media_type === m.media_type);
            return !data.messages.some(db => db.role === 'outbound' && db.content === m.content);
          });
          // Preservar mensajes anteriores cargados con "Cargar mensajes anteriores"
          let baseMsgs = data.messages;
          if (hasLoadedMoreRef.current && data.messages.length > 0) {
            const minNewId = Math.min(...data.messages.map(m => Number(m.id)));
            const olderMsgs = prev.filter(m => !String(m.id).startsWith('temp_') && Number(m.id) < minNewId);
            baseMsgs = [...olderMsgs, ...data.messages];
          }
          const prevStr = JSON.stringify(prev.filter(m => !String(m.id).startsWith('temp_')).map(m => m.id + m.status));
          const newStr = JSON.stringify(baseMsgs.map(m => m.id + m.status));
          if (prevStr === newStr && temps.length === 0) return prev;
          return [...baseMsgs, ...temps].sort((a, b) => {
            const idA = String(a.id).startsWith('temp_') ? 999999999 : Number(a.id);
            const idB = String(b.id).startsWith('temp_') ? 999999999 : Number(b.id);
            return idA - idB;
          });
        });
      }
    } catch (e) {}
    if (!silent) setLoadingMsgs(false);
  };

  const loadMoreMessages = async () => {
    if (!activeConv || loadingMore) return;
    const firstId = messages.find(m => !String(m.id).startsWith('temp_'))?.id;
    if (!firstId) return;
    setLoadingMore(true);
    try {
      const res = await fetch(`${API}/whatsapp/conversations/${activeConv}/messages?limit=50&before_id=${firstId}`, { headers: authH() });
      const data = await res.json();
      if (data.messages && data.messages.length > 0) {
        hasLoadedMoreRef.current = true;
        setHasMoreMsgs(!!data.has_more);
        const area = messagesAreaRef.current;
        const prevScrollHeight = area?.scrollHeight || 0;
        setMessages(prev => {
          const existingIds = new Set(prev.map(m => m.id));
          const newMsgs = data.messages.filter(m => !existingIds.has(m.id));
          return [...newMsgs, ...prev].sort((a, b) => {
            const idA = String(a.id).startsWith('temp_') ? 999999999 : Number(a.id);
            const idB = String(b.id).startsWith('temp_') ? 999999999 : Number(b.id);
            return idA - idB;
          });
        });
        // Mantener posición de scroll después de insertar mensajes arriba
        setTimeout(() => {
          if (area) area.scrollTop = area.scrollHeight - prevScrollHeight;
        }, 0);
      } else {
        setHasMoreMsgs(false);
      }
    } catch(e) {}
    setLoadingMore(false);
  };

  const loadShopifyData = async (phone) => {
    if (!phone) return;
    const rawPhone = phone.replace(/\D/g, '');
    try {
      const res = await fetch(`${API}/contacts/by-phone/${rawPhone}`, { headers: authH() });
      const d = await res.json();
      if (d.found) {
        setShopifyContact(d);
        if (d.nombre) setShopifyNames(prev => ({ ...prev, [rawPhone]: d.nombre }));
        setLoadingOrders(true);
        const ores = await fetch(`${API}/orders/?customer_id=${d.id}&phone=${rawPhone}&limit=10`, { headers: authH() });
        const odata = await ores.json();
        const getEstado = (o) => {
          if (o.estado && o.estado !== 'sin_despachar') return o.estado;
          if (o.estado_envio === 'fulfilled') return 'despachado';
          if (o.estado_pago === 'paid') return 'pagado';
          if (o.estado_pago === 'voided') return 'cancelado';
          return 'pendiente';
        };
        const ESTADO_LABEL = { despachado:'Despachado', pagado:'Pagado', pendiente:'Pendiente', cancelado:'Cancelado', entregado:'Entregado', preparando:'Preparando', sin_despachar:'Sin despachar' };
        const orders = (odata.orders || []).slice(0, 8).map(o => ({
          shopify_id: o.shopify_id, id: o.id,
          estado: getEstado(o),
          estado_label: ESTADO_LABEL[getEstado(o)] || getEstado(o),
          estado_pago: o.estado_pago, estado_envio: o.estado_envio,
          fulfillment_status: o.estado_envio, financial_status: o.estado_pago,
          product: o.producto || 'Producto', variant: o.variante || '',
          total: o.valor || 0, metodo: o.metodo || 'COD',
          items_detalle: o.items_detalle || [],
          guia: o.guia_nexus || null,
          guia_nexus: o.guia_nexus || null,
          transportadora: o.transportadora || null,
          tienda: o.tienda || '',
          tags: o.tags || '', note: o.notas || '',
        }));
        setShopifyOrders(orders);
        setLoadingOrders(false);
        const activeO = orders.find(o => o.estado !== 'entregado' && o.estado !== 'cancelado') || orders[0];
        if (activeO?.guia_nexus) {
          setNexusOrderInfo({ has_nexus: true, tracking_number: activeO.guia_nexus, carrier: activeO.transportadora, shopify_order_id: activeO.shopify_id });
        } else if (activeO?.shopify_id) {
          try {
            const nres = await fetch(`${API}/nexus/by-shopify-order/${activeO.shopify_id}`, { headers: authH() });
            if (nres.ok) setNexusOrderInfo({ ...(await nres.json()), shopify_order_id: activeO.shopify_id });
            else setNexusOrderInfo(null);
          } catch (e) { setNexusOrderInfo(null); }
        } else {
          setNexusOrderInfo(null);
        }
        // Auto-assign label based on Shopify order status (never overwrite manual labels)
        const AUTO_LABELS = ['despachado','pendiente','pagado'];
        const existingLabel = convLabels[activeConv] || conversations.find(c => c.id === activeConv)?.label;
        if (!existingLabel || AUTO_LABELS.includes(existingLabel)) {
          const autoLabel = AUTO_LABELS.includes(activeO?.estado) && labelMap[activeO.estado] ? activeO.estado : null;
          if (autoLabel !== (existingLabel || null) && activeConv) {
            setConvLabels(prev => { const next={...prev,[activeConv]:autoLabel}; localStorage.setItem(uKey('talku_labels'),JSON.stringify(next)); return next; });
            patchConv(activeConv, { label: autoLabel });
          }
        }
        const allItems = activeO?.items_detalle || [];
        const inlineImgs = {};
        const needFetch = [];
        allItems.forEach((it, i) => {
          if (it.image_url) { inlineImgs[i] = it.image_url; }
          else if (it.variant_id) { needFetch.push({ idx: i, vid: it.variant_id }); }
        });
        if (needFetch.length > 0) {
          Promise.all(needFetch.map(({ vid }) =>
            fetch(`${API}/orders/variant-image/${vid}`, { headers: authH() })
              .then(r => r.ok ? r.json() : null).catch(() => null)
          )).then(results => {
            const imgs = { ...inlineImgs };
            results.forEach((r, i) => { if (r?.image_url) imgs[needFetch[i].idx] = r.image_url; });
            setOrderImages(imgs);
          });
        } else if (Object.keys(inlineImgs).length > 0) { setOrderImages(inlineImgs); }
        else if (allItems.length > 0 && activeO.shopify_id) {
          fetch(`${API}/nexus/orders/${activeO.shopify_id}/detail`, { headers: authH() })
            .then(r => r.ok ? r.json() : null)
            .then(d => {
              if (!d) return;
              const m = {};
              const nxItems = d.items || d.data?.items || [];
              if (nxItems.length > 0) {
                allItems.forEach((_, i) => { const ni = nxItems[i]; const ig = ni?.product?.image_url || ni?.variant_image_url || ni?.image_url; if (ig) m[i] = ig; });
              }
              if (Object.keys(m).length === 0) {
                const ig = d.variant_image_url || d.product_image || d.data?.variant_image_url || d.data?.product_image;
                if (ig) allItems.forEach((_, i) => { m[i] = ig; });
              }
              if (Object.keys(m).length > 0) setOrderImages(m);
            }).catch(() => {});
        } else { setOrderImages({}); }
      } else {
        setShopifyContact(null);
        setShopifyOrders([]);
        setNexusOrderInfo(null);
        setOrderImages({});
        setShipGuia(''); setShipMsg('');
      }
    } catch (e) { setLoadingOrders(false); }
  };

  // Plantillas internas reales del tenant (Configuración → Plantillas)
  const loadIntTemplates = async () => {
    try {
      const r = await fetch(`${API}/whatsapp/internal-templates`, { headers: authH() });
      const d = await r.json();
      setIntTemplates(Array.isArray(d.templates) ? d.templates : []);
    } catch { setIntTemplates([]); }
  };

  // Equipo real del tenant para "Asignar agente"
  const loadTeam = async () => {
    setAssignSearch('');
    try {
      const r = await fetch(`${API}/tenants/me/members`, { headers: authH() });
      const d = r.ok ? await r.json() : [];
      setTeamMembers(Array.isArray(d) ? d : []);
    } catch { setTeamMembers([]); }
  };

  const assignAgent = async (name) => {
    const convId = activeConv;
    setShowAssign(false);
    setConversations(prev => prev.map(c => c.id === convId ? { ...c, assigned_to: name } : c));
    try {
      await fetch(`${API}/whatsapp/conversations/${convId}`, {
        method: 'PATCH', headers: authH({ 'Content-Type': 'application/json' }), body: JSON.stringify({ assigned_to: name }),
      });
    } catch {}
  };

  const loadMetaTemplates = async () => {
    setMetaTplLoading(true); setTplPick(null); setMetaTplErr('');
    try {
      const res = await fetch(`${API}/whatsapp/templates`, { headers: authH() });
      const data = await res.json();
      setMetaTemplates(data.data || data.templates || []);
      if (data.data) setTplDefsSorted(data.data);
    } catch (e) { setMetaTemplates([]); }
    setMetaTplLoading(false);
  };

  const dismissNotification = (id) => {
    setExitingNotifs(prev => new Set([...prev, id]));
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
      setExitingNotifs(prev => { const s = new Set([...prev]); s.delete(id); return s; });
    }, 380);
  };

  const replyNotification = (notif) => {
    setActiveConv(notif.convId);
    markRead(notif.convId);
    dismissNotification(notif.id);
  };

  const markRead = async (convId) => {
    try {
      await fetch(`${API}/whatsapp/conversations/${convId}/mark-read`, { method: 'POST', headers: authH() });
      setConversations(prev => prev.map(c => c.id === convId ? { ...c, unread_count: 0 } : c));
      dismissNotification(`notif_${convId}`);
    } catch (e) {}
  };

  useEffect(() => { conversationsRef.current = conversations; }, [conversations]);

  useEffect(() => {
    loadConversations();
    pollRef.current = setInterval(loadConversations, 5000);
    const msgPoll = setInterval(() => {
      if (activeConvRef.current) {
        loadMessages(activeConvRef.current, true);
        const conv = conversationsRef.current.find(c => c.id === activeConvRef.current);
        if (conv?.unread_count > 0) markRead(activeConvRef.current);
      }
    }, 2000);
    return () => { clearInterval(pollRef.current); clearInterval(msgPoll); };
  }, []);

  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  useEffect(() => {
    if (activeConv) {
      hasLoadedMoreRef.current = false;
      setMessages([]);
      loadMessages(activeConv);
      setMediaErrors({});
      activePhoneRef.current = null;
      setPendingMedia(null);
      setSendError('');
      setShopifyContact(null);
      setShopifyOrders([]);
      setNexusOrderInfo(null);
      setShipGuia(''); setShipMsg('');
    }
  }, [activeConv]);

  useEffect(() => {
    if (activeConv && conversations.length > 0) {
      const c = conversations.find(x => x.id === activeConv);
      if (c?.contact_phone && c.contact_phone !== activePhoneRef.current) {
        activePhoneRef.current = c.contact_phone;
        loadShopifyData(c.contact_phone);
      }
    }
  }, [activeConv, conversations]);

  useEffect(() => {
    if (messagesAreaRef.current) {
      messagesAreaRef.current.scrollTop = messagesAreaRef.current.scrollHeight;
    }
  }, [messages.length]);

  useEffect(() => {
    const handleClick = (e) => {
      if (optionsRef.current && !optionsRef.current.contains(e.target)) setShowOptionsMenu(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const sendMessage = async (text) => {
    const msg = text;
    if (!msg || !msg.trim() || !activeConv) return;
    const conv = conversations.find(c => c.id === activeConv);
    if (!conv) return;
    const currentReplyTo = replyTo;
    setReplyTo(null);
    const tempMsg = { id: 'temp_' + Date.now(), role: 'outbound', content: msg, sent_at: new Date().toISOString(), status: 'sent', source: 'agent', quoted_content: currentReplyTo?.content || null, quoted_sender: currentReplyTo?.sender || null };
    setMessages(prev => {
      const all = [...prev, tempMsg];
      return all.sort((a, b) => {
        const idA = String(a.id).startsWith('temp_') ? 999999999 : Number(a.id);
        const idB = String(b.id).startsWith('temp_') ? 999999999 : Number(b.id);
        return idA - idB;
      });
    });
    setSending(true);
    const tempId = tempMsg.id;
    try {
      await fetch(`${API}/whatsapp/send`, {
        method: 'POST',
        headers: authH({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          phone: conv.contact_phone,
          message: msg,
          source: 'agent',
          conv_id: activeConv,
          ...(currentReplyTo ? {
            context_message_id: currentReplyTo.wamid || '',
            quoted_content: currentReplyTo.content || '',
            quoted_sender: currentReplyTo.sender || '',
          } : {})
        })
      });
      // Remover temp por ID exacto y recargar desde DB para evitar duplicado
      setMessages(prev => prev.filter(m => m.id !== tempId));
      loadMessages(activeConv, true);
    } catch (e) {}
    setSending(false);
  };

  // WhatsApp solo acepta imágenes JPG/PNG: las demás (WebP, GIF, BMP…) se convierten a JPG antes de enviar
  const toWhatsAppImage = async (file) => {
    if (!file.type.startsWith('image/') || ['image/jpeg', 'image/png'].includes(file.type)) return file;
    const url = URL.createObjectURL(file);
    try {
      const img = await new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = url; });
      const c = document.createElement('canvas');
      c.width = img.naturalWidth; c.height = img.naturalHeight;
      const ctx = c.getContext('2d'); ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, c.width, c.height); ctx.drawImage(img, 0, 0);
      const blob = await new Promise(res => c.toBlob(res, 'image/jpeg', 0.9));
      if (!blob) return null;
      return new File([blob], file.name.replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' });
    } catch { return null; } finally { URL.revokeObjectURL(url); }
  };

  const sendMedia = async (rawFile) => {
    const conv = conversations.find(c => c.id === activeConv);
    if (!conv || !rawFile) return;
    const file = await toWhatsAppImage(rawFile);
    if (!file) { setSendError('Esta imagen no se puede enviar por WhatsApp. Usa una imagen JPG o PNG.'); return; }
    const MB5 = 5 * 1024 * 1024;
    const MB100 = 100 * 1024 * 1024;
    const isImage = file.type.startsWith('image/');
    const maxSize = isImage ? MB5 : MB100;
    if (file.size > maxSize) {
      setSendError(`Archivo muy grande (${(file.size/1024/1024).toFixed(1)}MB). Máximo: ${isImage?'5':'100'}MB`);
      return;
    }
    setSending(true);
    setSendError('');
    const type = file.type.startsWith('image/') ? 'image' : file.type.startsWith('video/') ? 'video' : file.type.startsWith('audio/') ? 'audio' : 'document';
    const contentLabel = type === 'image' ? '[Imagen]' : type === 'video' ? '[Video]' : type === 'audio' ? '[Audio]' : `[Documento: ${file.name}]`;
    const tempId = 'temp_' + Date.now();
    const tempMedia = { id: tempId, role: 'outbound', content: contentLabel, sent_at: new Date().toISOString(), status: 'sent', source: 'agent', media_type: type, media_url: null };
    setMessages(prev => [...prev, tempMedia].sort((a, b) => {
      const idA = String(a.id).startsWith('temp_') ? 999999999 : Number(a.id);
      const idB = String(b.id).startsWith('temp_') ? 999999999 : Number(b.id);
      return idA - idB;
    }));
    try {
      const formData = new FormData();
      formData.append('file', file);
      const uploadRes = await fetch(`${API}/whatsapp/upload-media`, { method: 'POST', headers: authH(), body: formData });
      const uploadData = await uploadRes.json();
      if (!uploadData.id) {
        const errDetail = uploadData.error?.message || uploadData.message || uploadData.error_data?.details || JSON.stringify(uploadData).slice(0,120);
        setSendError(`Error al subir: ${errDetail}`);
        setMessages(prev => prev.filter(m => m.id !== tempId));
        setSending(false);
        return;
      }
      if (uploadData.local_url) {
        setMessages(prev => prev.map(m => m.id === tempId ? { ...m, media_url: uploadData.local_url } : m));
      }
      const sendRes = await fetch(`${API}/whatsapp/send-media`, {
        method: 'POST',
        headers: authH({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ phone: conv.contact_phone, type, media_id: uploadData.id, filename: file.name, local_url: uploadData.local_url || '' })
      });
      const sendData = await sendRes.json();
      if (!sendRes.ok || sendData.error) {
        const errMsg = sendData.error?.message || sendData.detail || JSON.stringify(sendData.error || sendData).slice(0, 120);
        setSendError(`Error al enviar: ${errMsg}`);
        setMessages(prev => prev.filter(m => m.id !== tempId));
        setSending(false);
        return;
      }
      setMessages(prev => prev.filter(m => m.id !== tempId));
      loadMessages(activeConv, true);
    } catch (e) {
      setSendError(`Error: ${e.message}`);
      setMessages(prev => prev.filter(m => m.id !== tempId));
    }
    setSending(false);
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus'
                     : MediaRecorder.isTypeSupported('audio/ogg;codecs=opus') ? 'audio/ogg;codecs=opus'
                     : 'audio/webm';
      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      recordingCancelledRef.current = false;
      recPeakRef.current = 0;
      mediaRecorder.ondataavailable = e => audioChunksRef.current.push(e.data);
      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        if (recordingCancelledRef.current) return;
        if (recPeakRef.current >= 0 && recPeakRef.current < 0.02) {
          setSendError('No se detectó sonido del micrófono, por eso no se envió el audio. Revisa que el micrófono correcto esté activo y no esté silenciado.');
          return;
        }
        const ext = mimeType.includes('mp4') ? 'm4a' : mimeType.includes('ogg') ? 'ogg' : 'webm';
        const baseMime = mimeType.split(';')[0];
        const blob = new Blob(audioChunksRef.current, { type: mimeType });
        const file = new File([blob], `nota_voz.${ext}`, { type: baseMime });
        await sendMedia(file);
      };
      mediaRecorder.start();
      setRecording(true);
      setRecordingTime(0);
      setSendError('');
      recordingTimerRef.current = setInterval(() => setRecordingTime(t => {
        if (t + 1 >= 300) { setTimeout(() => stopRecording(), 0); } // máximo 5 minutos
        return t + 1;
      }), 1000);
      // Niveles de voz en vivo (barras): se actualizan directo en el DOM para no re-renderizar
      try {
        const AC = window.AudioContext || window.webkitAudioContext;
        const meter = stream.clone(); const ctx = new AC(); const src = ctx.createMediaStreamSource(meter); const an = ctx.createAnalyser();
        an.fftSize = 64; src.connect(an);
        const data = new Uint8Array(an.frequencyBinCount);
        const tick = () => {
          if (mediaRecorderRef.current?.state !== 'recording') { meter.getTracks().forEach(t => t.stop()); ctx.close().catch(() => {}); return; }
          an.getByteFrequencyData(data);
          const peak = Math.max(...data) / 255; if (peak > recPeakRef.current) recPeakRef.current = peak;
          const bars = document.querySelectorAll('.rec-wave i');
          bars.forEach((b, k) => { const v = data[(k * 2) % data.length] / 255; b.style.transform = `scaleY(${Math.max(0.12, v)})`; });
          requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      } catch { recPeakRef.current = -1; }
    } catch (e) { setSendError('No se pudo acceder al micrófono. Revisa el permiso del navegador (ícono del candado junto a la dirección).'); }
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    setRecording(false);
    clearInterval(recordingTimerRef.current);
    setRecordingTime(0);
  };

  const openNexusModal = async (shopifyOrderId) => {
    setNexusMsg(''); setNexusLoading(true); setShowNexusModal(true); setNxConfirm(false); setNxShowData(false);
    try {
      const res = await fetch(`${API}/nexus/by-shopify-order/${shopifyOrderId}`, { headers: authH() });
      const d = await res.json();
      setNexusOrderInfo({ ...d, shopify_order_id: shopifyOrderId });

      // Intentar cargar datos reales desde Nexus para pre-llenar el form
      let nexusDetail = null;
      if (d.has_nexus) {
        try {
          const dr = await fetch(`${API}/nexus/orders/${shopifyOrderId}/detail`, { headers: authH() });
          if (dr.ok) nexusDetail = await dr.json();
        } catch(_) {}
      }

      const conv = conversations.find(x => x.id === activeConv);
      const phone = shopifyContact?.telefono || (conv?.contact_phone || '').replace(/\D/g, '');
      const ord = shopifyOrders.find(o => o.fulfillment_status !== 'fulfilled') || shopifyOrders[0];
      const _city = nexusDetail?.city || nexusDetail?.ciudad || shopifyContact?.ciudad || '';
      const _state = nexusDetail?.state || nexusDetail?.department || nexusDetail?.departamento || shopifyContact?.provincia || inferDepto(_city);
      contentPrefillRef.current = nexusDetail?.content_description || ord?.variant || '';
      setNexusForm({
        customer_name:       nexusDetail?.customer_name || shopifyContact?.nombre || conv?.contact_name || '',
        customer_phone:      nexusDetail?.customer_phone || phone || '',
        shipping_address:    nexusDetail?.shipping_address || nexusDetail?.address || shopifyContact?.direccion || '',
        city:                _city,
        city_code:           '',
        state:               _state,
        content_description: nexusDetail?.content_description || ord?.variant || '',
        total_amount:        nexusDetail?.total_amount || nexusDetail?.total || String(ord?.total || '') || '',
        customer_email:      nexusDetail?.customer_email || nexusDetail?.email || shopifyContact?.email || '',
      });
      setCityQuery(_city ? `${_city}${_state ? ', ' + _state : ''}` : '');
      setCitySuggestions([]);
      const pName = nexusDetail?.product_name || ord?.product || '';
      const vName = nexusDetail?.variant_selected || ord?.variant || '';
      setNexusProductTitle(vName && pName.endsWith(vName) ? pName.slice(0, -vName.length).replace(/\s*-\s*$/, '').trim() : pName);
      const nexusImg = nexusDetail?.variant_image_url || nexusDetail?.product_image || null;
      setNexusProductImg(nexusImg);
      if (!nexusImg) {
        const variantId = ord?.items_detalle?.[0]?.variant_id;
        if (variantId) {
          try {
            const imgRes = await fetch(`${API}/orders/variant-image/${variantId}`, { headers: authH() });
            if (imgRes.ok) { const imgD = await imgRes.json(); if (imgD.image_url) setNexusProductImg(imgD.image_url); }
          } catch(_) {}
        }
      }
      setShippingQuotes([]); setSelectedCarrier('coordinadora'); setFailedCarriers({}); setSinRecaudo(false); setSrForced(false);
      setNxVariants(null); setNxVarSel({});
      if (d.has_nexus) await loadNexusVariants(shopifyOrderId);
      if (d.has_nexus) {
        try {
          const qr = await fetch(`${API}/nexus/orders/${shopifyOrderId}/quote`, { method: 'POST', headers: authH() });
          if (qr.ok) {
            let qd = await qr.json();
            // Pedido ya pagado (Sistecrédito, Addi, Wompi…): Nexus lo cotiza sin recaudo aunque no se pida
            if (qd.sin_recaudo) {
              setSinRecaudo(true); setSrForced(true);
              const qr2 = await fetch(`${API}/nexus/orders/${shopifyOrderId}/quote`, { method: 'POST', headers: authH({ 'Content-Type': 'application/json' }), body: JSON.stringify({ sin_recaudo: true }) });
              if (qr2.ok) qd = await qr2.json();
            }
            setShippingQuotes(qd.quotes || qd.data || []);
          }
        } catch(_) {}
      }
    } catch(e) {
      setNexusMsg(`Error: ${e?.message || String(e)}`);
    } finally { setNexusLoading(false); }
  };

  const requoteOrder = async (sr) => {
    const id = nexusOrderInfo?.shopify_order_id; if (!id) return;
    setQuotesLoading(true); setShippingQuotes([]); setFailedCarriers({});
    try {
      const qr = await fetch(`${API}/nexus/orders/${id}/quote`, { method: 'POST', headers: authH({ 'Content-Type': 'application/json' }), body: JSON.stringify(sr ? { sin_recaudo: true } : {}) });
      if (qr.ok) {
        const qd = await qr.json();
        setShippingQuotes(qd.quotes || qd.data || []);
      }
    } catch(_) {} finally { setQuotesLoading(false); }
  };

  const loadNexusVariants = async (orderId) => {
    try {
      const r = await fetch(`${API}/nexus/orders/${orderId}/variants`, { headers: authH() });
      if (!r.ok) { setNxVariants(null); return; }
      const v = await r.json();
      if (v && v.success !== false && Array.isArray(v.items) && v.items.length > 0) {
        setNxVariants(v);
        setNxVarSel(Object.fromEntries(v.items.flatMap(it => nxPairs(it).map(p => [`${it.item_id}:${p.pair}`, p.current_variant_id]))));
      } else setNxVariants(null);
    } catch(_) { setNxVariants(null); }
  };
  // Pares de un producto: combos traen 2 (Par 1, Par 2); normales 1
  const nxPairs = (it) => (Array.isArray(it.pairs) && it.pairs.length ? it.pairs : [{ pair: 1, current_variant: it.current_variant, current_variant_id: it.current_variant_id }]);
  const nxVarPending = () => (nxVariants?.items || []).flatMap(it => (it.variants || []).length === 0 ? [] : nxPairs(it)
    .filter(p => { const s = nxVarSel[`${it.item_id}:${p.pair}`]; return s && Number(s) !== Number(p.current_variant_id); })
    .map(p => ({ it, pair: p.pair, variant_id: Number(nxVarSel[`${it.item_id}:${p.pair}`]) })));
  // Guarda en Nexus cada producto al que se le cambió la variante. true = todo OK.
  const saveNexusVariants = async () => {
    const pend = nxVarPending();
    if (!pend.length) return true;
    const orderId = nexusOrderInfo?.shopify_order_id;
    setNxVarSaving(true);
    try {
      for (const { it, pair, variant_id } of pend) {
        const r = await fetch(`${API}/nexus/orders/${orderId}/items/${it.item_id}/variant`, {
          method: 'PATCH', headers: authH({ 'Content-Type': 'application/json' }),
          body: JSON.stringify(it.is_combo ? { variant_id, pair } : { variant_id }),
        });
        const d = await r.json().catch(() => ({}));
        if (!r.ok) {
          const m = d.detail || d.message || `Error ${r.status}`;
          setNexusMsg(`❌ ${it.product_name}${it.is_combo ? ` (Par ${pair})` : ''}: ${r.status === 409 ? (m || 'Anula la guía para cambiar la talla') : m}`);
          await loadNexusVariants(orderId);
          return false;
        }
        setNexusMsg(`✅ ${d.message || 'Variante actualizada'}`);
      }
      await loadNexusVariants(orderId);
      return true;
    } catch(e) {
      setNexusMsg('❌ Error de conexión al cambiar la variante');
      return false;
    } finally { setNxVarSaving(false); }
  };

  const nexusGenerateGuide = async () => {
    if (!nexusOrderInfo?.shopify_order_id) return;
    setNexusLoading(true); setNexusMsg('');
    try {
      // 0. Cambios de color/talla (variantes reales de Nexus)
      if (nxVariants && nxVarPending().length) {
        const okVar = await saveNexusVariants();
        if (!okVar) { setNexusLoading(false); return; }
      }
      // 1. Actualizar datos en Nexus antes de generar
      const updateFields = Object.fromEntries(Object.entries(nexusForm).filter(([,v]) => String(v).trim() !== '' && v !== null && v !== undefined));
      // El contenido de la guía lo arma Nexus con la variante actual (sale en 2 partes del PDF).
      // Solo se envía si el usuario lo escribió a mano y el pedido no tiene selector de variantes.
      const _typedDesc = String(nexusForm.content_description || '').trim();
      delete updateFields.content_description;
      if (!nxVariants && _typedDesc && _typedDesc !== String(contentPrefillRef.current || '').trim()) updateFields.content_description = _typedDesc;
      if (Object.keys(updateFields).length > 0) {
        const patchRes = await fetch(`${API}/nexus/orders/${nexusOrderInfo.shopify_order_id}`, {
          method: 'PATCH',
          headers: authH({ 'Content-Type': 'application/json' }),
          body: JSON.stringify(updateFields),
        });
        if (!patchRes.ok) {
          const patchErr = await patchRes.json().catch(() => ({}));
          setNexusMsg('❌ Error al actualizar datos en Nexus: ' + (patchErr.detail || patchRes.status) + '. Corrija los datos e intente de nuevo.');
          setNexusLoading(false);
          return;
        }
      }
      // 2. Generar guía
      const res = await fetch(`${API}/nexus/orders/${nexusOrderInfo.shopify_order_id}/generate-guide`, { method: 'POST', headers: authH({ 'Content-Type': 'application/json' }), body: JSON.stringify(sinRecaudo ? { carrier: selectedCarrier, sin_recaudo: true } : { carrier: selectedCarrier }) });
      const d = await res.json();
      if (d.guide_number) {
        // El backend devuelve 200 con already_existed=true cuando Nexus responde 409
        // (guía ya activa). No es una guía nueva: se sincroniza la existente.
        const label = d.already_existed ? '✅ Guía sincronizada (ya existía)' : '✅ Guía generada';
        setNexusMsg(`${label}: ${d.guide_number}${d.charged != null ? ` · Cobrado de la billetera: $${Number(d.charged).toLocaleString('es-CO')}` : ''}`);
        setNexusOrderInfo(prev => ({ ...prev, tracking_number: d.guide_number, carrier_status: 'shipped' }));
        const conv = conversations.find(x => x.id === activeConv);
        if (conv?.contact_phone) loadShopifyData(conv.contact_phone);
      } else {
        const _motivo = d.message || d.detail || 'Error generando guía';
        setNexusMsg(`❌ ${_motivo}`);
        if (selectedCarrier) setFailedCarriers(p => ({ ...p, [selectedCarrier]: _motivo }));
      }
    } catch(e) {
      setNexusMsg('❌ Error de conexión');
    } finally { setNexusLoading(false); }
  };

  const shopifyAction = async (action, orderId, extra = {}) => {
    setActionLoading(true); setActionMsg('');
    try {
      let url, body = {};
      if (action === 'cancel') { url = `${API}/orders/${orderId}/cancel`; }
      else if (action === 'note') { url = `${API}/orders/${orderId}/note`; body = { note: extra.note }; }
      else if (action === 'tags') { url = `${API}/orders/${orderId}/tags`; body = { tags: extra.tags }; }
      else if (action === 'fulfill') { url = `${API}/orders/${orderId}/fulfill`; }
      else if (action === 'mark-paid') { url = `${API}/orders/${orderId}/mark-paid`; }
      const res = await fetch(url, { method: 'POST', headers: authH({ 'Content-Type': 'application/json' }), body: JSON.stringify(body) });
      if (res.ok) { setActionMsg('✅ Acción realizada correctamente'); const c = conversations.find(x => x.id === activeConv); if (c?.contact_phone) loadShopifyData(c.contact_phone); }
      else { setActionMsg('❌ Error al realizar la acción'); }
    } catch (e) { setActionMsg('❌ Error de conexión'); }
    setActionLoading(false);
  };

  const conv = conversations.find(c => c.id === activeConv);
  const currentStatus = convStatuses[activeConv] || conv?.status || 'open';
  const statusObj = STATUS_OPTIONS.find(s => s.id === currentStatus) || STATUS_OPTIONS[0];
  // ── Carpetas (columna 1): mismas reglas que las pestañas y estados existentes ──
  const convSt = (c) => convStatuses[c.id] || c.status || 'open';
  const folderMatch = (c, f) => {
    if (searchQuery) return true;
    switch (f) {
      case 'mias': return !!user?.name && c.assigned_to === user.name;
      case 'sinasignar': return !c.assigned_to;
      case 'favoritos': return favConvs.has(c.id);
      case 'open': case 'waiting_client': case 'escalated': case 'resolved': return convSt(c) === f;
      default: return true;
    }
  };
  // Un solo filtro activo a la vez (carpeta, etiqueta, origen o pedido); repetir el activo → Todas
  const pickFilter = (kind, value) => {
    const same = (kind === 'folder' && folder === value) || (kind === 'label' && labelFilter === value)
      || (kind === 'source' && sourceFilter === value) || (kind === 'order' && orderFilter === value);
    setFolder(kind === 'folder' && !same ? value : 'todas');
    setLabelFilter(kind === 'label' && !same ? value : null);
    setSourceFilter(kind === 'source' && !same ? value : null);
    setOrderFilter(kind === 'order' && !same ? value : null);
  };
  const FOLDER_NAMES = { mias: 'Asignadas a mí', sinasignar: 'Sin asignar', favoritos: 'Fijadas', open: 'Abiertas', waiting_client: 'Esperando cliente', escalated: 'Escaladas', resolved: 'Resueltas' };
  const activeFilterName = labelFilter ? `Etiqueta · ${labelMap[labelFilter]?.name || labelFilter}`
    : sourceFilter ? `Origen · ${sourceFilter}` : orderFilter ? `Pedido · ${orderFilter.replace(/_/g, ' ')}`
    : folder !== 'todas' ? FOLDER_NAMES[folder] : null;
  const LABEL_PALETTE = ['#6b7280','#3b82f6','#0ea5e9','#16a34a','#10b981','#f59e0b','#f97316','#ef4444','#a855f7','#6366f1'];
  const loadLabels = async () => {
    try { const r = await fetch(`${API}/whatsapp/labels`, { headers: authH() }); const d = r.ok ? await r.json() : {}; setTenantLabels(d.labels || []); }
    catch { setTenantLabels([]); }
  };
  useEffect(() => { loadLabels(); }, []); // eslint-disable-line
  const saveLabel = async () => {
    const f = labelForm; if (!f || !f.name.trim()) return;
    try {
      const r = await fetch(`${API}/whatsapp/labels${f.id ? '/' + f.id : ''}`, {
        method: f.id ? 'PUT' : 'POST', headers: authH({ 'Content-Type': 'application/json' }), body: JSON.stringify({ name: f.name.trim(), color: f.color }),
      });
      const d = await r.json();
      if (!r.ok) { setLabelForm({ ...f, error: d.detail || 'No se pudo guardar' }); return; }
      setLabelForm(null); loadLabels();
    } catch { setLabelForm({ ...f, error: 'Error de red' }); }
  };
  const deleteLabel = async (l) => {
    if (!window.confirm(`¿Eliminar la etiqueta "${l.name}"? Los chats que la tienen quedarán sin etiqueta.`)) return;
    try { await fetch(`${API}/whatsapp/labels/${l.id}`, { method: 'DELETE', headers: authH() }); } catch {}
    if (labelFilter === l.key) pickFilter('folder', 'todas');
    setConversations(prev => prev.map(c => c.label === l.key ? { ...c, label: null } : c));
    setConvLabels(prev => { const n = { ...prev }; Object.keys(n).forEach(k => { if (n[k] === l.key) delete n[k]; }); return n; });
    loadLabels();
  };

  const openSearchResult = (c) => {
    if (!conversations.some(x => x.id === c.id)) {
      const conv = { channel: 'whatsapp', status: 'open', unread_count: 0, ...c };
      extraConvsRef.current = [...extraConvsRef.current.filter(x => x.id !== conv.id), conv];
      setConversations(prev => [...prev, conv]);
    }
    setActiveConv(c.id); markRead(c.id); setSearchQuery('');
  };
  const searchLocal = (c) => {
    const q = searchQuery.toLowerCase().trim();
    const phone = (c.contact_phone || '').replace(/\D/g, '');
    const qDigits = searchQuery.replace(/\D/g, '');
    return (c.contact_name || '').toLowerCase().includes(q)
      || (phone && qDigits.length >= 6 && phone.includes(qDigits))
      || (c.order_number && String(c.order_number).includes(searchQuery.replace('#', '')));
  };
  const folderCount = (f) => conversations.filter(c => !['fb', 'ig', 'messenger', 'instagram'].includes(c.channel) && folderMatch(c, f)).length;

  const filtered = conversations.filter(c => {
    const q = searchQuery.trim().toLowerCase();
    const matchesSearch = !q ||
      (c.contact_name || '').toLowerCase().includes(q) ||
      (c.contact_phone || '').includes(q) ||
      (c.last_message || '').toLowerCase().includes(q);
    if (!matchesSearch) return false;
    if (['fb', 'ig', 'messenger', 'instagram'].includes(c.channel)) return false;
    if (filter === 'Nuevos') return (c.unread_count || 0) > 0;
    if (filter === 'Abiertos') return c.status === 'open';
    if (filter === 'Urgentes') return c.status === 'escalated';
    if (filter === 'Resueltos') return c.status === 'resolved';
    return true;
  });

  const activeOrder = shopifyOrders.find(o => o.fulfillment_status !== 'fulfilled' && o.financial_status !== 'voided' && o.financial_status !== 'refunded') || shopifyOrders[0];
  const pastOrders = shopifyOrders.filter(o => o.shopify_id !== activeOrder?.shopify_id);
  const guiaNexus = nexusOrderInfo?.tracking_number || null;
  const guiaShopify = activeOrder?.guia || null;
  const trackingUrl = guiaNexus
    ? `https://www.coordinadora.com/portafolio-de-servicios/servicios-en-linea/rastrear-guias/?guia=${guiaNexus}`
    : activeOrder?.tracking_url || null;
  const transportadora = nexusOrderInfo?.carrier || activeOrder?.transportadora || null;
  const carrierStatus = nexusOrderInfo?.carrier_status || null;
  const carrierLastUpdate = nexusOrderInfo?.carrier_last_update || null;

  useEffect(() => {
    if (!nexusOrderInfo?.shopify_order_id) { setTrackingTimeline(null); setTrackingTimelineOpen(false); return; }
    let cancelled = false;
    const fetchTimeline = async () => {
      setTrackingTimelineLoading(true);
      try {
        const res = await fetch(`${API}/nexus/orders/${nexusOrderInfo.shopify_order_id}/timeline`, { headers: authH() });
        if (!cancelled && res.ok) {
          const data = await res.json();
          setTrackingTimeline(data);
          if (data.events?.length > 0) setTrackingTimelineOpen(true);
        }
      } catch (e) {}
      if (!cancelled) setTrackingTimelineLoading(false);
    };
    fetchTimeline();
    return () => { cancelled = true; };
  }, [nexusOrderInfo?.shopify_order_id]);

  const ChannelIcon = ({ ch }) => ch === 'ig' ? <IgIcon /> : ch === 'fb' ? <FbIcon /> : <WaIcon />;
  const fmtRecording = (s) => `${Math.floor(s/60).toString().padStart(2,'0')}:${(s%60).toString().padStart(2,'0')}`;
  const isOrderEditable = (order) => order && order.fulfillment_status !== 'fulfilled';

  const SourceBadge = ({ source, isOut }) => {
    if (!isOut) return null;
    if (source === 'bot') return <span style={{display:'inline-flex',alignItems:'center',gap:3,background:'#eff6ff',border:'1px solid #bfdbfe',borderRadius:6,padding:'1px 6px',fontSize:9,color:'#3b82f6',fontWeight:700}}><svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round"><rect x="3" y="11" width="18" height="10" rx="2"/><path d="M12 11V7"/><circle cx="12" cy="5" r="2"/></svg>IA</span>;
    if (source === 'flow') return <span style={{display:'inline-flex',alignItems:'center',gap:3,background:'#fdf4ff',border:'1px solid #e9d5ff',borderRadius:6,padding:'1px 6px',fontSize:9,color:'#9333ea',fontWeight:700}}><svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#9333ea" strokeWidth="2" strokeLinecap="round"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>Flujo</span>;
    return <span style={{display:'inline-flex',alignItems:'center',gap:3,background:'#f0fdf4',border:'1px solid #bbf7d0',borderRadius:6,padding:'1px 6px',fontSize:9,color:'#16a34a',fontWeight:700}}><svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>Agente</span>;
  };

  const StatusIcon = ({ status, isOut }) => {
    if (!isOut) return null;
    if (status === 'failed') return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="9"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>;
    if (status === 'read') return <span style={{display:'inline-flex'}}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" style={{marginLeft:-6}}><polyline points="20 6 9 17 4 12"/></svg></span>;
    if (status === 'delivered') return <span style={{display:'inline-flex'}}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#aaa" strokeWidth="2" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#aaa" strokeWidth="2" strokeLinecap="round" style={{marginLeft:-6}}><polyline points="20 6 9 17 4 12"/></svg></span>;
    return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#aaa" strokeWidth="2" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>;
  };

  return (
    <>
      
      <style>{`
        *{box-sizing:border-box;margin:0;padding:0;font-family:'SF Pro Display','SF Pro Text',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;}
        body{overflow:hidden;}
        .app{display:flex;flex-direction:column;flex:1;min-height:0;background:#f0f0ee;}
        .app-body{display:flex;flex:1;overflow:hidden;}
        .sidebar{width:80px;background:transparent;display:flex;flex-direction:column;justify-content:space-between;align-items:center;padding:20px 0;flex-shrink:0;}
        .sb-island{background:rgba(255,255,255,0.9);backdrop-filter:blur(8px);-webkit-backdrop-filter:blur(8px);border:1px solid rgba(255,255,255,0.4);border-radius:9999px;box-shadow:0 4px 16px rgba(0,0,0,0.07);display:flex;flex-direction:column;align-items:center;gap:2px;padding:10px 10px;}
        .sb-icon-btn{width:42px;height:42px;border-radius:50%;display:flex;align-items:center;justify-content:center;cursor:pointer;border:none;background:transparent;transition:all 0.18s;color:#9CA3AF;flex-shrink:0;}
        .sb-icon-btn:hover{background:rgba(0,0,0,0.05);color:#374151;}
        .sb-icon-btn.active{color:#1f2937;background:rgba(0,0,0,0.07);}
        .sb-dark-circle{width:42px;height:42px;border-radius:50%;background:radial-gradient(circle at 50% 30%,#4B5563,#111827);box-shadow:inset 0 2px 4px rgba(255,255,255,0.12),0 4px 10px rgba(0,0,0,0.32);display:flex;align-items:center;justify-content:center;cursor:pointer;border:none;transition:all 0.18s;flex-shrink:0;}
        .sb-dark-circle:hover{box-shadow:inset 0 2px 4px rgba(255,255,255,0.12),0 6px 16px rgba(0,0,0,0.42);transform:translateY(-1px);}
        .sb-sep{width:22px;height:1px;background:rgba(0,0,0,0.08);margin:3px 0;}
        .topbar{height:68px;background:transparent;display:flex;align-items:center;padding:0 24px;gap:16px;flex-shrink:0;z-index:10;}
        .tb-brand{display:flex;align-items:center;gap:10px;min-width:150px;}
        .tb-logo{height:32px;background:transparent;display:flex;align-items:center;flex-shrink:0;}
        .tb-name{font-size:19px;font-weight:700;color:#0a0a0a;letter-spacing:-0.4px;}
        .tb-name span{color:#6ee7b7;}
        .tb-nav{flex:1;display:flex;align-items:center;justify-content:center;}
        .tb-nav-inner{display:flex;align-items:center;gap:2px;background:rgba(255,255,255,0.78);backdrop-filter:blur(10px);-webkit-backdrop-filter:blur(10px);border-radius:999px;border:1px solid rgba(255,255,255,0.65);box-shadow:0 4px 24px rgba(0,0,0,0.08),0 1px 3px rgba(0,0,0,0.05);padding:3px;}
        .tb-nav-item{font-size:13.5px;font-weight:500;color:#555;padding:7px 20px;border-radius:999px;cursor:pointer;transition:all 0.18s;white-space:nowrap;border:none;background:transparent;font-family:'SF Pro Display','SF Pro Text',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;}
        .tb-nav-item:hover{color:#0a0a0a;background:rgba(0,0,0,0.05);}
        .tb-nav-item.active{background:linear-gradient(145deg,#1f1f1f 0%,#0a0a0a 100%);color:#fff;font-weight:700;box-shadow:inset 0 1px 0 rgba(255,255,255,0.12),inset 0 -1px 0 rgba(0,0,0,0.2),0 2px 8px rgba(0,0,0,0.22);}
        .tb-right{display:flex;align-items:center;gap:10px;min-width:150px;justify-content:flex-end;}
        .tb-icon-btn{width:42px;height:42px;border-radius:50%;border:1px solid rgba(255,255,255,0.6);background:rgba(255,255,255,0.7);display:flex;align-items:center;justify-content:center;cursor:pointer;position:relative;transition:background 0.15s;backdrop-filter:blur(8px);}
        .tb-icon-btn:hover{background:rgba(255,255,255,0.9);}
        .tb-badge{position:absolute;top:8px;right:8px;width:7px;height:7px;border-radius:50%;background:#ef4444;border:1.5px solid #fff;}
        .tb-profile{display:flex;align-items:center;gap:9px;background:rgba(255,255,255,0.78);border:1px solid rgba(255,255,255,0.65);backdrop-filter:blur(10px);-webkit-backdrop-filter:blur(10px);border-radius:999px;padding:5px 14px 5px 5px;cursor:pointer;}
        .tb-profile-img{width:34px;height:34px;border-radius:50%;background:#0a0a0a;overflow:hidden;}
        .tb-profile-name{font-size:13px;font-weight:700;color:#0a0a0a;}
        .tb-profile-role{font-size:11px;color:#999;}
        .conv-layout{flex:1;display:flex;overflow:hidden;padding:0;gap:0;min-height:0;}

        /* ── Left panel ── */
        .conv-folders-wrap{width:58px;flex-shrink:0;position:relative;border-right:1px solid #ebebeb;background:#fff;}
        .conv-folders{position:absolute;top:0;left:0;bottom:0;width:58px;background:rgba(255,255,255,.96);backdrop-filter:blur(14px);-webkit-backdrop-filter:blur(14px);overflow-x:hidden;overflow-y:auto;padding:14px 8px;z-index:30;border-radius:0;transition:width .32s cubic-bezier(.22,1,.36,1),box-shadow .32s ease;}
        .conv-folders:hover{width:228px;box-shadow:12px 0 36px rgba(10,10,10,.12);}
        .conv-folders::-webkit-scrollbar{width:0;}
        @keyframes cfIn{from{opacity:0;transform:translateX(-10px);}to{opacity:1;transform:none;}}
        .conv-folders > *{animation:cfIn .4s cubic-bezier(.22,1,.36,1) both;}
        .conv-folders > *:nth-child(1){animation-delay:25ms;}
        .conv-folders > *:nth-child(2){animation-delay:50ms;}
        .conv-folders > *:nth-child(3){animation-delay:75ms;}
        .conv-folders > *:nth-child(4){animation-delay:100ms;}
        .conv-folders > *:nth-child(5){animation-delay:125ms;}
        .conv-folders > *:nth-child(6){animation-delay:150ms;}
        .conv-folders > *:nth-child(7){animation-delay:175ms;}
        .conv-folders > *:nth-child(8){animation-delay:200ms;}
        .conv-folders > *:nth-child(9){animation-delay:225ms;}
        .conv-folders > *:nth-child(10){animation-delay:250ms;}
        .conv-folders > *:nth-child(11){animation-delay:275ms;}
        .conv-folders > *:nth-child(12){animation-delay:300ms;}
        .conv-folders > *:nth-child(13){animation-delay:325ms;}
        .conv-folders > *:nth-child(14){animation-delay:350ms;}
        .conv-folders > *:nth-child(15){animation-delay:375ms;}
        .conv-folders > *:nth-child(16){animation-delay:400ms;}
        .conv-folders > *:nth-child(17){animation-delay:425ms;}
        .conv-folders > *:nth-child(18){animation-delay:450ms;}
        .conv-folders > *:nth-child(19){animation-delay:475ms;}
        .conv-folders > *:nth-child(20){animation-delay:500ms;}
        .conv-folders > *:nth-child(21){animation-delay:525ms;}
        .conv-folders > *:nth-child(22){animation-delay:550ms;}
        .conv-folders > *:nth-child(23){animation-delay:575ms;}
        .conv-folders > *:nth-child(24){animation-delay:600ms;}
        .conv-folders > *:nth-child(25){animation-delay:625ms;}
        .cf-sec{font-size:11.5px;color:#9a9a9a;padding:14px 12px 6px;white-space:nowrap;height:12px;box-sizing:content-box;opacity:0;transform:translateX(-6px);transition:opacity .2s ease,transform .25s ease;}
        .cf-item{position:relative;display:flex;align-items:center;gap:12px;width:100%;border:none;background:transparent;border-radius:6px;padding:9px 12px;font-size:13px;font-weight:400;color:#0a0a0a;cursor:pointer;font-family:inherit;text-align:left;white-space:nowrap;transition:background .18s ease,transform .18s cubic-bezier(.22,1,.36,1);}
        .cf-item:hover{background:#f4f4f4;transform:translateX(2px);}
        .cf-item:active{transform:scale(.96);}
        .cf-item.active{background:#f0f0f0;}
        .cf-item::before{content:'';position:absolute;left:-8px;top:50%;width:4px;height:18px;border-radius:0 4px 4px 0;background:#111;transform:translateY(-50%) scaleY(0);transition:transform .3s cubic-bezier(.34,1.56,.64,1);}
        .cf-item.active::before{transform:translateY(-50%) scaleY(1);}
        .cf-item svg{flex-shrink:0;color:#555;transition:transform .25s cubic-bezier(.34,1.56,.64,1),color .18s;}
        .cf-item:hover svg{color:#0a0a0a;}
        .cf-item.active svg{color:#0a0a0a;}
        .cf-l,.cf-item .cf-n{opacity:0;transform:translateX(-6px);transition:opacity .2s ease,transform .25s ease;}
        .cf-item .cf-n{margin-left:auto;font-size:11.5px;color:#666;font-weight:400;background:#f2f2f2;border-radius:4px;padding:1px 8px;min-width:12px;text-align:center;}
        .cf-item.active .cf-n{background:#0a0a0a;color:#fff;}
        .conv-folders:hover .cf-l,.conv-folders:hover .cf-n,.conv-folders:hover .cf-sec{opacity:1;transform:none;transition-delay:.08s;}
        .cf-lrow{position:relative;}
        .cf-lact{position:absolute;right:4px;top:50%;transform:translateY(-50%);display:flex;gap:2px;opacity:0;pointer-events:none;transition:opacity .15s;}
        .conv-folders:hover .cf-lrow:hover .cf-lact{opacity:1;pointer-events:auto;}
        .cf-lact button{border:none;background:#fff;color:#666;width:22px;height:22px;border-radius:5px;display:flex;align-items:center;justify-content:center;cursor:pointer;box-shadow:0 0 0 1px #e5e5e5;transition:color .15s,background .15s;}
        .cf-lact button:hover{color:#111;background:#f5f5f5;}
        .cf-add{color:#666;} .conv-folders:not(:hover) .cf-lform{display:none;}
        .cf-lform{margin:4px 2px 6px;padding:10px;border:1px solid #e5e5e5;border-radius:8px;background:#fff;animation:clpPop .2s ease both;}
        .cf-lform input{width:100%;box-sizing:border-box;border:1px solid #dcdcdc;border-radius:6px;padding:7px 9px;font-size:12.5px;font-family:inherit;outline:none;}
        .cf-lform input:focus{border-color:#111;}
        .cf-colors{display:flex;flex-wrap:wrap;gap:6px;margin:8px 0;}
        .cf-colors button{width:16px;height:16px;border-radius:4px;border:none;cursor:pointer;box-shadow:0 0 0 1px rgba(0,0,0,.08);transition:box-shadow .15s;}
        .cf-colors button.sel{box-shadow:0 0 0 2px #fff,0 0 0 3.5px #111;}
        .cf-lerr{font-size:11.5px;color:#b42318;margin-bottom:6px;}
        .cf-lbtns{display:flex;gap:6px;justify-content:flex-end;}
        .cf-lbtns button{border:1px solid #dcdcdc;background:#fff;border-radius:6px;padding:5px 10px;font-size:12px;font-weight:400;font-family:inherit;cursor:pointer;}
        .cf-lbtns button.pri{background:#111;color:#fff;border-color:#111;}
        .cf-dot{width:9px;height:9px;border-radius:2px;flex-shrink:0;margin:0 3.5px;transition:transform .25s cubic-bezier(.34,1.56,.64,1);}
        .cf-item:hover .cf-dot{transform:scale(1.35);}
        @media (prefers-reduced-motion: reduce){ .conv-folders,.conv-folders > *,.cf-item,.cf-item svg,.cf-l,.cf-n,.cf-sec{animation:none!important;transition:none!important;} }
        @keyframes clpPop{from{opacity:0;transform:translateY(-6px) scale(.97);}to{opacity:1;transform:none;}}
        .clp-active-filter{display:flex;align-items:center;justify-content:space-between;gap:8px;background:#f2f2f2;color:#0a0a0a;border-radius:6px;padding:6px 6px 6px 12px;margin:-4px 0 12px;font-size:12px;animation:clpPop .3s cubic-bezier(.22,1,.36,1) both;}
        .clp-active-filter b{font-weight:400;color:#0a0a0a;background:#a3e31c;border-radius:4px;padding:2px 8px;margin-left:4px;}
        .clp-active-filter button{border:none;background:#e4e4e4;color:#0a0a0a;width:24px;height:24px;border-radius:5px;display:flex;align-items:center;justify-content:center;cursor:pointer;transition:background .15s;}
        .clp-active-filter button:hover{background:#d6d6d6;}
        .clp-empty{display:flex;flex-direction:column;align-items:center;gap:10px;padding:48px 20px;text-align:center;animation:clpPop .35s cubic-bezier(.22,1,.36,1) both;}
        .clp-empty-ic{width:44px;height:44px;border-radius:8px;background:#f4f4f4;color:#888;display:flex;align-items:center;justify-content:center;}
        .clp-empty-t{font-size:13px;color:#888;}
        .clp-empty-btn{border:none;background:#111;color:#fff;font-family:inherit;font-size:12.5px;font-weight:400;padding:8px 16px;border-radius:6px;cursor:pointer;transition:transform .18s cubic-bezier(.22,1,.36,1),background .15s;}
        .clp-empty-btn:hover{background:#2a2a2a;transform:translateY(-1px);} .clp-empty-btn:active{transform:scale(.96);}
        @keyframes gsIn{from{opacity:0;transform:translateY(6px);}to{opacity:1;transform:none;}}
        .gs-head{display:flex;align-items:center;gap:6px;}
        .gs-count{font-size:10.5px;font-weight:400;color:#666;background:#f2f2f2;border-radius:6px;padding:1px 7px;}
        .gs-none{font-size:12px;color:#aaa;padding:6px 16px 12px;}
        .gs-chat{display:flex;align-items:center;gap:12px;animation:gsIn .3s cubic-bezier(.22,1,.36,1) both;}
        .gs-av{width:36px;height:36px;border-radius:6px;background:#ececec;color:#333;display:flex;align-items:center;justify-content:center;font-size:13px;flex-shrink:0;transition:transform .25s cubic-bezier(.34,1.56,.64,1);}
        .gs-chat:hover .gs-av{transform:scale(1.08);}
        @keyframes ncPop{from{opacity:0;transform:translateY(14px) scale(.97);}to{opacity:1;transform:none;}}
        @keyframes ncIn{from{opacity:0;transform:translateY(5px);}to{opacity:1;transform:none;}}
        .nc-overlay{animation:ncIn .2s ease both;}
        .nc-modal{width:520px;max-width:calc(100vw - 32px);max-height:88vh;background:#fff;border-radius:10px;border:1px solid #e5e5e5;display:flex;flex-direction:column;overflow:hidden;box-shadow:0 18px 48px rgba(10,10,10,.16);animation:ncPop .26s cubic-bezier(.22,1,.36,1) both;}
        .nc-head{display:flex;align-items:flex-start;justify-content:space-between;padding:20px 22px 12px;}
        .nc-title{font-size:16px;font-weight:600;color:#111;letter-spacing:-.2px;}
        .nc-sub{font-size:12px;color:#888;margin-top:3px;}
        .nc-x{border:none;background:transparent;width:28px;height:28px;border-radius:6px;display:flex;align-items:center;justify-content:center;cursor:pointer;color:#666;transition:background .15s,color .15s;}
        .nc-x:hover{background:#f2f2f2;color:#111;}
        .nc-body{padding:4px 22px 16px;overflow-y:auto;}
        .nc-step{font-size:12px;font-weight:600;color:#444;margin:16px 0 6px;}
        .nc-n{display:none;}
        .nc-input{width:100%;box-sizing:border-box;border:1px solid #dcdcdc;border-radius:6px;padding:9px 11px;font-size:13.5px;font-family:inherit;outline:none;transition:border-color .15s,box-shadow .15s;background:#fff;}
        .nc-input:focus{border-color:#111;box-shadow:0 0 0 2px rgba(10,10,10,.06);}
        .nc-hint{font-size:12px;color:#666;margin-top:6px;} .nc-hint b{font-weight:600;color:#111;}
        .nc-suggest{display:flex;flex-direction:column;margin-top:4px;border:1px solid #e5e5e5;border-radius:6px;overflow:hidden;}
        .nc-sug{display:flex;align-items:center;gap:10px;border:none;border-bottom:1px solid #f2f2f2;background:#fff;padding:8px 10px;cursor:pointer;font-family:inherit;text-align:left;animation:ncIn .2s ease both;transition:background .12s;}
        .nc-sug:hover{background:#f4f4f4;}
        .nc-sn{font-size:13px;color:#0a0a0a;flex:1;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;} .nc-sp{font-size:12px;color:#888;}
        .nc-av{width:32px;height:32px;border-radius:6px;background:#ececec;color:#333;display:flex;align-items:center;justify-content:center;font-size:12.5px;font-weight:600;flex-shrink:0;}
        .nc-av.sm{width:24px;height:24px;border-radius:5px;font-size:11px;}
        .nc-picked{display:flex;align-items:center;gap:12px;border:1px solid #e5e5e5;border-radius:6px;padding:9px 11px;background:#fafafa;animation:ncIn .2s ease both;}
        .nc-pn{font-size:13.5px;color:#0a0a0a;} .nc-pp{font-size:12px;color:#888;}
        .nc-link{border:1px solid #dcdcdc;background:#fff;border-radius:6px;padding:5px 10px;font-size:12px;font-weight:400;cursor:pointer;font-family:inherit;transition:background .15s;}
        .nc-link:hover{background:#e6e6e6;}
        .nc-tpls{display:flex;flex-direction:column;gap:6px;max-height:210px;overflow-y:auto;}
        .nc-tpl{text-align:left;border:1px solid #e5e5e5;background:#fff;border-radius:6px;padding:9px 11px;cursor:pointer;font-family:inherit;animation:ncIn .2s ease both;transition:border-color .15s,background .15s;}
        .nc-tpl:hover{border-color:#bdbdbd;}
        .nc-tpl:active{background:#f5f5f5;}
        .nc-tpl.sel{border-color:#111;background:#fafafa;}
        .nc-tn{display:flex;align-items:center;gap:6px;font-size:13px;font-weight:500;color:#111;text-transform:capitalize;margin-bottom:3px;}
        .nc-tag{font-size:10.5px;font-weight:400;text-transform:none;border:1px solid #e0e0e0;color:#666;border-radius:4px;padding:0 5px;}
        .nc-tb{font-size:12px;color:#888;line-height:1.4;overflow:hidden;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;}
        .nc-preview{margin-top:4px;background:#fafafa;border:1px solid #ececec;border-radius:6px;padding:10px 12px;font-size:13px;color:#333;line-height:1.5;white-space:pre-wrap;animation:ncIn .2s ease both;}
        .as-list{display:flex;flex-direction:column;border:1px solid #e5e5e5;border-radius:6px;overflow:hidden;max-height:340px;overflow-y:auto;}
        .as-item{display:flex;align-items:center;gap:12px;padding:10px 12px;border:none;border-bottom:1px solid #f1f1f1;background:#fff;cursor:pointer;font-family:inherit;text-align:left;animation:ncIn .2s ease both;transition:background .12s;}
        .as-item:last-child{border-bottom:none;}
        .as-item:hover{background:#f7f7f7;} .as-item.sel{background:#f4f4f4;}
        .as-av{width:32px;height:32px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:500;color:#444;flex-shrink:0;}
        .as-name{display:block;font-size:13.5px;color:#111;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
        .as-meta{display:block;font-size:11.5px;color:#888;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
        .tp-tabs{display:flex;border:1px solid #e3e3e3;border-radius:6px;overflow:hidden;}
        .tp-tabs button{flex:1;border:none;background:#fff;padding:8px 0;font-size:12.5px;font-weight:400;font-family:inherit;color:#666;cursor:pointer;transition:background .15s,color .15s;}
        .tp-tabs button + button{border-left:1px solid #e3e3e3;}
        .tp-tabs button.on{background:#111;color:#fff;}
        .nc-tpl:disabled{pointer-events:none;}
        .sp-headl{display:flex;align-items:center;gap:12px;}
        .sp-logo{width:30px;height:30px;padding:5px;border-radius:6px;background:#f1f8f5;border:1px solid #d2e8dd;box-sizing:border-box;}
        .sp-list{display:flex;flex-direction:column;gap:8px;max-height:380px;overflow-y:auto;}
        .sp-order{text-align:left;border:1px solid #e3e3e3;border-radius:8px;background:#fff;padding:11px 12px;cursor:pointer;font-family:inherit;animation:ncIn .2s ease both;transition:border-color .15s,box-shadow .15s;}
        .sp-order:hover{border-color:#008060;box-shadow:0 0 0 1px #008060;}
        .sp-row{display:flex;align-items:center;justify-content:space-between;gap:8px;}
        .sp-id{font-size:13.5px;font-weight:600;color:#111;}
        .sp-badge{font-size:11px;font-weight:400;border-radius:4px;padding:1px 7px;background:#fff4e5;color:#8a6116;border:1px solid #f5d9a8;}
        .sp-badge.ok{background:#e3f1df;color:#2c6a1f;border-color:#bfe0b5;}
        .sp-prod{font-size:12.5px;color:#555;margin:5px 0;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
        .sp-total{font-size:13px;color:#111;font-weight:500;} .sp-date{font-size:11.5px;color:#999;}
        .sp-acts{display:flex;flex-direction:column;border:1px solid #e3e3e3;border-radius:8px;overflow:hidden;}
        .sp-act{display:flex;align-items:center;gap:12px;padding:10px 12px;border:none;border-bottom:1px solid #f0f0f0;background:#fff;cursor:pointer;font-family:inherit;text-align:left;transition:background .12s;}
        .sp-act:last-child{border-bottom:none;} .sp-act:hover:not(:disabled){background:#f6faf8;}
        .sp-act:disabled{cursor:not-allowed;opacity:.45;}
        .sp-aic{width:30px;height:30px;border-radius:6px;background:#f1f8f5;color:#008060;display:flex;align-items:center;justify-content:center;flex-shrink:0;}
        .sp-at{flex:1;display:flex;flex-direction:column;} .sp-at b{font-size:13px;font-weight:500;color:#111;} .sp-at span{font-size:11.5px;color:#888;margin-top:1px;}
        .sp-act.danger .sp-aic{background:#fdf0ef;color:#b42318;} .sp-act.danger b{color:#b42318;} .sp-act.danger:hover:not(:disabled){background:#fff7f6;}
        .sp-inl{display:flex;gap:8px;} .sp-inl .nc-input{flex:1;}
        .sp-btn{border:none;border-radius:6px;background:#008060;color:#fff;font-family:inherit;font-size:12.5px;font-weight:400;padding:0 14px;cursor:pointer;flex-shrink:0;transition:background .15s;}
        .sp-btn:hover:not(:disabled){background:#006e52;} .sp-btn:disabled{background:#a9cfc1;cursor:not-allowed;}
        .sp-cur{font-size:12px;color:#777;margin-bottom:6px;display:flex;flex-wrap:wrap;gap:4px;}
        .sp-tag{font-size:11.5px;background:#f1f1f1;color:#333;border-radius:4px;padding:1px 7px;}
        .sp-ok{font-size:12.5px;color:#1f5f3f;background:#effaf4;border:1px solid #c9ead8;border-radius:6px;padding:8px 11px;margin-bottom:10px;animation:ncIn .2s ease both;}
        .nc-notice{display:flex;gap:10px;align-items:flex-start;background:#f7f7f7;border:1px solid #e5e5e5;border-radius:6px;padding:10px 12px;font-size:12px;color:#444;line-height:1.5;margin-top:2px;}
        .nc-notice svg{flex-shrink:0;margin-top:2px;color:#666;}
        .nc-empty{font-size:12.5px;color:#999;padding:10px 2px;}
        .nc-foot{padding:14px 22px 18px;border-top:1px solid #f1f1f1;}
        .nc-err{font-size:12.5px;color:#b42318;background:#fff5f4;border:1px solid #f5d0cc;border-radius:6px;padding:8px 11px;margin-bottom:10px;animation:ncIn .2s ease both;}
        .nc-btn{border:none;border-radius:6px;padding:10px 0;font-size:13.5px;font-weight:400;font-family:inherit;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:8px;transition:background .15s;}
        .nc-btn:active{opacity:.85;}
        .nc-btn.ghost{flex:1;background:#fff;color:#111;border:1px solid #dcdcdc;} .nc-btn.ghost:hover{background:#f5f5f5;}
        .nc-btn.primary{flex:2;background:#111;color:#fff;} .nc-btn.primary:hover{background:#2b2b2b;} .nc-btn.primary:disabled{background:#777;cursor:not-allowed;}
        .conv-list-panel{width:340px;background:#fff;border-right:1px solid #ebebeb;display:flex;flex-direction:column;flex-shrink:0;}
        .clp-order-filter{position:relative;margin:0 12px 8px;}
        .clp-order-btn{width:100%;padding:8px 12px;border-radius:10px;border:1.5px solid #e8e8e6;background:#f9f9f7;font-size:13px;font-weight:500;color:#555;cursor:pointer;display:flex;align-items:center;justify-content:space-between;font-family:inherit;}
        .clp-order-btn.active{border-color:#0a0a0a;background:#0a0a0a;color:#fff;}
        .clp-order-dropdown{position:absolute;top:calc(100% + 4px);left:0;right:0;background:#fff;border:1px solid #e8e8e6;border-radius:12px;box-shadow:0 8px 24px rgba(0,0,0,0.1);z-index:100;overflow:hidden;}
        .clp-od-item{padding:9px 14px;font-size:13px;cursor:pointer;display:flex;align-items:center;gap:8px;transition:background 0.1s;}
        .clp-od-item:hover{background:#f5f5f3;}
        .clp-od-item.active{font-weight:700;color:#0a0a0a;}
        .src-dot{display:inline-block;width:8px;height:8px;border-radius:50%;flex-shrink:0;}
        .src-dot.src-organic{background:#a0a0a0;} .src-dot.src-meta_ad{background:#1877f2;} .src-dot.src-nexus_landing{background:#8b5cf6;}
        .src-dot.src-nexus_recovery{background:#f97316;} .src-dot.src-social_ad{background:#ec4899;} .src-dot.src-nexus_catalog{background:#14b8a6;}
        .clp-tabs{display:flex;padding:0 12px 8px;gap:4px;overflow-x:auto;}
        .clp-tabs::-webkit-scrollbar{height:3px;}
        .clp-tabs::-webkit-scrollbar-thumb{background:#ddd;border-radius:3px;}
        .clp-tab{padding:5px 12px;border-radius:999px;font-size:12px;font-weight:500;color:#888;cursor:pointer;border:1.5px solid transparent;background:transparent;white-space:nowrap;font-family:inherit;flex-shrink:0;}
        .clp-tab.active{border-color:#0a0a0a;background:#0a0a0a;color:#fff;font-weight:700;}
        .clp-header{padding:20px 16px 0;}
        .clp-top-row{display:flex;align-items:center;justify-content:space-between;margin-bottom:14px;}
        .clp-title{font-size:22px;font-weight:700;color:#0a0a0a;letter-spacing:-0.4px;}
        .clp-top-actions{display:flex;align-items:center;gap:6px;}
        .clp-new-btn{display:flex;align-items:center;gap:5px;font-size:13px;font-weight:500;color:#0a0a0a;background:#f5f5f5;border:none;border-radius:999px;padding:6px 12px;cursor:pointer;font-family:'SF Pro Display','SF Pro Text',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;transition:background 0.15s;}
        .clp-new-btn:hover{background:#ebebeb;}
        .clp-filter-btn{width:30px;height:30px;border-radius:50%;background:transparent;border:none;cursor:pointer;display:flex;align-items:center;justify-content:center;transition:background 0.15s;}
        .clp-filter-btn:hover{background:#f0f0f0;}
        .clp-search{display:flex;align-items:center;gap:8px;background:#f5f5f5;border-radius:10px;padding:0 12px;height:36px;margin:0 0 14px;}
        .clp-search input{background:none;border:none;outline:none;font-size:13px;color:#0a0a0a;font-family:'SF Pro Display','SF Pro Text',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;width:100%;}
        .clp-search input::placeholder{color:#c0c0c0;}
        .clp-toggle{display:flex;background:#f5f5f5;border-radius:10px;padding:3px;margin:0 0 16px;}
        .clp-toggle-btn{flex:1;text-align:center;padding:7px 0;border-radius:8px;font-size:13px;font-weight:500;cursor:pointer;border:none;background:transparent;font-family:'SF Pro Display','SF Pro Text',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;color:#888;transition:all 0.15s;}
        .clp-toggle-btn.active{background:#fff;color:#0a0a0a;font-weight:700;box-shadow:0 1px 3px rgba(0,0,0,0.09);}
        .clp-section-label{font-size:11px;font-weight:700;color:#b0b0b0;letter-spacing:0.04em;padding:10px 16px 6px;text-transform:none;}
        .clp-list{flex:1;overflow-y:auto;}
        .clp-list::-webkit-scrollbar{width:0;}
        .gs-item{padding:10px 16px;cursor:pointer;transition:background 0.1s;border-bottom:1px solid #f4f4f2;}
        .gs-item:hover{background:#f8f8f6;}
        .gs-name{font-size:13px;font-weight:700;color:#0a0a0a;margin-bottom:2px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
        .gs-snippet{font-size:12px;color:#888;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
        .gs-time{font-size:10px;color:#c0c0c0;flex-shrink:0;margin-left:6px;}

        /* Conv items */
        .conv-item{display:flex;align-items:center;gap:12px;padding:11px 16px;cursor:pointer;transition:background 0.1s;position:relative;}
        .conv-item:hover{background:#f8f8f8;}
        .conv-item.active{background:#f0f0f0;}
        .ci-av-wrap{position:relative;flex-shrink:0;}
        .ci-av{width:44px;height:44px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:13px;font-weight:700;color:#555;}
        .ci-online{position:absolute;bottom:1px;left:1px;width:11px;height:11px;border-radius:50%;background:#22c55e;border:2px solid #fff;}
        .ci-body{flex:1;min-width:0;}
        .ci-top{display:flex;justify-content:space-between;align-items:center;margin-bottom:2px;}
        .ci-name{font-size:14px;font-weight:700;color:#0a0a0a;letter-spacing:-0.1px;}
        .ci-time{font-size:11px;color:#b8b8b8;flex-shrink:0;}
        .ci-preview{font-size:12.5px;color:#9a9a9a;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
        .ci-badge{min-width:19px;height:19px;border-radius:999px;background:#2196F3;color:#fff;font-size:10px;font-weight:700;display:flex;align-items:center;justify-content:center;padding:0 5px;flex-shrink:0;margin-left:4px;}
        .ci-label-dot{width:7px;height:7px;border-radius:50%;flex-shrink:0;margin-left:4px;}
        @keyframes ctxIn{from{opacity:0;transform:translateY(-4px) scale(.98);}to{opacity:1;transform:none;}}
        .ctx-menu{position:fixed;background:#fff;border:1px solid #e3e3e3;border-radius:8px;padding:4px;width:220px;max-height:min(420px,calc(var(--vh, 100vh) - 24px));overflow-y:auto;box-shadow:0 10px 28px rgba(10,10,10,.14);z-index:900;animation:ctxIn .14s ease-out both;transform-origin:top left;}
        .ctx-head{padding:8px 10px 6px;font-size:12px;color:#111;font-weight:600;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
        .ctx-title{padding:8px 10px 4px;font-size:11px;font-weight:500;color:#8a8a8a;}
        .ctx-item{padding:7px 10px;border-radius:5px;cursor:pointer;font-size:13px;font-weight:400;color:#111;display:flex;align-items:center;gap:9px;transition:background .1s;}
        .ctx-item:hover{background:#f3f3f3;}
        .ctx-item svg{flex-shrink:0;color:#666;}
        .ctx-item.on{background:#f6f6f6;}
        .ctx-dot{width:8px;height:8px;border-radius:2px;flex-shrink:0;}
        .ctx-note{padding:4px 10px 8px;font-size:12px;color:#999;line-height:1.4;}
        .ctx-sep{height:1px;background:#eee;margin:4px 0;}
        .chat-panel{flex:1;display:flex;flex-direction:column;background:#fff;border:none;border-radius:0;box-shadow:none;overflow:hidden;min-height:0;min-width:0;}
        .chat-header{background:#fff;border-bottom:1px solid #ebebeb;display:flex;align-items:center;justify-content:space-between;padding:12px 20px;gap:12px;flex-shrink:0;}
        .ch-av{width:46px;height:46px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:14px;font-weight:700;color:#555;position:relative;flex-shrink:0;}
        .ch-channel{position:absolute;bottom:-3px;right:-3px;width:16px;height:16px;border-radius:4px;background:#fff;display:flex;align-items:center;justify-content:center;border:1px solid #fff;}
        .ch-info{flex:1;min-width:0;}
        .ch-name-row{display:flex;align-items:center;gap:8px;margin-bottom:2px;}
        .ch-name{font-size:15px;font-weight:600;color:#111;}
        .ch-note-badge{background:#f2f2f2;color:#444;border-radius:4px;padding:1px 7px;font-size:11.5px;font-weight:400;}
        .ch-sub{font-size:12px;color:#9CA3AF;}
        .ch-actions{display:flex;gap:6px;align-items:center;}
        .ch-btn-icon{width:32px;height:32px;border-radius:6px;border:none;background:transparent;display:flex;align-items:center;justify-content:center;cursor:pointer;transition:background .15s,color .15s;flex-shrink:0;color:#6B7280;}
        .ch-status{display:flex;align-items:center;gap:7px;height:30px;padding:0 10px;border:1px solid #e3e3e3;border-radius:6px;background:#fff;font-family:inherit;font-size:12.5px;font-weight:400;color:#111;cursor:pointer;transition:border-color .15s,background .15s;}
        .ch-status:hover{border-color:#c8c8c8;background:#fafafa;}
        .ch-sdot{width:7px;height:7px;border-radius:2px;flex-shrink:0;}
        .ch-menu{position:absolute;top:calc(100% + 6px);right:0;background:#fff;border:1px solid #e3e3e3;border-radius:8px;padding:4px;width:200px;box-shadow:0 10px 28px rgba(10,10,10,.12);z-index:300;animation:ctxIn .14s ease-out both;}
        .ch-mi{display:flex;align-items:center;gap:9px;padding:7px 10px;border-radius:5px;font-size:13px;font-weight:400;color:#111;cursor:pointer;transition:background .1s;}
        .ch-mi:hover{background:#f3f3f3;} .ch-mi.on{background:#f6f6f6;}
        .ch-bot{display:flex;align-items:center;gap:6px;height:30px;padding:0 10px;border:1px solid #e3e3e3;border-radius:6px;background:#fff;font-family:inherit;font-size:12.5px;font-weight:400;color:#888;cursor:pointer;transition:border-color .15s,color .15s,background .15s;}
        .ch-bot:hover{border-color:#c8c8c8;}
        .ch-bot.on{color:#111;} .ch-bot.on svg{color:#16a34a;}
        .ch-vsep{width:1px;height:20px;background:#e8e8e8;margin:0 2px;}
        .ch-btn-icon:hover{background:#f2f2f2;color:#111;}
        .messages-area{flex:1;overflow-y:auto;padding:18px 24px;display:flex;flex-direction:column;gap:8px;background:#efeae2;}
        .date-sep{display:flex;align-items:center;justify-content:center;margin:8px 0;}
        .date-sep-line{display:none;}
        .date-sep-label{font-size:11px;font-weight:500;color:#54656f;background:rgba(255,255,255,.92);padding:3px 10px;border-radius:4px;box-shadow:none;border:1px solid rgba(0,0,0,.05);}
        .msg-row{display:flex;flex-direction:column;max-width:68%;}
        .msg-row.out{align-self:flex-end;align-items:flex-end;}
        .msg-row.in{align-self:flex-start;align-items:flex-start;}
        .msg-meta{display:flex;align-items:center;gap:5px;margin-bottom:3px;}
        .msg-av-sm{width:22px;height:22px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:10.5px;font-weight:500;flex-shrink:0;}
        .msg-sender{font-size:11px;font-weight:500;color:#555;}
        .msg-ts{font-size:10px;color:#9a9a9a;}
        .msg-bubble{padding:8px 11px;font-size:13.5px;line-height:1.5;word-break:break-word;white-space:pre-wrap;box-shadow:none;}
        .msg-bubble.in{background:#ffffff;color:#111;border-radius:2px 8px 8px 8px;border:1px solid #e3e3e3;}
        .msg-bubble.out{background:#d9fdd3;color:#111;border-radius:8px 2px 8px 8px;border:1px solid rgba(0,0,0,.04);}
        .tplb{width:300px;max-width:100%;white-space:normal;}
        .tplb-media{display:block;width:calc(100% + 10px);margin:-4px -5px 7px;max-height:320px;object-fit:cover;border-radius:6px;cursor:pointer;background:rgba(0,0,0,.04);}
        .tplb-ph{display:flex;align-items:center;justify-content:center;height:120px;margin:-4px -5px 7px;border-radius:6px;background:rgba(0,0,0,.05);color:#8a8a8a;font-size:12px;}
        .tplb-h{font-weight:600;margin-bottom:4px;white-space:pre-wrap;}
        .tplb-body{white-space:pre-wrap;}
        .tplb-foot{font-size:12px;color:#667781;margin-top:4px;white-space:pre-wrap;}
        .tplb-btns{margin:8px -11px -8px;}
        .tplb-btn{display:flex;align-items:center;justify-content:center;gap:6px;padding:9px 10px;border-top:1px solid rgba(0,0,0,.08);color:#027eb5;font-size:13.5px;font-weight:400;text-align:center;}
        .quoted-msg{display:flex;gap:8px;margin-bottom:6px;padding:6px 10px;border-radius:5px;background:rgba(0,0,0,0.05);cursor:default;overflow:hidden;}
        .quoted-bar{width:3px;border-radius:2px;flex-shrink:0;background:#25d366;}
        .quoted-body{flex:1;min-width:0;}
        .quoted-sender{font-size:11px;font-weight:600;color:#075e54;margin-bottom:2px;}
        .quoted-text{font-size:12px;color:#555;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
        .msg-status-row{display:flex;align-items:center;gap:4px;margin-top:4px;}
        .msg-actions{display:none;gap:4px;margin-top:3px;}
        .msg-row:hover .msg-actions{display:flex;}
        .msg-action-btn{padding:3px 9px;border-radius:5px;border:1px solid #e4e4e2;background:#fff;cursor:pointer;font-size:11px;color:#555;display:flex;align-items:center;gap:4px;transition:background 0.12s;font-family:'SF Pro Display','SF Pro Text',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;}
        .msg-action-btn:hover{background:#f0f0ee;border-color:#d0d0ce;}
        .reply-preview{display:flex;align-items:center;gap:10px;padding:9px 18px;background:#f8f8f6;border-top:1px solid #eeeeec;border-bottom:none;}
        .reply-bar{width:3px;height:36px;background:#25d366;border-radius:2px;flex-shrink:0;}
        .reply-preview-body{flex:1;min-width:0;}
        .reply-preview-sender{font-size:11px;font-weight:600;color:#075e54;margin-bottom:2px;}
        .reply-preview-text{font-size:12px;color:#666;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
        .reply-close{background:none;border:none;cursor:pointer;padding:4px;color:#aaa;font-size:18px;line-height:1;flex-shrink:0;}
        .reply-close:hover{color:#555;}
        .chat-input-wrapper{background:#fff;border-top:1px solid #ebebeb;flex-shrink:0;position:relative;padding:12px 18px 12px;}
        .chat-input-pill{position:relative;border:1px solid #e8e8e8;border-radius:8px;background:#fff;padding:12px 104px 12px 14px;transition:border-color 0.2s;}
        .chat-input-pill:focus-within{border-color:#0a0a0a;}
        .ci-input{display:block;width:100%;background:transparent;border:none;outline:none;font-size:14px;font-family:'SF Pro Display','SF Pro Text',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;color:#111827;resize:none;height:auto;max-height:120px;overflow-y:auto;line-height:1.5;padding:0;}
        .ci-input::placeholder{color:#9CA3AF;}
        .ci-send{position:absolute;right:8px;bottom:8px;height:34px;padding:0 14px;border-radius:6px;background:#0a0a0a;color:#fff;border:none;display:flex;align-items:center;gap:7px;justify-content:center;cursor:pointer;flex-shrink:0;transition:background 0.15s;font-family:'SF Pro Display','SF Pro Text',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;font-size:13px;font-weight:400;}
        .ae{width:1.3em;height:1.3em;vertical-align:-0.28em;display:inline-block;margin:0 .03em;object-fit:contain;}
        .pm-bar{padding:10px 18px 0;background:#fff;border-top:1px solid #ebebeb;}
        .pm-card{display:flex;align-items:center;gap:12px;border:1px solid #e3e3e3;border-radius:8px;padding:8px;background:#fafafa;animation:ncIn .2s ease both;}
        .pm-thumb{width:48px;height:48px;border-radius:6px;background:#ececec;color:#555;display:flex;align-items:center;justify-content:center;overflow:hidden;flex-shrink:0;}
        .pm-thumb img,.pm-thumb video{width:100%;height:100%;object-fit:cover;}
        @keyframes recPulse{0%,100%{opacity:1;}50%{opacity:.35;}}
        .rec-bar{display:flex;align-items:center;gap:12px;border:1px solid #e3e3e3;border-radius:8px;background:#fff;padding:0 8px 0 14px;height:50px;animation:ncIn .2s ease both;}
        .rec-dot{width:9px;height:9px;border-radius:50%;background:#dc2626;animation:recPulse 1.2s ease-in-out infinite;flex-shrink:0;}
        .rec-time{font-size:13px;color:#111;font-variant-numeric:tabular-nums;min-width:38px;}
        .rec-wave{flex:1;display:flex;align-items:center;gap:3px;height:26px;overflow:hidden;}
        .rec-wave i{flex:1;max-width:4px;height:100%;background:#111;border-radius:2px;transform:scaleY(.12);transition:transform .08s linear;}
        .rec-max{font-size:11px;color:#9a9a9a;flex-shrink:0;}
        .pm-grid{display:flex;flex-wrap:wrap;gap:6px;}
        .pm-thumb.sm{width:44px;height:44px;position:relative;overflow:visible;}
        .pm-thumb.sm img,.pm-thumb.sm video{border-radius:6px;}
        .pm-x{position:absolute;top:-5px;right:-5px;width:17px;height:17px;border-radius:50%;border:1.5px solid #fff;background:#111;color:#fff;display:flex;align-items:center;justify-content:center;cursor:pointer;padding:0;opacity:0;transition:opacity .12s;}
        .pm-thumb.sm:hover .pm-x{opacity:1;}
        .pm-add{border:1px dashed #c8c8c8;background:#fff;color:#888;cursor:pointer;}
        .pm-add:hover{border-color:#111;color:#111;}
        .pm-progress{display:flex;align-items:center;gap:10px;border:1px solid #e3e3e3;border-radius:8px;padding:10px 12px;font-size:12.5px;color:#444;background:#fafafa;}
        .pm-track{flex:1;height:4px;background:#e8e8e8;border-radius:2px;overflow:hidden;}
        .pm-track span{display:block;height:100%;background:#111;transition:width .3s ease;}
        .pm-ext{font-size:10.5px;font-weight:600;color:#444;letter-spacing:.3px;}
        .pm-info{flex:1;min-width:0;}
        .pm-name{font-size:13px;color:#111;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
        .pm-meta{font-size:11.5px;color:#888;margin-top:2px;}
        .pm-btn{height:32px;padding:0 12px;border-radius:6px;font-family:inherit;font-size:12.5px;font-weight:400;cursor:pointer;display:flex;align-items:center;gap:6px;flex-shrink:0;transition:background .15s;}
        .pm-btn.ghost{border:1px solid #dcdcdc;background:#fff;color:#111;} .pm-btn.ghost:hover{background:#f5f5f5;}
        .pm-btn.primary{border:none;background:#111;color:#fff;} .pm-btn.primary:hover{background:#2b2b2b;} .pm-btn.primary:disabled{background:#777;cursor:not-allowed;}
        .ci-count{position:absolute;right:110px;bottom:16px;font-size:11px;color:#9a9a9a;font-variant-numeric:tabular-nums;pointer-events:none;}
        .ci-count.over{color:#b42318;}
        .toolbar-btn:disabled{opacity:.4;cursor:not-allowed;transform:none!important;}
        .ci-send:hover{background:#2a2a2a;} .ci-send:disabled{background:#ccc;cursor:not-allowed;}
        .chat-toolbar{background:transparent;border-radius:0;padding:6px 0 0;margin-top:4px;display:flex;align-items:center;gap:2px;border:none;box-shadow:none;flex-wrap:wrap;}
        .toolbar-sep{width:1px;height:16px;background:#e8e8e6;margin:0 3px;flex-shrink:0;}
        .toolbar-btn{display:flex;align-items:center;gap:7px;padding:8px 12px;border-radius:6px;border:none;background:transparent;cursor:pointer;font-size:13px;font-family:'SF Pro Display','SF Pro Text',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;font-weight:400;color:#555;transition:all .2s cubic-bezier(.4,0,.2,1);position:relative;white-space:nowrap;}
        .toolbar-btn:hover{background:#f5f5f3;color:#111;}
        .toolbar-btn:active{transform:translateY(0);background:#ebebeb;}
        .toolbar-btn svg{width:17px;height:17px;transition:transform .2s;}
        .toolbar-btn:hover svg{transform:scale(1.06);}
        .shopify-btn{color:#555;}
        .shopify-btn:hover{background:#f5f5f3;color:#111;}
        .ci-attach{display:none;}
        .ci-mic{display:none;}
        @keyframes pulse-red{0%,100%{box-shadow:0 0 0 0 rgba(239,68,68,0.3)}50%{box-shadow:0 0 0 6px rgba(239,68,68,0)}}
        .audio-player{display:flex;align-items:center;gap:10px;min-width:220px;max-width:320px;padding:2px 0;}
        .ap-play{width:36px;height:36px;border-radius:50%;border:none;display:flex;align-items:center;justify-content:center;cursor:pointer;flex-shrink:0;transition:background 0.15s;}
        .audio-player.out .ap-play{background:#075e54;color:#fff;}
        .audio-player.in .ap-play{background:#25d366;color:#fff;}
        .ap-play:hover{opacity:0.85;}
        .ap-body{flex:1;min-width:0;display:flex;flex-direction:column;gap:4px;}
        .ap-wave{height:28px;display:flex;align-items:center;gap:1.5px;cursor:pointer;padding:2px 0;}
        .ap-bar{width:3px;min-height:3px;border-radius:1.5px;transition:background 0.1s;}
        .ap-info{display:flex;justify-content:space-between;align-items:center;}
        .ap-dur{font-size:11px;color:#667781;font-variant-numeric:tabular-nums;}
        .ap-time{font-size:10.5px;color:#667781;}
        @media(max-width:600px){.audio-player{min-width:180px;max-width:260px;}.ap-wave{height:24px;}}
        .cl-header{display:flex;align-items:center;gap:12px;margin-bottom:12px;}
        .cl-avatar{width:44px;height:44px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:15px;font-weight:600;color:#555;flex-shrink:0;}
        .cl-name-row{flex:1;min-width:0;}
        .cl-name{font-size:15px;font-weight:600;color:#111;line-height:1.2;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
        .cl-detail{display:flex;flex-direction:column;gap:3px;margin-bottom:8px;}
        .cl-detail-row{display:flex;align-items:center;gap:6px;font-size:12px;color:#555;line-height:1.3;}
        .cl-detail-row svg{flex-shrink:0;opacity:0.4;}
        .cl-detail-val{min-width:0;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
        .cl-stats{display:flex;gap:0;border-top:1px solid #f0efed;padding-top:8px;}
        .cl-stat{flex:1;text-align:center;}
        .cl-stat:first-child{border-right:1px solid #f0efed;}
        .cl-stat-num{font-size:15px;font-weight:600;color:#111;line-height:1.2;}
        .cl-stat-label{font-size:10px;color:#999;margin-top:1px;}
        .op-card{background:#f8f8f6;border-radius:12px;padding:12px;border:1px solid #f0efed;}
        .op-head{display:flex;justify-content:space-between;align-items:center;margin-bottom:10px;}
        .op-id{font-size:14px;font-weight:700;color:#0a0a0a;letter-spacing:-0.02em;}
        .op-item{display:flex;gap:10px;align-items:flex-start;padding:6px 0;}
        .op-item:not(:last-child){border-bottom:1px solid rgba(0,0,0,0.04);padding-bottom:8px;margin-bottom:2px;}
        .op-img{width:48px;height:48px;border-radius:8px;object-fit:cover;background:#eee;flex-shrink:0;}
        .op-img-placeholder{width:48px;height:48px;border-radius:8px;background:#f0efed;display:flex;align-items:center;justify-content:center;flex-shrink:0;}
        .op-item-body{flex:1;min-width:0;}
        .op-item-name{font-size:12.5px;font-weight:600;color:#1a1a1a;line-height:1.3;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;}
        .op-item-variant{font-size:11px;color:#888;margin-top:1px;}
        .op-item-qty{font-size:10px;color:#aaa;margin-top:1px;}
        .op-footer{display:flex;justify-content:space-between;align-items:center;margin-top:8px;padding-top:8px;border-top:1px solid rgba(0,0,0,0.04);}
        .op-price{font-size:15px;font-weight:700;color:#0a0a0a;letter-spacing:-0.02em;}
        .op-payment{font-size:11px;font-weight:600;padding:3px 8px;border-radius:999px;}
        .tr-grid{display:flex;flex-direction:column;gap:4px;}
        .tr-row{display:flex;justify-content:space-between;align-items:center;padding:3px 0;}
        .tr-label{font-size:11px;color:#888;}
        .tr-value{font-size:11px;font-weight:600;color:#0a0a0a;text-align:right;max-width:55%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
        .tr-section{}
        .tr-section-label{font-size:11px;font-weight:500;color:#888;margin-bottom:5px;}
        .tr-pills{display:flex;flex-wrap:wrap;gap:4px;margin-top:2px;}
        .tr-pill{font-size:10.5px;font-weight:400;padding:1px 7px;border-radius:4px;white-space:nowrap;text-transform:capitalize;}
        .info-panel{width:320px;background:#fff;border-left:1px solid #ebebeb;display:flex;flex-direction:column;flex-shrink:0;overflow:hidden;}
        .ip-tabs{display:flex;gap:0;flex-shrink:0;padding:0 16px;border-bottom:1px solid #f0f0ee;}
        .ip-tab{flex:1;padding:13px 8px;font-size:13px;font-weight:500;color:#bbb;text-align:center;cursor:pointer;border-bottom:2.5px solid transparent;transition:all 0.2s;letter-spacing:-0.01em;}
        .ip-tab:hover{color:#888;}
        .ip-tab.active{color:#0a0a0a;font-weight:700;border-bottom-color:#0a0a0a;}
        .ip-body{flex:1;overflow-y:auto;padding:18px 16px;display:flex;flex-direction:column;gap:18px;}
        .ip-section-title{font-size:12.5px;font-weight:600;color:#111;margin-bottom:4px;display:flex;align-items:center;gap:6px;}
        .ip-section-title svg{opacity:0.45;}
        .ip-card{background:#fafafa;border-radius:8px;padding:12px;border:1px solid #ececec;}
        .ip-row{display:flex;justify-content:space-between;align-items:flex-start;padding:7px 0;border-bottom:1px solid rgba(0,0,0,0.04);}
        .ip-row:last-child{border-bottom:none;padding-bottom:0;}
        .ip-row:first-child{padding-top:0;}
        .ip-label{font-size:12px;color:#999;white-space:nowrap;}
        .ip-value{font-size:12px;font-weight:500;color:#1a1a1a;text-align:right;max-width:58%;word-break:break-word;}
        .order-card{background:linear-gradient(135deg,#fafaf8 0%,#f5f5f3 100%);border-radius:14px;padding:14px;margin-bottom:0;border:1px solid #eeeeed;transition:box-shadow 0.2s;}
        .order-card:hover{box-shadow:0 2px 12px rgba(0,0,0,0.04);}
        .order-card-top{display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;}
        .order-num{font-size:14px;font-weight:700;color:#0a0a0a;letter-spacing:-0.02em;}
        .order-status{font-size:10px;font-weight:700;padding:3px 10px;border-radius:999px;letter-spacing:0.02em;}
        .status-entregado{background:#f0fdf4;color:#16a34a;}
        .status-despachado{background:#eff6ff;color:#3b82f6;}
        .status-pendiente{background:#fef9c3;color:#854d0e;}
        .order-product{font-size:12px;color:#666;margin-bottom:2px;line-height:1.4;}
        .order-price{font-size:14px;font-weight:700;color:#0a0a0a;margin-top:6px;letter-spacing:-0.02em;}
        .order-meta{display:flex;justify-content:space-between;align-items:center;margin-top:6px;padding-top:6px;border-top:1px solid #f0f0ee;}
        .order-guia{font-size:11px;color:#3b82f6;font-weight:500;}
        .novedad-item{display:flex;gap:10px;margin-bottom:10px;position:relative;}
        .novedad-item:not(:last-child)::after{content:'';position:absolute;left:5px;top:14px;bottom:-4px;width:1px;background:#e8e8e6;}
        .novedad-dot{width:11px;height:11px;border-radius:50%;flex-shrink:0;margin-top:3px;}
        .novedad-title{font-size:12px;font-weight:700;margin-bottom:1px;}
        .novedad-desc{font-size:11px;color:#888;}
        .novedad-time{font-size:10px;color:#bbb;margin-top:1px;}
        .hist-item{display:flex;justify-content:space-between;align-items:center;padding:9px 11px;background:#fff;border-radius:6px;margin-bottom:6px;border:1px solid #ececec;transition:background .15s;}
        .hist-item:hover{background:#fafafa;}
        .note-btn:disabled{background:#bdbdbd;cursor:not-allowed;}
        .note-input:focus{border-color:#111;}
        .cl-actions{display:grid;grid-template-columns:repeat(4,1fr);gap:4px;margin:2px 0 14px;}
        .cl-actions button{display:flex;flex-direction:column;align-items:center;gap:4px;border:none;background:transparent;border-radius:6px;padding:8px 2px;font-family:inherit;font-size:11px;font-weight:400;color:#555;cursor:pointer;transition:background .15s,color .15s;}
        .cl-actions button svg{width:34px;height:34px;padding:8px;box-sizing:border-box;border:1px solid #e3e3e3;border-radius:8px;background:#fff;transition:border-color .15s;}
        .cl-actions button:hover{color:#111;} .cl-actions button:hover svg{border-color:#111;}
        .ip-sec-h{font-size:12.5px;font-weight:600;color:#111;margin:4px 0 8px;display:flex;align-items:center;gap:6px;padding-top:12px;border-top:1px solid #f0f0f0;}
        .ip-cnt{font-size:11px;font-weight:400;color:#666;background:#f2f2f2;border-radius:4px;padding:0 6px;}
        .nt-empty{font-size:12px;color:#999;margin-bottom:6px;}
        .nt-list{display:flex;flex-direction:column;gap:8px;}
        .nt-item{border:1px solid #ececec;border-left:3px solid #111;border-radius:6px;padding:9px 11px;background:#fff;animation:ncIn .2s ease both;}
        .nt-top{display:flex;align-items:center;gap:8px;margin-bottom:4px;}
        .nt-author{font-size:12px;font-weight:500;color:#111;}
        .nt-time{font-size:11px;color:#999;}
        .nt-del{margin-left:auto;border:none;background:transparent;color:#aaa;cursor:pointer;padding:2px;border-radius:4px;opacity:0;transition:opacity .15s,color .15s;}
        .nt-item:hover .nt-del{opacity:1;} .nt-del:hover{color:#b42318;}
        .nt-text{font-size:12.5px;color:#333;line-height:1.5;white-space:pre-wrap;}
        .note-input-wrap{display:flex;flex-direction:column;gap:7px;}
        .note-input{width:100%;box-sizing:border-box;min-height:70px;resize:vertical;background:#fff;border:1px solid #dcdcdc;border-radius:6px;padding:9px 11px;font-size:13px;font-family:inherit;outline:none;margin-top:8px;}
        .note-input::placeholder{color:#bbb;}
        .note-btn{margin-top:6px;width:100%;background:#111;color:#fff;border:none;border-radius:6px;padding:9px 0;font-size:12.5px;font-weight:400;cursor:pointer;font-family:inherit;transition:background .15s;}
        .empty-state{display:flex;flex-direction:column;align-items:center;justify-content:center;height:100%;gap:12px;color:#aaa;}
        @keyframes spin{to{transform:rotate(360deg)}}
        .spinner{width:18px;height:18px;border:2px solid #e8e8e6;border-top-color:#0a0a0a;border-radius:50%;animation:spin 0.7s linear infinite;}
        .live-dot{width:7px;height:7px;border-radius:50%;background:#22c55e;animation:pulse 2s infinite;}
        @keyframes pulse{0%,100%{opacity:1}50%{opacity:0.4}}
        .seg-badge{display:inline-block;padding:3px 10px;border-radius:999px;font-size:10px;font-weight:700;letter-spacing:0.03em;text-transform:capitalize;}
        .seg-vip{background:#fef9c3;color:#854d0e;} .seg-recurrente{background:#eff6ff;color:#1d4ed8;}
        .seg-nuevo{background:#f0fdf4;color:#166534;} .seg-inactivo{background:#f4f4f2;color:#888;}
        .modal-overlay{position:fixed;inset:0;background:rgba(0,0,0,0.4);z-index:500;display:flex;align-items:center;justify-content:center;}
        .modal{background:#fff;border-radius:20px;padding:24px;box-shadow:0 20px 60px rgba(0,0,0,0.2);max-height:90vh;display:flex;flex-direction:column;}
        .modal-hdr{display:flex;justify-content:space-between;align-items:center;margin-bottom:18px;flex-shrink:0;}
        .modal-title{font-size:17px;font-weight:700;color:#0a0a0a;}
        .modal-close{width:30px;height:30px;border-radius:8px;border:1px solid #e8e8e6;background:#f6f6f4;display:flex;align-items:center;justify-content:center;cursor:pointer;font-size:16px;color:#888;}
        .modal-search{display:flex;align-items:center;gap:8px;background:#f6f6f4;border:1px solid #e8e8e6;border-radius:10px;padding:0 12px;height:38px;margin-bottom:14px;flex-shrink:0;}
        .modal-search input{background:none;border:none;outline:none;font-size:13px;font-family:'SF Pro Display','SF Pro Text',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;color:#0a0a0a;width:100%;}
        .modal-scroll{flex:1;overflow-y:auto;}
        .tpl-item{padding:12px 14px;border-radius:12px;border:1px solid #e8e8e6;margin-bottom:8px;cursor:pointer;transition:all 0.15s;}
        .tpl-item:hover{border-color:#0a0a0a;background:#f8f8f6;}
        .tpl-name{font-size:13px;font-weight:700;color:#0a0a0a;margin-bottom:4px;}
        .tpl-text{font-size:12px;color:#888;line-height:1.5;}
        .tpl-badge{display:inline-block;font-size:10px;padding:2px 7px;border-radius:999px;font-weight:700;margin-bottom:6px;}
        .flow-item{display:flex;align-items:center;gap:12px;padding:12px 14px;border-radius:12px;border:1px solid #e8e8e6;margin-bottom:8px;cursor:pointer;transition:all 0.15s;}
        .flow-item:hover{border-color:#0a0a0a;background:#f8f8f6;}
        .options-menu{background:#fff;border:1px solid #e8e8e6;border-radius:12px;padding:6px;width:210px;box-shadow:0 4px 20px rgba(0,0,0,0.12);}
        .options-item{display:flex;align-items:center;gap:9px;padding:9px 12px;border-radius:8px;cursor:pointer;font-size:13px;font-weight:500;transition:background 0.12s;}
        .options-item:hover{background:#f4f4f2;} .options-item.danger{color:#ef4444;} .options-item.danger:hover{background:#fef2f2;}
        .action-btn{width:100%;padding:10px;border-radius:10px;border:1px solid #e8e8e6;background:#fff;font-size:13px;font-weight:500;cursor:pointer;font-family:'SF Pro Display','SF Pro Text',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;color:#0a0a0a;margin-bottom:8px;display:flex;align-items:center;gap:8px;transition:background 0.15s;}
        .action-btn:hover{background:#f4f4f2;} .action-btn.danger{color:#ef4444;border-color:#fee2e2;} .action-btn.danger:hover{background:#fef2f2;} .action-btn.disabled{opacity:0.4;cursor:not-allowed;}
        .call-option{display:flex;align-items:center;gap:12px;padding:14px;border-radius:12px;border:1px solid #e8e8e6;margin-bottom:8px;cursor:pointer;transition:all 0.15s;}
        .call-option:hover{border-color:#0a0a0a;background:#f8f8f6;}
        .info-toggle-btn{display:none;}
        .info-panel-overlay{display:none;}

        @media(max-width:900px){
          .conv-folders-wrap{display:none;}
        }
        @media(max-width:1080px){
          .conv-list-panel{width:280px;}
          .info-panel{position:fixed;right:0;top:0;bottom:0;z-index:600;width:340px;box-shadow:-8px 0 30px rgba(0,0,0,0.12);transform:translateX(100%);transition:transform 0.25s ease;}
          .info-panel.open{transform:translateX(0);}
          .info-panel-overlay{display:none;position:fixed;inset:0;background:rgba(0,0,0,0.3);z-index:599;}
          .info-panel-overlay.open{display:block;}
          .info-toggle-btn{display:flex;}
          .chat-header{padding:12px 16px;}
          .chat-input-wrapper{padding:8px 12px 8px;}
          .chat-toolbar{flex-wrap:nowrap;overflow-x:auto;gap:0;padding:3px 4px;margin-top:6px;}
          .chat-toolbar::-webkit-scrollbar{height:0;}
          .toolbar-btn{padding:5px 7px;font-size:0;gap:0;}
          .toolbar-btn svg,.toolbar-btn img{font-size:initial;}
          .toolbar-sep{display:none;}
          .messages-area{padding:14px;}
        }
        @media(max-width:820px){
          .conv-list-panel{width:240px;}
        }
      `}</style>

      <input ref={fileInputRef} type="file" multiple style={{display:'none'}}
        accept="image/*,video/mp4,video/3gpp,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip"
        onChange={e => {
          const files = [...(e.target.files || [])];
          if (files.length) {
            const items = files.map(file => {
              const type = file.type.startsWith('image/') ? 'image' : file.type.startsWith('video/') ? 'video' : file.type.startsWith('audio/') ? 'audio' : 'document';
              return { file, type, previewUrl: (type === 'image' || type === 'video') ? URL.createObjectURL(file) : null };
            });
            setPendingMedia(prev => {
              const all = [...(prev || []), ...items];
              all.slice(10).forEach(x => x.previewUrl && URL.revokeObjectURL(x.previewUrl));
              if (all.length > 10) setSendError('Puedes enviar máximo 10 archivos a la vez.');
              return all.slice(0, 10);
            });
          }
          e.target.value='';
        }}
      />

      <div className="app">

        <div className="app-body">

          {/* Context menu global */}
          {contextMenu && (() => {
            const cConv = conversations.find(c=>c.id===contextMenu.convId);
            const cLabel = cConv?.label || convLabels[contextMenu.convId];
            const isFav = favConvs.has(contextMenu.convId);
            // Que el menú no se salga de la pantalla
            const left = Math.min(contextMenu.x, window.innerWidth / escZ() - 232);
            const top = Math.min(contextMenu.y, window.innerHeight / escZ() - Math.min(420, 120 + tenantLabels.length * 32));
            return (
            <div className="ctx-menu" style={{top:Math.max(8, top), left:Math.max(8, left)}} onClick={e=>e.stopPropagation()}>
              <div className="ctx-head">{cConv?.contact_name || fmtPhone(cConv?.contact_phone) || 'Chat'}</div>
              <div className="ctx-item" onClick={()=>{ toggleFav(contextMenu.convId); setContextMenu(null); }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill={isFav ? "currentColor" : "none"} stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                {isFav ? 'Quitar de fijados' : 'Fijar chat'}
              </div>
              <div className="ctx-sep"/>
              <div className="ctx-title">Etiqueta</div>
              {tenantLabels.length === 0 && <div className="ctx-note">Aún no tienes etiquetas. Créalas en el panel de carpetas.</div>}
              {tenantLabels.map(({key: id, color, name: label}) => (
                <div key={id} className={`ctx-item${cLabel===id?' on':''}`} onClick={()=>{ setLabel(contextMenu.convId, cLabel===id ? null : id); setContextMenu(null); }}>
                  <span className="ctx-dot" style={{background:color}}/>
                  <span style={{flex:1,minWidth:0,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{label}</span>
                  {cLabel===id && <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="2.4" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>}
                </div>
              ))}
              {cLabel && (
                <>
                  <div className="ctx-sep"/>
                  <div className="ctx-item" onClick={()=>{ setLabel(contextMenu.convId, null); setContextMenu(null); }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    Quitar etiqueta
                  </div>
                </>
              )}
            </div>
            );
          })()}

          <div className="conv-layout" onClick={()=>{ if(contextMenu) setContextMenu(null); if(showLabelFilter) setShowLabelFilter(false); if(showOrderFilter) setShowOrderFilter(false); if(showSourceFilter) setShowSourceFilter(false); }}>
            {/* ── Columna 1: carpetas (replegada; se abre al pasar el mouse) ── */}
            <div className="conv-folders-wrap">
            <div className="conv-folders">
                <button title="Asignadas a mí" className={`cf-item${folder==='mias'?' active':''}`} onClick={()=>pickFilter('folder','mias')}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg><span className="cf-l">Asignadas a mí</span><span className="cf-n">{folderCount('mias')}</span></button>
                <button title="Sin asignar" className={`cf-item${folder==='sinasignar'?' active':''}`} onClick={()=>pickFilter('folder','sinasignar')}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7"/><line x1="16" y1="16" x2="22" y2="22"/><line x1="22" y1="16" x2="16" y2="22"/></svg><span className="cf-l">Sin asignar</span><span className="cf-n">{folderCount('sinasignar')}</span></button>
                <button title="Todas" className={`cf-item${folder==='todas'?' active':''}`} onClick={()=>pickFilter('folder','todas')}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 12h-6l-2 3h-4l-2-3H2"/><path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/></svg><span className="cf-l">Todas</span><span className="cf-n">{folderCount('todas')}</span></button>
                <button title="Fijadas" className={`cf-item${folder==='favoritos'?' active':''}`} onClick={()=>pickFilter('folder','favoritos')}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg><span className="cf-l">Fijadas</span><span className="cf-n">{folderCount('favoritos')}</span></button>
              <div className="cf-sec">Estado</div>
                <button title="Abiertas" className={`cf-item${folder==='open'?' active':''}`} onClick={()=>pickFilter('folder','open')}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg><span className="cf-l">Abiertas</span><span className="cf-n">{folderCount('open')}</span></button>
                <button title="Esperando cliente" className={`cf-item${folder==='waiting_client'?' active':''}`} onClick={()=>pickFilter('folder','waiting_client')}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 15 15"/></svg><span className="cf-l">Esperando cliente</span><span className="cf-n">{folderCount('waiting_client')}</span></button>
                <button title="Escaladas" className={`cf-item${folder==='escalated'?' active':''}`} onClick={()=>pickFilter('folder','escalated')}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg><span className="cf-l">Escaladas</span><span className="cf-n">{folderCount('escalated')}</span></button>
                <button title="Resueltas" className={`cf-item${folder==='resolved'?' active':''}`} onClick={()=>pickFilter('folder','resolved')}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg><span className="cf-l">Resueltas</span><span className="cf-n">{folderCount('resolved')}</span></button>
              {conversations.some(c => c.order_estado) && <div className="cf-sec">Pedido</div>}
              {[['sin_confirmar','Sin confirmar'],['por_generar','Por generar'],['despachado','Despachado'],['en_camino','En camino'],['en_destino','En destino'],['en_reparto','En reparto'],['entregado','Entregado'],['novedad','Novedad'],['devolucion','Devolución'],['cancelado','Cancelado']].filter(([v]) => conversations.some(c => c.order_estado === v)).map(([v, l]) => (
                <button key={v} title={l} className={`cf-item${orderFilter===v?' active':''}`} onClick={()=>pickFilter('order',v)}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/></svg><span className="cf-l">{l}</span><span className="cf-n">{conversations.filter(c => c.order_estado === v).length}</span></button>
              ))}
              <div className="cf-sec">Etiquetas</div>
              {tenantLabels.map(l => (
                <div key={l.key} className="cf-lrow">
                  <button title={l.name} className={`cf-item${labelFilter===l.key?' active':''}`} onClick={()=>pickFilter('label',l.key)}><span className="cf-dot" style={{background:l.color}}/><span className="cf-l">{l.name}</span></button>
                  <span className="cf-lact">
                    <button title="Editar" onClick={()=>setLabelForm({ id: l.id, name: l.name, color: l.color })}><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z"/></svg></button>
                    <button title="Eliminar" onClick={()=>deleteLabel(l)}><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/></svg></button>
                  </span>
                </div>
              ))}
              {labelForm ? (
                <div className="cf-lform" onClick={e=>e.stopPropagation()}>
                  <input autoFocus value={labelForm.name} maxLength={40} placeholder="Nombre de la etiqueta"
                    onChange={e=>setLabelForm({...labelForm, name:e.target.value, error:null})}
                    onKeyDown={e=>{ if(e.key==='Enter') saveLabel(); if(e.key==='Escape') setLabelForm(null); }}/>
                  <div className="cf-colors">
                    {LABEL_PALETTE.map(c => <button key={c} className={labelForm.color===c?'sel':''} style={{background:c}} onClick={()=>setLabelForm({...labelForm, color:c})} aria-label={c}/>)}
                  </div>
                  {labelForm.error && <div className="cf-lerr">{labelForm.error}</div>}
                  <div className="cf-lbtns">
                    <button onClick={()=>setLabelForm(null)}>Cancelar</button>
                    <button className="pri" onClick={saveLabel}>{labelForm.id ? 'Guardar' : 'Crear'}</button>
                  </div>
                </div>
              ) : (
                <button title="Nueva etiqueta" className="cf-item cf-add" onClick={()=>setLabelForm({ name:'', color:'#6b7280' })}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg><span className="cf-l">Nueva etiqueta</span>
                </button>
              )}
              <div className="cf-sec">Origen</div>
                {[['organic','Orgánico'],['meta_ad','Meta Ads'],['social_ad','Social Ads'],['nexus_landing','Nexus Landing'],['nexus_recovery','Nexus Recovery'],['nexus_catalog','Catálogo Nexus']]
                  .filter(([v]) => conversations.some(c => (c.source || 'organic') === v))
                  .map(([v, l]) => (
                    <button key={v} title={l} className={`cf-item${sourceFilter===v?' active':''}`} onClick={()=>pickFilter('source',v)}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><path d="M3 12h18"/><path d="M12 3a14 14 0 0 1 0 18a14 14 0 0 1 0-18z"/></svg><span className="cf-l">{l}</span></button>
                  ))}
            </div>
            </div>
            <div className="conv-list-panel">

              {/* ── Header ── */}
              <div className="clp-header">
                <div className="clp-top-row">
                  <div className="clp-title">Chats</div>
                  <div className="clp-top-actions">
                    <button className="clp-new-btn" onClick={openNewConvModal}>
                      <svg width="12" height="12" viewBox="0 0 14 14" fill="none"><path d="M7 1v12M1 7h12" stroke="#0a0a0a" strokeWidth="2" strokeLinecap="round"/></svg>
                      Nuevo Chat
                    </button>
                  </div>
                </div>

                {/* Search */}
                <div className="clp-search">
                  <svg width="13" height="13" viewBox="0 0 16 16" fill="none"><circle cx="7" cy="7" r="5" stroke="#c0c0c0" strokeWidth="1.5"/><path d="M11 11l3 3" stroke="#c0c0c0" strokeWidth="1.5" strokeLinecap="round"/></svg>
                  <input placeholder="Buscar contacto..." value={searchQuery} onChange={e=>setSearchQuery(e.target.value)}/>
                </div>
                {activeFilterName && !searchQuery && (
                  <div className="clp-active-filter">
                    <span>Mostrando: <b>{activeFilterName}</b></span>
                    <button onClick={()=>pickFilter('folder','todas')} aria-label="Quitar filtro">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </button>
                  </div>
                )}
              </div>

              {/* ── List ── */}
              <div className="clp-list">
                {loading ? (
                  <div style={{display:'flex',justifyContent:'center',padding:40}}><div className="spinner"/></div>
                ) : searchQuery.trim().length >= 2 ? ((() => {
                  const local = conversations.filter(c => !['fb', 'ig', 'messenger', 'instagram'].includes(c.channel) && searchLocal(c));
                  const chats = [...local, ...searchConvs.filter(r => !local.some(c => c.id === r.id))];
                  return (
                  <div className="gs-wrap">
                    <div className="clp-section-label gs-head">Chats <span className="gs-count">{chats.length}</span></div>
                    {chats.length === 0 && !globalSearchLoading && <div className="gs-none">Ningún chat con ese nombre, teléfono o pedido</div>}
                    {chats.map((c, i) => (
                      <div key={`c_${c.id}`} className="gs-item gs-chat" style={{animationDelay:`${Math.min(i,10)*30}ms`}} onClick={()=>openSearchResult(c)}>
                        <div className="gs-av">{(c.contact_name || c.contact_phone || '?').trim().charAt(0).toUpperCase()}</div>
                        <div style={{minWidth:0,flex:1}}>
                          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:8}}>
                            <div className="gs-name">{c.contact_name || fmtPhone(c.contact_phone)}</div>
                            <div className="gs-time">{c.last_message_at ? fmtTime(c.last_message_at) : ''}</div>
                          </div>
                          <div className="gs-snippet">{c.order_number ? `Pedido #${c.order_number} · ` : ''}{fmtPhone(c.contact_phone)}</div>
                        </div>
                      </div>
                    ))}
                    <div className="clp-section-label gs-head">
                      Mensajes {globalSearchLoading ? <div className="spinner" style={{width:10,height:10,borderWidth:1.5}}/> : <span className="gs-count">{globalResults.length}{globalResults.length >= 200 ? '+' : ''}</span>}
                    </div>
                    {globalResults.map(r => {
                      const q = searchQuery.toLowerCase();
                      const content = r.content || '';
                      const idx = content.toLowerCase().indexOf(q);
                      const snippet = idx >= 0
                        ? content.slice(Math.max(0, idx - 20), idx + 60)
                        : content.slice(0, 80);
                      return (
                        <div key={`${r.conv_id}_${r.message_id}`} className="gs-item" onClick={()=>openSearchResult({ id: r.conv_id, contact_name: r.conv_name, contact_phone: r.conv_phone, last_message: r.content, last_message_at: r.sent_at })}>
                          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                            <div className="gs-name">{r.conv_name || r.conv_phone}</div>
                            <div className="gs-time">{fmtTime(r.sent_at)}</div>
                          </div>
                          <div className="gs-snippet">
                            {idx >= 0 ? (
                              <>
                                {idx > 20 ? '…' : ''}{content.slice(Math.max(0,idx-20), idx)}
                                <mark style={{background:'#fef08a',borderRadius:2,padding:'0 1px',color:'#0a0a0a'}}>{content.slice(idx, idx+q.length)}</mark>
                                {content.slice(idx+q.length, idx+60)}{content.length > idx+60 ? '…' : ''}
                              </>
                            ) : snippet}
                          </div>
                        </div>
                      );
                    })}
                    {!globalSearchLoading && globalResults.length === 0 && <div className="gs-none">Ningún mensaje contiene "{searchQuery}"</div>}
                  </div>
                  );
                })()
                ) : (() => {
                  // Filter by tab — si hay búsqueda activa, mostrar todas sin filtrar por tab
                  const byTab = conversations.filter(c => {
                    if (searchQuery) return true;
                    const st = convStatuses[c.id] || c.status || 'open';
                    if (listTab === 'resueltos') return st === 'resolved';
                    if (listTab === 'abiertos') return st !== 'resolved';
                    if (listTab === 'asignados') return c.assigned_to === user?.name && st !== 'resolved';
                    return true; // 'todos'
                  });
                  // Filter by carpeta (columna 1)
                  const byFolder = byTab.filter(c => folderMatch(c, folder));
                  // Filter by search
                  const bySearch = byFolder.filter(c => {
                    if (!searchQuery) return true;
                    const q = searchQuery.toLowerCase().replace(/[^0-9a-záéíóúñ\s#]/gi, '');
                    const phone = (c.contact_phone||'').replace(/\D/g,'');
                    const qDigits = searchQuery.replace(/\D/g,'');
                    return (c.contact_name||'').toLowerCase().includes(q)
                      || (phone && qDigits.length >= 6 && phone.includes(qDigits))
                      || (c.order_number && String(c.order_number).includes(searchQuery.replace('#','')));
                  });
                  // Filter by label
                  const byLabel = labelFilter && !searchQuery ? bySearch.filter(c => (c.label || convLabels[c.id]) === labelFilter) : bySearch;
                  // Filter by order_estado
                  const byOrder = orderFilter && !searchQuery ? byLabel.filter(c => c.order_estado === orderFilter) : byLabel;
                  // Filter by source
                  const bySource = sourceFilter && !searchQuery ? byOrder.filter(c => (c.source || 'organic') === sourceFilter) : byOrder;
                  // Split favorites / rest
                  const favs = bySource.filter(c => favConvs.has(c.id));
                  const rest = bySource.filter(c => !favConvs.has(c.id));

                  const renderItem = (c, idx) => {
                    const lbl = c.label || convLabels[c.id];
                    const hasUnread = (c.unread_count||0) > 0 && activeConv !== c.id;
                    return (
                      <div key={c.id}
                        className={`conv-item${activeConv===c.id?' active':''}`}
                        onClick={()=>{setActiveConv(c.id);markRead(c.id);setContextMenu(null);}}
                        onContextMenu={e=>{e.preventDefault();setContextMenu({x:e.clientX/escZ(),y:e.clientY/escZ(),convId:c.id});}}
                      >
                        <div className="ci-av-wrap">
                          <div className="ci-av" style={{background:AVATAR_COLORS[(c.id||0)%AVATAR_COLORS.length]}}>
                            {initials(shopifyNames[c.contact_phone?.replace(/\D/g,'')] || c.contact_name || c.contact_phone)}
                          </div>
                          <div className="ci-online"/>
                        </div>
                        <div className="ci-body">
                          <div className="ci-top">
                            <span className="ci-name" style={hasUnread?{fontWeight:700}:{}}>{shopifyNames[c.contact_phone?.replace(/\D/g,'')] || c.contact_name || fmtPhone(c.contact_phone)}</span>
                            <span className="ci-time">{fmtTime(c.last_message_at)}</span>
                          </div>
                          {c.source && c.source !== 'organic' && (() => {
                            const srcCfg = {meta_ad:['Meta Ad','#1877f2'],nexus_landing:['Nexus','#8b5cf6'],nexus_recovery:['Recovery','#f97316'],social_ad:['Social Ad','#ec4899'],nexus_catalog:['Catálogo','#14b8a6']};
                            const [sl,sc] = srcCfg[c.source]||[c.source,'#888'];
                            return <span style={{display:'inline-block',fontSize:9,fontWeight:600,color:sc,background:sc+'18',border:`1px solid ${sc}40`,borderRadius:4,padding:'0 5px',lineHeight:'16px',marginTop:1}}>{sl}</span>;
                          })()}
                          <div className="ci-preview" style={hasUnread?{color:'#555',fontWeight:500}:{}}>{c.last_message ? withAppleEmoji(c.last_message) : 'Sin mensajes'}</div>
                        </div>
                        {hasUnread && <div className="ci-badge">{c.unread_count}</div>}
                        {lbl && <div className="ci-label-dot" title={labelMap[lbl]?.name || lbl} style={{background:labelMap[lbl]?.color || '#9ca3af'}}/>}
                        {favConvs.has(c.id) && !hasUnread && <svg width="10" height="10" viewBox="0 0 24 24" fill="#f59e0b" stroke="none" style={{flexShrink:0,marginLeft:2}}><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>}
                      </div>
                    );
                  };

                  if (bySource.length === 0) return (
                    <div className="clp-empty">
                      <div className="clp-empty-ic"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg></div>
                      <div className="clp-empty-t">{searchQuery ? `Sin resultados para "${searchQuery}"` : 'No hay chats en este filtro'}</div>
                      {activeFilterName && !searchQuery && <button className="clp-empty-btn" onClick={()=>pickFilter('folder','todas')}>Ver todos los chats</button>}
                    </div>
                  );

                  return (
                    <>
                      {favs.length > 0 && (
                        <>
                          <div className="clp-section-label">Fijados</div>
                          {favs.map((c,i)=>renderItem(c,i))}
                        </>
                      )}
                      {rest.length > 0 && (
                        <>
                          <div className="clp-section-label">Recientes</div>
                          {rest.map((c,i)=>renderItem(c,favs.length+i))}
                        </>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>

            <div className="chat-panel">
              {!conv ? (
                <div className="empty-state">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#ddd" strokeWidth="1.5" strokeLinecap="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                  <div style={{fontSize:14,fontWeight:500}}>Selecciona una conversación</div>
                  <div style={{fontSize:12}}>Los mensajes aparecen aquí en tiempo real</div>
                </div>
              ) : (
                <>
                  <div className="chat-header">
                    <div className="ch-av" style={{background:AVATAR_COLORS[(activeConv||0)%AVATAR_COLORS.length]}}>
                      {initials(shopifyContact?.nombre || conv.contact_name || conv.contact_phone)}
                      <div className="ch-channel"><ChannelIcon ch={conv.channel||'wa'}/></div>
                    </div>
                    <div className="ch-info">
                      <div className="ch-name-row">
                        <div className="ch-name">{shopifyContact?.nombre || conv.contact_name || fmtPhone(conv.contact_phone)}</div>
                        {notes.length > 0 && <span className="ch-note-badge">{notes.length} {notes.length===1?'nota':'notas'}</span>}
                        {conv.assigned_to && <span className="ch-note-badge" title="Asignado a">{conv.assigned_to}</span>}
                      </div>
                      <div className="ch-sub">{fmtPhone(conv.contact_phone)}</div>
                    </div>
                    <div className="ch-actions">
                      {/* Estado */}
                      <div style={{position:'relative'}}>
                        <button className="ch-status" onClick={()=>{setShowStatusMenu(!showStatusMenu);setShowOptionsMenu(false);setShowCallMenu(false);}}>
                          <span className="ch-sdot" style={{background:statusObj.color}}/>{statusObj.label}
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><polyline points="6 9 12 15 18 9"/></svg>
                        </button>
                        {showStatusMenu && (
                          <div className="ch-menu">
                            {STATUS_OPTIONS.map(s=>(
                              <div key={s.id} className={`ch-mi${currentStatus===s.id?' on':''}`} onClick={()=>{setConvStatuses(p=>({...p,[activeConv]:s.id}));setConversations(prev=>prev.map(c=>c.id===activeConv?{...c,status:s.id}:c));patchConv(activeConv,{status:s.id});setShowStatusMenu(false);}}>
                                <span className="ch-sdot" style={{background:s.color}}/>{s.label}
                                {currentStatus===s.id && <svg style={{marginLeft:'auto'}} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="2.4" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      {/* Bot */}
                      <button className={`ch-bot${botStatus[activeConv] !== false ? ' on' : ''}`} onClick={() => toggleBot(activeConv)}
                        title={botStatus[activeConv] !== false ? 'El bot responde este chat. Clic para pausarlo' : 'Bot pausado. Clic para activarlo'}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="8" width="16" height="12" rx="3"/><line x1="12" y1="4" x2="12" y2="8"/><circle cx="9" cy="14" r="1" fill="currentColor"/><circle cx="15" cy="14" r="1" fill="currentColor"/></svg>
                        {botStatus[activeConv] !== false ? 'Bot activo' : 'Bot pausado'}
                      </button>
                      <span className="ch-vsep"/>
                      <button className="ch-btn-icon" title="Asignar a un asesor" onClick={()=>{ setShowAssign(true); loadTeam(); }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>
                      </button>
                      <button className="ch-btn-icon" title="Buscar en el chat (Ctrl+F)" onClick={()=>{ setShowChatSearch(v=>!v); setChatSearchTerm(''); setTimeout(()=>chatSearchRef.current?.focus(),50); }} style={{color: showChatSearch ? '#111' : undefined}}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.35-4.35"/></svg>
                      </button>
                      <button className="ch-btn-icon" title="Abrir en WhatsApp" onClick={()=>window.open(`https://wa.me/${conv.contact_phone?.replace(/\D/g,'')}`)}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.4 8.4 0 0 1-12.6 7.3L3 20l1.3-5A8.4 8.4 0 1 1 21 11.5z"/></svg>
                      </button>
                      <button className="ch-btn-icon info-toggle-btn" title="Panel del cliente" onClick={()=>setShowInfoPanel(p=>!p)} style={{color: showInfoPanel ? '#111' : undefined}}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="3"/><path d="M15 3v18"/></svg>
                      </button>
                    </div>
                  </div>

                  {/* ── In-chat search bar ── */}
                  {showChatSearch && (
                    <div style={{background:'#fff',borderBottom:'1px solid #eeeeec',padding:'10px 20px',display:'flex',alignItems:'center',gap:10,flexShrink:0}}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#aaa" strokeWidth="1.8" strokeLinecap="round"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.35-4.35"/></svg>
                      <input
                        ref={chatSearchRef}
                        value={chatSearchTerm}
                        onChange={e=>setChatSearchTerm(e.target.value)}
                        onKeyDown={e=>{ if(e.key==='Enter'){ e.shiftKey ? goSearchPrev() : goSearchNext(); } if(e.key==='Escape'){setShowChatSearch(false);setChatSearchTerm('');} }}
                        placeholder="Buscar en esta conversación..."
                        style={{flex:1,border:'none',outline:'none',fontSize:13,fontFamily:'inherit',color:'#0a0a0a',background:'transparent'}}
                      />
                      {chatSearchTerm.length >= 2 && (
                        <span style={{fontSize:11,color:'#aaa',whiteSpace:'nowrap',flexShrink:0}}>
                          {searchMatches.length === 0 ? 'Sin resultados' : `${chatSearchIdx+1} / ${searchMatches.length}`}
                        </span>
                      )}
                      {searchMatches.length > 1 && (
                        <>
                          <button onClick={goSearchPrev} style={{width:26,height:26,borderRadius:6,border:'1px solid #e4e4e2',background:'#fff',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="2.5" strokeLinecap="round"><polyline points="18 15 12 9 6 15"/></svg>
                          </button>
                          <button onClick={goSearchNext} style={{width:26,height:26,borderRadius:6,border:'1px solid #e4e4e2',background:'#fff',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="2.5" strokeLinecap="round"><polyline points="6 9 12 15 18 9"/></svg>
                          </button>
                        </>
                      )}
                      <button onClick={()=>{setShowChatSearch(false);setChatSearchTerm('');}} style={{width:26,height:26,borderRadius:6,border:'none',background:'transparent',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',color:'#aaa',fontSize:16,flexShrink:0}}>×</button>
                    </div>
                  )}

                  <div className="messages-area" ref={messagesAreaRef}>
                    {/* Botón cargar anteriores */}
                    {hasMoreMsgs && !loadingMsgs && (
                      <div style={{display:'flex',justifyContent:'center',marginBottom:8,flexShrink:0}}>
                        <button onClick={loadMoreMessages} disabled={loadingMore} style={{display:'flex',alignItems:'center',gap:6,padding:'6px 16px',borderRadius:999,border:'1px solid #e4e4e2',background:'#fff',fontSize:12,fontWeight:500,color:'#555',cursor:loadingMore?'not-allowed':'pointer',fontFamily:'inherit',transition:'background 0.15s'}}>
                          {loadingMore ? <><div className="spinner" style={{width:11,height:11}}/> Cargando...</> : <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><polyline points="18 15 12 9 6 15"/></svg> Cargar mensajes anteriores</>}
                        </button>
                      </div>
                    )}
                    {loadingMsgs ? (
                      <div style={{display:'flex',justifyContent:'center',padding:32}}><div className="spinner"/></div>
                    ) : messages.length === 0 ? (
                      <div style={{textAlign:'center',color:'#bbb',fontSize:13,marginTop:32}}>No hay mensajes aún</div>
                    ) : (() => {
                      let lastDateStr = null;
                      const avatarBg = AVATAR_COLORS[(activeConv || 0) % AVATAR_COLORS.length];
                      return messages.map((m) => {
                        const isOut = m.role === 'outbound' || m.role === 'assistant' || m.direction === 'outbound';
                        const source = m.role === 'assistant' ? 'bot' : (m.source || 'agent');
                        const status = m.status || 'sent';
                        // Un texto que es solo un enlace (ej. el catálogo) es texto; solo se toma como media si apunta a un archivo
                        const contentIsMediaUrl = m.content && m.content.startsWith('https://') && !/\s/.test(m.content.trim())
                          && (m.media_type || m.content.includes('/whatsapp/media/') || /\.(jpe?g|png|webp|gif|mp4|3gp|mov|ogg|opus|mp3|m4a|aac|amr|pdf)(\?|#|$)/i.test(m.content.trim()));
                        const rawMediaUrl = m.media_url || (contentIsMediaUrl ? m.content.trim() : null);
                        const isLocalMedia = rawMediaUrl && rawMediaUrl.includes('/whatsapp/media/');
                        const isDirectImg = rawMediaUrl && (rawMediaUrl.includes('cdn.shopify.com') || rawMediaUrl.includes('nexusdrops.online'));
                        const proxyUrl = rawMediaUrl ? (isLocalMedia || isDirectImg ? rawMediaUrl : `${API}/whatsapp/media-proxy?url=${encodeURIComponent(rawMediaUrl)}`) : null;
                        const rawMt = m.media_type || '';
                        const mediaType = rawMt.startsWith('audio/') ? 'audio'
                          : rawMt.startsWith('video/') ? 'video'
                          : rawMt.startsWith('image/') ? 'image'
                          : rawMt.startsWith('application/') ? 'document'
                          : rawMt || null;
                        const hasError = mediaErrors[m.id];
                        const isConfirmTemplate = isOut && (m.content || '').includes('¿Los datos están correctos?');
                        const tplHit = !isConfirmTemplate ? tplMatches[m.id] : null;

                        // Date separator
                        const msgDate = m.sent_at || m.created_at;
                        let dateLabel = null;
                        if (msgDate) {
                          try {
                            const _ms = String(msgDate).replace(' ','T');
                            const d = new Date(_ms.includes('+') || _ms.endsWith('Z') ? _ms : _ms + '-05:00');
                            const today = new Date(); today.setHours(0,0,0,0);
                            const yesterday = new Date(today); yesterday.setDate(today.getDate()-1);
                            d.setHours(0,0,0,0);
                            const ds = d.toDateString();
                            if (ds !== lastDateStr) {
                              lastDateStr = ds;
                              if (d.getTime() === today.getTime()) dateLabel = 'HOY';
                              else if (d.getTime() === yesterday.getTime()) dateLabel = 'AYER';
                              else dateLabel = d.toLocaleDateString('es-CO',{day:'numeric',month:'long'}).toUpperCase();
                            }
                          } catch(e) {}
                        }

                        const senderName = isOut ? (user?.name?.split(' ')[0] || 'Tú') : (shopifyContact?.nombre || conv.contact_name || fmtPhone(conv.contact_phone));

                        return (
                          <React.Fragment key={m.id}>
                            {dateLabel && (
                              <div className="date-sep">
                                <div className="date-sep-line"/>
                                <span className="date-sep-label">{dateLabel}</span>
                                <div className="date-sep-line"/>
                              </div>
                            )}
                            <div ref={el=>msgRefs.current[m.id]=el} className={`msg-row ${isOut?'out':'in'}`}>
                              {/* Meta row above bubble */}
                              <div className="msg-meta" style={{flexDirection: isOut ? 'row-reverse' : 'row'}}>
                                <div className="msg-av-sm" style={{background: isOut ? '#075e54' : avatarBg, color: isOut ? '#fff' : '#555'}}>
                                  {isOut && source === 'bot'
                                    ? <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="8" width="16" height="12" rx="3"/><line x1="12" y1="4" x2="12" y2="8"/><circle cx="9" cy="14" r="1" fill="currentColor"/><circle cx="15" cy="14" r="1" fill="currentColor"/></svg>
                                    : isOut ? (user?.name?.[0]||'T').toUpperCase() : initials(shopifyContact?.nombre || conv.contact_name || conv.contact_phone)}
                                </div>
                                <span className="msg-sender">{senderName}</span>
                                <span className="msg-ts">{fmtTime(m.sent_at || m.created_at)}</span>
                                {isOut && <SourceBadge source={source} isOut={isOut}/>}
                              </div>
                              {/* Bubble */}
                              <div className={`msg-bubble ${isOut?'out':'in'}`}>
                                {m.quoted_content && (
                                  <div className="quoted-msg">
                                    <div className="quoted-bar"/>
                                    <div className="quoted-body">
                                      <div className="quoted-sender">{m.quoted_sender || 'Mensaje'}</div>
                                      <div className="quoted-text">{m.quoted_content.slice(0, 100)}</div>
                                    </div>
                                  </div>
                                )}
                                {isConfirmTemplate ? (
                                  <div style={{width:300}}>
                                    {proxyUrl && !hasError && (
                                      <img src={proxyUrl} alt="producto" style={{width:'100%',height:'auto',borderRadius:'6px 6px 0 0',marginBottom:10,cursor:'pointer',display:'block'}} onClick={()=>setImgModal(proxyUrl)} onError={()=>setMediaErrors(p=>({...p,[m.id]:true}))} />
                                    )}
                                    <div style={{whiteSpace:'pre-wrap',lineHeight:1.6,fontSize:13}}>
                                      {withAppleEmojiParts(chatSearchTerm.length >= 2 ? highlightText(m.content, chatSearchTerm) : m.content)}
                                    </div>
                                    <div style={{display:'flex',gap:8,marginTop:10,paddingTop:8,borderTop:'1px solid rgba(0,0,0,0.08)'}}>
                                      <div style={{flex:1,textAlign:'center',color:'#2563eb',fontSize:12,fontWeight:700,padding:'6px 4px',borderRadius:6,background:'rgba(37,99,235,0.08)'}}>Corregir datos</div>
                                      <div style={{flex:1,textAlign:'center',color:'#16a34a',fontSize:12,fontWeight:700,padding:'6px 4px',borderRadius:6,background:'rgba(22,163,74,0.08)'}}>Datos correctos</div>
                                    </div>
                                  </div>
                                ) : tplHit ? (
                                  <TemplateBubble
                                    tpl={tplHit.tpl}
                                    body={tplHit.body}
                                    mediaUrl={proxyUrl && !hasError && (mediaType === 'image' || mediaType === 'video') ? proxyUrl : null}
                                    onOpenMedia={(u)=>setImgModal(u)}
                                    renderText={(t)=> chatSearchTerm.length >= 2 ? withAppleEmojiParts(highlightText(t, chatSearchTerm)) : waFormat(t)}
                                  />
                                ) : proxyUrl && !hasError ? (
                                  mediaType === 'audio' ?
                                    <AudioPlayer src={proxyUrl} isOut={isOut} time={fmtTime(m.sent_at || m.created_at)} onError={()=>setMediaErrors(p=>({...p,[m.id]:true}))} /> :
                                  mediaType === 'video' ?
                                    <video controls src={proxyUrl} style={{maxWidth:'100%',borderRadius:8,maxHeight:200,cursor:'pointer'}} onClick={()=>setImgModal({url:proxyUrl,type:'video'})} onError={()=>setMediaErrors(p=>({...p,[m.id]:true}))} /> :
                                  mediaType === 'document' ?
                                    <a href={proxyUrl} target="_blank" rel="noreferrer" style={{color:'#3b82f6',fontSize:13,display:'flex',alignItems:'center',gap:6}}>📄 Ver documento</a> :
                                  mediaType === 'sticker' ?
                                    <div style={{position:'relative',display:'inline-block'}}>
                                      <img src={proxyUrl} alt="sticker" style={{maxWidth:120,borderRadius:8,cursor:'pointer'}} onClick={()=>setImgModal(proxyUrl)} onError={()=>setMediaErrors(p=>({...p,[m.id]:true}))} />
                                      <span style={{position:'absolute',top:4,right:4,background:'rgba(0,0,0,0.5)',color:'#fff',fontSize:9,fontWeight:700,padding:'1px 5px',borderRadius:6}}>Sticker</span>
                                    </div> :
                                    <img src={proxyUrl} alt="imagen" style={{maxWidth:'100%',borderRadius:8,maxHeight:300,cursor:'pointer'}} onClick={()=>setImgModal(proxyUrl)} onError={()=>setMediaErrors(p=>({...p,[m.id]:true}))} />
                                ) : hasError ? <MediaExpired type={mediaType} /> :
                                  m.content === '[Sticker]' ? '🎭 Sticker' :
                                  m.content === '' || !m.content ? '📎 Archivo' :
                                  withAppleEmojiParts(chatSearchTerm.length >= 2 ? highlightText(m.content, chatSearchTerm) : m.content)}
                              </div>
                              {/* Hover actions */}
                              {m.content && m.content !== '[Sticker]' && (
                                <div className="msg-actions">
                                  <button className="msg-action-btn" onClick={()=>setReplyTo({id:m.id,content:m.content,sender:senderName,wamid:m.twilio_sid||null})}>
                                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 17 4 12 9 7"/><path d="M20 18v-2a4 4 0 0 0-4-4H4"/></svg>
                                    Responder
                                  </button>
                                </div>
                              )}
                              {/* Status under bubble for outbound */}
                              {isOut && (
                                <div className="msg-status-row">
                                  <StatusIcon status={status} isOut={isOut}/>
                                  {status === 'failed' && <span style={{fontSize:10,color:'#ef4444'}}>No entregado</span>}
                                </div>
                              )}
                            </div>
                          </React.Fragment>
                        );
                      });
                    })()}
                  </div>

                  {/* Pending media preview */}
                  {pendingMedia && pendingMedia.length > 0 && (() => {
                    const list = pendingMedia;
                    const fmtSize = (n) => { const kb = n / 1024; return kb < 1024 ? `${Math.max(1, Math.round(kb))} KB` : `${(kb / 1024).toFixed(1)} MB`; };
                    const TYPE = { image: 'Imagen', video: 'Video', audio: 'Audio', document: 'Documento' };
                    const extOf = (f) => (f.name.split('.').pop() || '').toUpperCase().slice(0, 4) || 'DOC';
                    const thumb = (pm) => pm.type === 'image' && pm.previewUrl ? <img src={pm.previewUrl} alt=""/>
                      : pm.type === 'video' && pm.previewUrl ? <video src={pm.previewUrl} muted/>
                      : pm.type === 'audio' ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>
                      : <span className="pm-ext">{extOf(pm.file)}</span>;
                    const clearAll = () => { list.forEach(x => x.previewUrl && URL.revokeObjectURL(x.previewUrl)); setPendingMedia(null); };
                    const removeOne = (k) => { const x = list[k]; if (x.previewUrl) URL.revokeObjectURL(x.previewUrl); const rest = list.filter((_, i) => i !== k); setPendingMedia(rest.length ? rest : null); };
                    const sendAll = async () => {
                      const items = list; setPendingMedia(null);
                      setMediaProgress({ done: 0, total: items.length });
                      for (let k = 0; k < items.length; k++) {
                        await sendMedia(items[k].file);
                        if (items[k].previewUrl) URL.revokeObjectURL(items[k].previewUrl);
                        setMediaProgress({ done: k + 1, total: items.length });
                      }
                      setMediaProgress(null);
                    };
                    const totalSize = list.reduce((t, x) => t + x.file.size, 0);
                    return (
                    <div className="pm-bar">
                      <div className="pm-card">
                        {list.length === 1 ? (
                          <>
                            <div className="pm-thumb">{thumb(list[0])}</div>
                            <div className="pm-info">
                              <div className="pm-name">{list[0].file.name}</div>
                              <div className="pm-meta">{TYPE[list[0].type]} · {fmtSize(list[0].file.size)}</div>
                            </div>
                          </>
                        ) : (
                          <div className="pm-info">
                            <div className="pm-grid">
                              {list.map((pm, k) => (
                                <div key={k} className="pm-thumb sm" title={pm.file.name}>
                                  {thumb(pm)}
                                  <button className="pm-x" onClick={() => removeOne(k)} aria-label="Quitar">
                                    <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                                  </button>
                                </div>
                              ))}
                              {list.length < 10 && (
                                <button className="pm-thumb sm pm-add" onClick={() => fileInputRef.current?.click()} title="Agregar más">
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                                </button>
                              )}
                            </div>
                            <div className="pm-meta" style={{marginTop:6}}>{list.length} archivos · {fmtSize(totalSize)}</div>
                          </div>
                        )}
                        <button className="pm-btn ghost" onClick={clearAll}>{list.length > 1 ? 'Quitar todos' : 'Quitar'}</button>
                        <button className="pm-btn primary" disabled={sending || !!mediaProgress} onClick={sendAll}>
                          {list.length > 1 ? `Enviar ${list.length}` : 'Enviar archivo'}
                        </button>
                      </div>
                    </div>
                    );
                  })()}
                  {mediaProgress && (
                    <div className="pm-bar"><div className="pm-progress">
                      <div className="spinner" style={{width:12,height:12}}/>Enviando {Math.min(mediaProgress.done + 1, mediaProgress.total)} de {mediaProgress.total}…
                      <span className="pm-track"><span style={{width:`${(mediaProgress.done / mediaProgress.total) * 100}%`}}/></span>
                    </div></div>
                  )}
                  {sendError && (
                    <div style={{padding:'5px 18px',background:'#fef2f2',display:'flex',alignItems:'center',gap:8}}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="9"/><line x1="12" y1="8" x2="12" y2="12"/><circle cx="12" cy="16" r="0.5" fill="#ef4444"/></svg>
                      <span style={{fontSize:11,color:'#ef4444',flex:1}}>{sendError}</span>
                      <button onClick={()=>setSendError('')} style={{fontSize:11,color:'#aaa',background:'none',border:'none',cursor:'pointer',padding:0}}>✕</button>
                    </div>
                  )}

                  {/* Quote preview when replying */}
                  {replyTo && (
                    <div className="reply-preview">
                      <div className="reply-bar"/>
                      <div className="reply-preview-body">
                        <div className="reply-preview-sender">{replyTo.sender}</div>
                        <div className="reply-preview-text">{replyTo.content.slice(0, 90)}</div>
                      </div>
                      <button className="reply-close" onClick={()=>setReplyTo(null)}>×</button>
                    </div>
                  )}

                  {/* ── Banner ventana 24h WhatsApp ── */}
                  {isWa24hExpired && (
                    <div style={{padding:'9px 16px',background:'#fffbeb',borderTop:'1px solid #fde68a',display:'flex',alignItems:'flex-start',gap:8,fontSize:12,color:'#92400e',lineHeight:1.55,flexShrink:0}}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2" strokeLinecap="round" style={{flexShrink:0,marginTop:2}}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                      <span>
                        Has alcanzado tu ventana de respuesta abierta de 24 horas de Atención al Cliente en WhatsApp. Aún puedes mantenerte en contacto enviando un mensaje de plantilla de{' '}
                        <span
                          style={{fontWeight:700,cursor:'pointer',textDecoration:'underline',color:'#b45309',whiteSpace:'nowrap'}}
                          onClick={()=>{ setShowTemplates(true); setTemplateTab('meta'); loadMetaTemplates(); loadIntTemplates(); }}
                        >
                          WhatsApp aprobado
                        </span>.
                      </span>
                    </div>
                  )}

                  {/* ── New input area ── */}
                  <div className="chat-input-wrapper">
                    {recording ? (
                      <div className="rec-bar">
                        <span className="rec-dot"/>
                        <span className="rec-time">{fmtRecording(recordingTime)}</span>
                        <span className="rec-wave" aria-hidden="true">{Array.from({length: 28}).map((_, k) => <i key={k}/>)}</span>
                        <span className="rec-max">máx. 5:00</span>
                        <button className="pm-btn ghost" onClick={()=>{recordingCancelledRef.current=true;mediaRecorderRef.current?.stop();setRecording(false);clearInterval(recordingTimerRef.current);setRecordingTime(0);}}>Cancelar</button>
                        <button className="pm-btn primary" onClick={stopRecording}>Enviar audio</button>
                      </div>
                    ) : (
                      <ChatInput ref={chatInputRef} onSend={sendMessage} sending={sending} />
                    )}

                    {/* Emoji picker floating panel */}
                    {showEmojiPicker && (
                      <div style={{position:'absolute',bottom:'100%',left:16,zIndex:500}} onClick={e=>e.stopPropagation()}>
                        <EmojiPicker data={emojiData} set="apple" theme="light" locale="es" previewPosition="none" skinTonePosition="search" navPosition="top" perLine={8} emojiSize={22} emojiButtonSize={34} maxFrequentRows={2}
                          onEmojiSelect={e=>{chatInputRef.current?.append(e.native);setShowEmojiPicker(false);}}
                        />
                      </div>
                    )}

                    {/* Toolbar below input */}
                    <div className="chat-toolbar">
                      <button className="toolbar-btn" onClick={()=>{setShowTemplates(true);loadMetaTemplates();loadIntTemplates();}}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="9" y1="21" x2="9" y2="9"/></svg>
                        Plantillas
                      </button>
                      <button className="toolbar-btn" onClick={e=>{e.stopPropagation();setShowEmojiPicker(v=>!v);}}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/></svg>
                        Emoji
                      </button>
                      <button className="toolbar-btn" onClick={()=>fileInputRef.current?.click()}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
                        Archivo
                      </button>
                      <button className="toolbar-btn" onClick={recording ? stopRecording : startRecording} style={recording ? {color:'#ef4444'} : {}}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={recording?'#ef4444':'currentColor'} strokeWidth="1.8" strokeLinecap="round"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>
                        Audio
                      </button>
                      <div className="toolbar-sep"/>
                      
                      {storeDomain && <button className="toolbar-btn shopify-btn" disabled={shopifyOrders.length===0} title={shopifyOrders.length===0 ? 'Este cliente no tiene pedidos en Shopify' : 'Pedidos de Shopify'} onClick={()=>{ if(shopifyOrders.length===1){setSelectedOrder(shopifyOrders[0]);setShowOrderActions(true);}else{setShowOrderSelect(true);} }}>
                        <img src="/shopify-logo.svg" alt="" style={{width:17,height:17}}/>
                        Shopify
                      </button>}
                      {activeOrder?.shopify_id && (
                        <button className="toolbar-btn" title="Pedido en Nexus" onClick={()=>openNexusModal(activeOrder.shopify_id)}>
                          <img src="/nexus-logo.png" alt="Nexus" style={{width:14,height:14,objectFit:'contain'}} />
                          Nexus
                        </button>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className={`info-panel-overlay${showInfoPanel?' open':''}`} onClick={()=>setShowInfoPanel(false)}/>
            <div className={`info-panel${showInfoPanel?' open':''}`}>
              <div className="ip-body">
                {(
                  <>
                    <div>
                      <div className="cl-header">
                        <div className="cl-avatar" style={{background: AVATAR_COLORS[(conv?.id || 0) % AVATAR_COLORS.length]}}>
                          {initials(shopifyContact?.nombre || conv?.contact_name || conv?.contact_phone)}
                        </div>
                        <div className="cl-name-row">
                          <div className="cl-name">{shopifyContact?.nombre || conv?.contact_name || '—'}</div>
                          {shopifyContact?.segmento && <span className={`seg-badge seg-${shopifyContact.segmento}`} style={{marginTop:2}}>{shopifyContact.segmento}</span>}
                        </div>
                      </div>
                      <div className="cl-actions">
                        <button onClick={()=>{ notesSecRef.current?.scrollIntoView({behavior:'smooth',block:'start'}); setTimeout(()=>noteRef.current?.focus(),300); }}><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z"/></svg><span>Nota</span></button>
                        <button onClick={e=>{ e.stopPropagation(); const r=e.currentTarget.getBoundingClientRect(); setContextMenu({x:r.left/escZ(),y:r.bottom/escZ()+6,convId:activeConv}); }}><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg><span>Etiqueta</span></button>
                        <button onClick={()=>{ setShowAssign(true); loadTeam(); }}><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg><span>Asignar</span></button>
                        <button onClick={()=>window.open(`https://wa.me/${(conv?.contact_phone||'').replace(/\D/g,'')}`)}><svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.4 8.4 0 0 1-12.6 7.3L3 20l1.3-5A8.4 8.4 0 1 1 21 11.5z"/></svg><span>WhatsApp</span></button>
                      </div>
                      <div className="ip-sec-h">Datos del contacto</div>
                      <div className="cl-detail">
                        <div className="cl-detail-row">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                          <span className="cl-detail-val">{shopifyContact?.telefono || conv?.contact_phone || '—'}</span>
                        </div>
                        <div className="cl-detail-row">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                          <span className="cl-detail-val">{shopifyContact?.ciudad || '—'}</span>
                        </div>
                        {(shopifyContact?.direccion && shopifyContact.direccion !== '—') && (
                          <div className="cl-detail-row">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                            <span className="cl-detail-val" style={{whiteSpace:'normal'}}>{shopifyContact.direccion}</span>
                          </div>
                        )}
                      </div>
                      {shopifyContact && (
                        <div className="cl-stats">
                          <div className="cl-stat">
                            <div className="cl-stat-num">{shopifyContact.pedidos || 0}</div>
                            <div className="cl-stat-label">Pedidos</div>
                          </div>
                          <div className="cl-stat">
                            <div className="cl-stat-num" style={{color:'#16a34a'}}>{fmtCOP(shopifyContact.valor)}</div>
                            <div className="cl-stat-label">Valor total</div>
                          </div>
                        </div>
                      )}
                    </div>
                    {loadingOrders ? <div style={{display:'flex',justifyContent:'center',padding:16}}><div className="spinner"/></div>
                    : activeOrder ? (
                      <>
                        <div className="ip-section-title"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>Pedido activo</div>
                        <div className="op-card">
                          <div className="op-head">
                            <div>
                              <span className="op-id">{activeOrder.id}</span>
                              {activeOrder.tienda && <div style={{fontSize:10,color:'#999',marginTop:1}}>{activeOrder.tienda}</div>}
                            </div>
                            <span className={`order-status ${activeOrder.estado==='entregado'?'status-entregado':activeOrder.estado==='despachado'?'status-despachado':'status-pendiente'}`}>{activeOrder.estado_label}</span>
                          </div>
                          {(activeOrder.items_detalle && activeOrder.items_detalle.length > 0 ? activeOrder.items_detalle : [{producto: activeOrder.product, variante: activeOrder.variant, cantidad: 1, variant_id: ''}]).map((item, idx) => {
                            const imgUrl = orderImages[idx];
                            return (
                              <div key={idx} className="op-item">
                                {imgUrl ? (
                                  <img className="op-img" src={imgUrl} alt="" onError={e => { e.target.style.display='none'; e.target.nextSibling && (e.target.nextSibling.style.display='flex'); }} />
                                ) : null}
                                <div className="op-img-placeholder" style={imgUrl ? {display:'none'} : {}}>
                                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="1.5" strokeLinecap="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>
                                </div>
                                <div className="op-item-body">
                                  <div className="op-item-name">{item.producto || item.title || 'Producto'}</div>
                                  {(item.variante || item.variant_title) && (item.variante || item.variant_title) !== '—' && (
                                    <div className="op-item-variant">{item.variante || item.variant_title}</div>
                                  )}
                                  {(item.cantidad || item.quantity || 1) > 1 && (
                                    <div className="op-item-qty">Cant: {item.cantidad || item.quantity}</div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                          <div className="op-footer">
                            <span className="op-price">{fmtCOP(activeOrder.total)}</span>
                            {(() => {
                              const pm = PAYMENT_MAP[(activeOrder.metodo||'').toLowerCase()] || { label: activeOrder.metodo || 'COD', img: null };
                              const isPaid = activeOrder.estado_pago === 'paid';
                              return (
                                <span className="op-payment" style={{ background: isPaid ? '#f0fdf4' : '#fef9c3', color: isPaid ? '#16a34a' : '#854d0e' }}>
                                  {pm.img && <img src={pm.img} alt="" style={{height:13,objectFit:'contain',marginRight:4,verticalAlign:'middle',borderRadius:2}}/>}
                                  {isPaid ? 'Pagado' : 'Pendiente'} · {pm.label}
                                </span>
                              );
                            })()}
                          </div>
                        </div>

                        {/* ── Trazabilidad ─────────────────────────────────────── */}
                        <div className="ip-section-title"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>Trazabilidad</div>
                        <div className="op-card" style={{display:'flex',flexDirection:'column',gap:10}}>
                          {/* Envío */}
                          <div className="tr-section">
                            <div className="tr-section-label">Envío</div>
                            {(() => {
                              const cKey = (transportadora||'').toLowerCase();
                              const carrier = CARRIERS_MAP[cKey];
                              return (
                                <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:4}}>
                                  {carrier?.img && <img src={carrier.img} alt="" style={{width:22,height:22,objectFit:'contain',borderRadius:'50%',border:'1.5px solid #f0efed'}}/>}
                                  <span style={{fontSize:12,fontWeight:600,color:'#0a0a0a'}}>{carrier?.label || transportadora || '—'}</span>
                                </div>
                              );
                            })()}
                            <div className="tr-pills">
                              {activeOrder.estado_envio && (
                                <span className="tr-pill" style={{background: activeOrder.estado_envio==='entregado' ? '#dcfce7' : activeOrder.estado_envio==='despachado' ? '#dbeafe' : '#f5f5f4', color: activeOrder.estado_envio==='entregado' ? '#16a34a' : activeOrder.estado_envio==='despachado' ? '#2563eb' : '#78716c'}}>
                                  {activeOrder.estado_envio}
                                </span>
                              )}
                              {carrierStatus && (
                                <span className="tr-pill" style={{background:'#eff6ff',color:'#3b82f6'}}>{carrierStatus}</span>
                              )}
                            </div>
                            {carrierLastUpdate && (
                              <div style={{fontSize:10,color:'#aaa',marginTop:2}}>{carrierLastUpdate}</div>
                            )}
                          </div>

                          {/* Pago */}
                          <div className="tr-section" style={{borderTop:'1px solid #f5f5f4',paddingTop:10}}>
                            <div className="tr-section-label">Pago</div>
                            {(() => {
                              const pm = PAYMENT_MAP[(activeOrder.metodo||'').toLowerCase()] || { label: activeOrder.metodo || 'COD', img: null };
                              const isPaid = activeOrder.estado_pago === 'paid';
                              return (
                                <div style={{display:'flex',alignItems:'center',gap:8}}>
                                  {pm.img && <img src={pm.img} alt="" style={{height:18,objectFit:'contain',borderRadius:3}}/>}
                                  <span style={{fontSize:12,fontWeight:600,color:'#0a0a0a'}}>{pm.label}</span>
                                  <span className="tr-pill" style={{marginLeft:'auto',background: isPaid ? '#dcfce7' : '#fef9c3', color: isPaid ? '#16a34a' : '#854d0e'}}>
                                    {isPaid ? 'Pagado' : 'Pendiente'}
                                  </span>
                                </div>
                              );
                            })()}
                          </div>

                          {/* Guías */}
                          <div className="tr-section" style={{borderTop:'1px solid #f5f5f4',paddingTop:10}}>
                            <div className="tr-section-label">Guía de envío</div>
                            {guiaNexus ? (
                              <div style={{display:'flex',alignItems:'center',gap:6}}>
                                <img src="/nexus-logo.png" alt="" style={{width:14,height:14,objectFit:'contain'}}/>
                                <a href={trackingUrl} target="_blank" rel="noreferrer" style={{fontSize:12,fontWeight:700,color:'#f90370',textDecoration:'none'}}>
                                  {guiaNexus}
                                </a>
                              </div>
                            ) : nexusOrderInfo?.has_nexus ? (
                              <div style={{display:'flex',alignItems:'center',gap:6}}>
                                <img src="/nexus-logo.png" alt="" style={{width:14,height:14,objectFit:'contain'}}/>
                                <span style={{fontSize:11,color:'#aaa'}}>Sin guía aún</span>
                              </div>
                            ) : null}
                            {guiaShopify && (
                              <div style={{display:'flex',alignItems:'center',gap:6,marginTop: guiaNexus || nexusOrderInfo?.has_nexus ? 4 : 0}}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#96bf48" strokeWidth="2" strokeLinecap="round"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/></svg>
                                {activeOrder.tracking_url ? (
                                  <a href={activeOrder.tracking_url} target="_blank" rel="noreferrer" style={{fontSize:12,fontWeight:700,color:'#3b82f6',textDecoration:'none'}}>
                                    {guiaShopify}
                                  </a>
                                ) : (
                                  <span style={{fontSize:12,fontWeight:600,color:'#0a0a0a'}}>{guiaShopify}</span>
                                )}
                              </div>
                            )}
                            {!guiaNexus && !guiaShopify && !nexusOrderInfo?.has_nexus && (
                              <span style={{fontSize:11,color:'#bbb'}}>Sin guía de envío</span>
                            )}
                          </div>
                        </div>

                        {/* ── Timeline de tracking ────────────────────────────────── */}
                        {nexusOrderInfo?.shopify_order_id && (trackingTimeline?.events?.length > 0 || trackingTimelineLoading) && (
                          <div style={{marginBottom:14}}>
                            <div
                              onClick={()=>setTrackingTimelineOpen(!trackingTimelineOpen)}
                              style={{cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:trackingTimelineOpen?8:0,transition:'margin .2s'}}
                            >
                              <div className="ip-section-title" style={{marginBottom:0}}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
                                Seguimiento del envío
                              </div>
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#aaa" strokeWidth="2.5" strokeLinecap="round" style={{transform:trackingTimelineOpen?'rotate(180deg)':'rotate(0deg)',transition:'transform .2s'}}><polyline points="6 9 12 15 18 9"/></svg>
                            </div>
                            {trackingTimelineOpen && (
                              trackingTimelineLoading ? (
                                <div style={{display:'flex',alignItems:'center',justifyContent:'center',padding:'18px 0'}}>
                                  <div className="spinner" style={{width:16,height:16,borderWidth:2}}/>
                                </div>
                              ) : (
                                <div style={{background:'#fff',border:'1px solid #e8e8e6',borderRadius:14,padding:'14px 16px',position:'relative'}}>
                                  {trackingTimeline.events.map((ev, idx) => {
                                    const isLast = idx === trackingTimeline.events.length - 1;
                                    const statusColor = (() => {
                                      if (ev.status === 'delivered') return '#22c55e';
                                      if (ev.status === 'novedad') return '#f59e0b';
                                      if (ev.status === 'novedad_resuelta') return '#22c55e';
                                      if (ev.status === 'returned' || ev.status === 'cancelled') return '#ef4444';
                                      if (ev.status === 'en_reparto') return '#8b5cf6';
                                      return '#3b82f6';
                                    })();
                                    const fmtDate = (d) => {
                                      if (!d) return '';
                                      try {
                                        const dt = new Date(d);
                                        return dt.toLocaleDateString('es-CO',{day:'numeric',month:'short'}) + ' · ' + dt.toLocaleTimeString('es-CO',{hour:'2-digit',minute:'2-digit',hour12:false});
                                      } catch { return d; }
                                    };
                                    const statusLabel = (() => {
                                      const m = {
                                        shipped:'Guía generada',in_transit:'En tránsito',terminal_origen:'Terminal origen',
                                        terminal_destino:'Terminal destino',en_reparto:'En reparto',delivered:'Entregado',
                                        returned:'Devolución',cancelled:'Cancelado',novedad:'Novedad',novedad_resuelta:'Novedad resuelta'
                                      };
                                      return m[ev.status] || ev.status;
                                    })();
                                    return (
                                      <div key={idx} style={{display:'flex',gap:12,position:'relative',paddingBottom:isLast?0:16,minHeight:isLast?'auto':36}}>
                                        <div style={{display:'flex',flexDirection:'column',alignItems:'center',flexShrink:0,width:14}}>
                                          <div style={{width:10,height:10,borderRadius:'50%',background:isLast?statusColor:'#fff',border:`2px solid ${statusColor}`,flexShrink:0,position:'relative',zIndex:1,boxShadow:isLast?`0 0 0 3px ${statusColor}22`:undefined}}/>
                                          {!isLast && <div style={{width:1.5,flex:1,background:'#e8e8e6',marginTop:3}}/>}
                                        </div>
                                        <div style={{flex:1,minWidth:0,paddingTop:0}}>
                                          <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:1}}>
                                            <span style={{fontSize:11.5,fontWeight:700,color:ev.status==='novedad'?'#d97706':'#0a0a0a',lineHeight:1.2}}>{statusLabel}</span>
                                            {ev.status==='novedad' && <span style={{fontSize:8,fontWeight:800,background:'#fffbeb',color:'#d97706',padding:'1px 5px',borderRadius:999,border:'1px solid #fde68a',letterSpacing:'0.03em'}}>!</span>}
                                            {ev.status==='delivered' && <span style={{fontSize:8,fontWeight:800,background:'#f0fdf4',color:'#22c55e',padding:'1px 5px',borderRadius:999,border:'1px solid #bbf7d0'}}>&#10003;</span>}
                                          </div>
                                          {ev.description && ev.description !== statusLabel && (
                                            <div style={{fontSize:10.5,color:'#888',lineHeight:1.4,marginBottom:1}}>{ev.description}</div>
                                          )}
                                          <div style={{fontSize:9.5,color:'#bbb',fontVariantNumeric:'tabular-nums'}}>{fmtDate(ev.created_at)}</div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )
                            )}
                          </div>
                        )}

                        {/* ── Enviar guía manual ────────────────────────────────────── */}
                        <div style={{marginBottom:14}}>
                          <div className="ip-section-title" style={{color:'#aaa',fontWeight:400,fontSize:10,letterSpacing:0.3}}>
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="1" y="3" width="15" height="13" rx="2"/><path d="M16 8h4l3 3v5a2 2 0 0 1-2 2h-1"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>
                            Envía guía manual (opcional)
                          </div>
                          <div style={{display:'flex',gap:6,marginTop:8}}>
                            {[
                              {val:'coordinadora',img:'/coordinadora-btn.webp'},
                              {val:'interrapidisimo',img:'/interrapidisimo-btn.jpg'},
                              {val:'servientrega',img:'/servientrega-btn.jpg'},
                              {val:'tcc',img:'/tcc-logo.jpg'},
                            ].map(c=>{
                              const sel = shipCarrier===c.val;
                              return (
                                <button key={c.val} onClick={()=>{setShipCarrier(c.val);setShipMsg('');}} style={{flex:1,height:38,borderRadius:10,border:sel?'2px solid #0a0a0a':'1.5px solid #e8e8e6',background:'#fff',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',padding:0,transition:'all 0.15s'}}>
                                  <img src={c.img} alt={c.val} style={{height:22,objectFit:'contain',borderRadius:4,opacity:sel?1:0.4,transition:'opacity 0.15s'}}/>
                                </button>
                              );
                            })}
                          </div>
                          <div style={{display:'flex',gap:6,marginTop:8}}>
                            <input
                              type="text"
                              placeholder="Número de guía"
                              value={shipGuia}
                              onChange={e=>{setShipGuia(e.target.value);setShipMsg('');}}
                              style={{flex:1,padding:'9px 11px',borderRadius:9,border:'1.5px solid #e8e8e6',fontSize:12,fontFamily:'inherit',outline:'none',color:'#0a0a0a',background:'#fafafa',transition:'border 0.15s'}}
                            />
                            <button
                              disabled={shipLoading || !shipGuia.trim()}
                              onClick={async()=>{
                                if (!shipGuia.trim() || !activeOrder?.shopify_id) return;
                                setShipLoading(true); setShipMsg('');
                                try {
                                  const res = await fetch(`${API}/orders/${activeOrder.shopify_id}/fulfill-manual`, {
                                    method:'POST',
                                    headers: authH({'Content-Type':'application/json'}),
                                    body: JSON.stringify({ tracking_number: shipGuia.trim(), carrier: shipCarrier }),
                                  });
                                  const d = await res.json();
                                  if (res.ok && d.ok) {
                                    setShipMsg('✅ Guía enviada');
                                    setShipGuia('');
                                    const c = conversations.find(x=>x.id===activeConv);
                                    if (c?.contact_phone) loadShopifyData(c.contact_phone);
                                  } else {
                                    setShipMsg(`❌ ${d.detail || 'Error'}`);
                                  }
                                } catch(e) { setShipMsg('❌ Error de conexión'); }
                                setShipLoading(false);
                              }}
                              style={{padding:'9px 16px',borderRadius:9,border:'none',background:shipLoading||!shipGuia.trim()?'#ddd':'#0a0a0a',color:'#fff',fontSize:11,fontWeight:700,cursor:shipLoading||!shipGuia.trim()?'not-allowed':'pointer',fontFamily:'inherit',transition:'all 0.15s',whiteSpace:'nowrap'}}
                            >
                              {shipLoading ? '...' : 'Enviar'}
                            </button>
                          </div>
                          {shipMsg && (
                            <div style={{marginTop:6,fontSize:11,color:shipMsg.includes('✅')?'#16a34a':'#ef4444',fontWeight:500,textAlign:'center'}}>{shipMsg}</div>
                          )}
                        </div>
                      </>
                    ) : shopifyContact ? <div style={{padding:'10px 12px',background:'#f8f8f6',borderRadius:10,fontSize:12,color:'#aaa',textAlign:'center',marginBottom:16}}>Sin pedidos activos</div> : null}
                    {pastOrders.length > 0 && (
                      <>
                        <div className="ip-section-title"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>Historial reciente</div>
                        {pastOrders.map(o=>(
                          <div key={o.id} className="hist-item">
                            <div><div style={{fontSize:12,fontWeight:700,color:'#0a0a0a'}}>{o.id}</div><div style={{fontSize:11,color:'#aaa'}}>{o.product}</div></div>
                            <div style={{fontSize:10,fontWeight:700,padding:'2px 6px',borderRadius:999,background: o.estado==='despachado'?'#eff6ff':o.estado==='entregado'?'#f0fdf4':'#f8f8f6', color: o.estado==='despachado'?'#3b82f6':o.estado==='entregado'?'#16a34a':'#888'}}>{o.estado_label}</div>
                          </div>
                        ))}
                      </>
                    )}
                    {!shopifyContact && conv && <div style={{padding:'10px 12px',background:'#f8f8f6',borderRadius:10,fontSize:12,color:'#aaa',textAlign:'center',marginTop:8}}>Cliente nuevo — sin historial en Shopify</div>}
                  </>
                )}
                <div ref={notesSecRef}>
                  <div className="ip-sec-h">Notas internas {notes.length > 0 && <span className="ip-cnt">{notes.length}</span>}</div>
                  {notes.length === 0 && <div className="nt-empty">Sin notas. Solo tu equipo las ve; el cliente no.</div>}
                  <div className="nt-list">
                    {notes.map(n => (
                      <div key={n.id} className="nt-item">
                        <div className="nt-top">
                          <span className="nt-author">{n.author || 'Equipo'}</span>
                          <span className="nt-time">{fmtTime(n.created_at)}</span>
                          <button className="nt-del" title="Eliminar nota" onClick={()=>deleteNote(n)}><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/></svg></button>
                        </div>
                        <div className="nt-text">{n.text}</div>
                      </div>
                    ))}
                  </div>
                  <textarea ref={noteRef} className="note-input" placeholder="Escribe una nota interna… (Ctrl + Enter para guardar)" value={noteInput}
                    onChange={e=>setNoteInput(e.target.value)} onKeyDown={e=>{ if(e.key==='Enter' && (e.ctrlKey||e.metaKey)) { e.preventDefault(); saveNote(); } }}/>
                  <button className="note-btn" disabled={!noteInput.trim()||noteSaving} onClick={saveNote}>{noteSaving ? 'Guardando…' : 'Guardar nota'}</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showTemplates && (() => {
        const q = templateSearch.toLowerCase();
        const hdr = (t) => (t?.components || []).find(c => c.type === 'HEADER');
        const sendable = (t) => t.status === 'APPROVED' && (!hdr(t) || hdr(t).format === 'TEXT' || hdr(t).format === 'IMAGE');
        const metas = metaTemplates.filter(t => sendable(t) && (t.name || '').toLowerCase().includes(q));
        const ints = intTemplates.filter(p => (p.name || '').toLowerCase().includes(q));
        const bodyOf = (t) => (t?.components || []).find(c => c.type === 'BODY')?.text || '';
        const varIdx = (t) => [...new Set((bodyOf(t).match(/\{\{(\d+)\}\}/g) || []).map(v => parseInt(v.slice(2))))].sort((x, y) => x - y);
        const close = () => { setShowTemplates(false); setTplPick(null); setMetaTplErr(''); };
        const pick = (t) => { setTplPick(t); setTplVals(varIdx(t).map(() => '')); setMetaTplErr(''); };
        const idxs = tplPick ? varIdx(tplPick) : [];
        let preview = bodyOf(tplPick); idxs.forEach((n, i) => { preview = preview.split(`{{${n}}}`).join(tplVals[i] || `{{${n}}}`); });
        const sendPicked = () => {
          if (tplVals.some(v => !String(v).trim())) { setMetaTplErr('Completa todas las variables.'); return; }
          const h = hdr(tplPick);
          sendMetaTemplate(tplPick, tplVals, h && h.format === 'IMAGE' ? (h.example?.header_handle || [])[0] || '' : '');
        };
        return (
        <div className="modal-overlay nc-overlay" onClick={close}>
          <div className="nc-modal" style={{width:560}} onClick={e=>e.stopPropagation()}>
            <div className="nc-head">
              <div>
                <div className="nc-title">{tplPick ? (tplPick.name || '').replace(/_/g, ' ') : 'Plantillas'}</div>
                <div className="nc-sub">{tplPick ? 'Revisa el mensaje antes de enviarlo' : 'Envía una plantilla aprobada o usa una interna como borrador'}</div>
              </div>
              <button className="nc-x" onClick={close} aria-label="Cerrar"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>
            </div>

            {!tplPick ? (
              <div className="nc-body">
                <div className="tp-tabs">
                  {[['meta','Aprobadas por WhatsApp'],['internas','Internas']].map(([t,l]) => (
                    <button key={t} className={templateTab===t?'on':''} onClick={()=>setTemplateTab(t)}>{l}</button>
                  ))}
                </div>
                <input className="nc-input" value={templateSearch} onChange={e=>setTemplateSearch(e.target.value)} placeholder="Buscar plantilla..." style={{margin:'10px 0'}}/>
                {templateTab === 'internas' && isWa24hExpired && (
                  <div className="nc-notice" style={{marginBottom:10}}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="9"/><line x1="12" y1="11" x2="12" y2="16"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
                    <div>Pasaron más de 24 horas desde el último mensaje del cliente: WhatsApp solo permite enviar plantillas aprobadas.</div>
                  </div>
                )}
                <div className="nc-tpls" style={{maxHeight:360}}>
                  {templateTab === 'meta' ? (
                    metaTplLoading ? <div className="nc-empty">Cargando plantillas…</div>
                    : metas.length === 0 ? <div className="nc-empty">{q ? 'Ninguna plantilla coincide.' : 'No tienes plantillas aprobadas. Créalas en Configuración → Plantillas.'}</div>
                    : metas.map((t, i) => (
                      <button key={t.name+i} className="nc-tpl" style={{animationDelay:`${Math.min(i,8)*25}ms`}} onClick={()=>pick(t)}>
                        <div className="nc-tn">{(t.name||'').replace(/_/g,' ')}{hdr(t)?.format==='IMAGE' && <span className="nc-tag">imagen</span>}{varIdx(t).length>0 && <span className="nc-tag">{varIdx(t).length} variable{varIdx(t).length>1?'s':''}</span>}</div>
                        <div className="nc-tb">{bodyOf(t)}</div>
                      </button>
                    ))
                  ) : (
                    ints.length === 0 ? <div className="nc-empty">{q ? 'Ninguna plantilla coincide.' : 'No tienes plantillas internas. Créalas en Configuración → Plantillas.'}</div>
                    : ints.map((p, i) => (
                      <button key={p.id} className="nc-tpl" disabled={isWa24hExpired} style={{animationDelay:`${Math.min(i,8)*25}ms`,opacity:isWa24hExpired?.5:1,cursor:isWa24hExpired?'not-allowed':'pointer'}}
                        onClick={()=>{
                          const nombre = shopifyContact?.nombre || conv?.contact_name || 'cliente';
                          chatInputRef.current?.set((p.text||'').split('{nombre}').join(nombre).split('{orden}').join(activeOrder?.id||'—').split('{estado}').join(activeOrder?.status||'—'));
                          close();
                        }}>
                        <div className="nc-tn">{p.name}<span className="nc-tag">{p.category||'General'}</span></div>
                        <div className="nc-tb">{p.text}</div>
                      </button>
                    ))
                  )}
                </div>
                {templateTab === 'internas' && ints.length > 0 && !isWa24hExpired && <div className="nc-hint" style={{marginTop:8}}>La plantilla se copia en la caja de mensaje para que la revises antes de enviar.</div>}
              </div>
            ) : (
              <>
                <div className="nc-body">
                  {idxs.length > 0 && <div className="nc-step">Completa los datos</div>}
                  {idxs.map((n, i) => (
                    <input key={n} className="nc-input" style={{marginBottom:8}} value={tplVals[i]} placeholder={`Valor para {{${n}}}`}
                      onChange={e=>{ const v=[...tplVals]; v[i]=e.target.value; setTplVals(v); setMetaTplErr(''); }}/>
                  ))}
                  <div className="nc-step">Vista previa</div>
                  {hdr(tplPick)?.format === 'IMAGE' && (hdr(tplPick).example?.header_handle||[])[0] && (
                    <img src={(hdr(tplPick).example.header_handle)[0]} alt="" style={{width:'100%',maxHeight:160,objectFit:'cover',borderRadius:6,border:'1px solid #ececec',marginBottom:6}}/>
                  )}
                  <div className="nc-preview">{preview}</div>
                </div>
                <div className="nc-foot">
                  {metaTplErr && <div className="nc-err">{metaTplErr}</div>}
                  <div style={{display:'flex',gap:10}}>
                    <button className="nc-btn ghost" onClick={()=>{ setTplPick(null); setMetaTplErr(''); }}>Volver</button>
                    <button className="nc-btn primary" onClick={sendPicked} disabled={metaTplSending}>
                      {metaTplSending ? <><div className="spinner" style={{width:13,height:13,borderTopColor:'#fff',borderColor:'rgba(255,255,255,0.3)'}}/> Enviando…</> : 'Enviar plantilla'}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
        );
      })()}

      {/* Modal de variables para plantilla Meta */}
      {metaTplToSend && (
        <TemplateModal
          tpl={metaTplToSend}
          sending={metaTplSending}
          err={metaTplErr}
          onSend={(tpl, vars, headerUrl) => {
            setMetaTplVars(vars);
            setMetaTplHeaderUrl(headerUrl);
            sendMetaTemplate(tpl, vars, headerUrl);
          }}
          onClose={()=>{ setMetaTplToSend(null); setMetaTplVars([]); setMetaTplErr(''); setMetaTplHeaderUrl(''); }}
        />
      )}

      {showOrderSelect && (
        <div className="modal-overlay nc-overlay" onClick={()=>setShowOrderSelect(false)}>
          <div className="nc-modal" style={{width:480}} onClick={e=>e.stopPropagation()}>
            <div className="nc-head">
              <div className="sp-headl">
                <img src="/shopify-logo.svg" alt="Shopify" className="sp-logo"/>
                <div>
                  <div className="nc-title">Seleccionar pedido</div>
                  <div className="nc-sub">Este cliente tiene {shopifyOrders.length} pedidos en Shopify</div>
                </div>
              </div>
              <button className="nc-x" onClick={()=>setShowOrderSelect(false)} aria-label="Cerrar"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>
            </div>
            <div className="nc-body">
              <div className="sp-list">
                {shopifyOrders.map((o, i) => (
                  <button key={o.shopify_id} className="sp-order" style={{animationDelay:`${Math.min(i,8)*25}ms`}} onClick={()=>{setSelectedOrder(o);setShowOrderSelect(false);setShowOrderActions(true);setActionMsg('');}}>
                    <div className="sp-row">
                      <span className="sp-id">{o.id}</span>
                      <span className={`sp-badge${o.status==='Entregado'?' ok':''}`}>{o.status}</span>
                    </div>
                    <div className="sp-prod">{o.product}{o.variant?` · ${o.variant}`:''}</div>
                    <div className="sp-row">
                      <span className="sp-total">{fmtCOP(o.total)}</span>
                      <span className="sp-date">{!isOrderEditable(o) && 'No modificable · '}{o.date ? new Date(o.date).toLocaleDateString('es-CO',{day:'2-digit',month:'short',year:'numeric'}) : '—'}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {showOrderActions && selectedOrder && (() => {
        const o = selectedOrder;
        const editable = isOrderEditable(o);
        const paid = o.financial_status === 'paid';
        const close = () => { setShowOrderActions(false); setActionMsg(''); setShopifyNoteInput(''); setShopifyTagInput(''); };
        const ok = actionMsg && actionMsg.includes('✅');
        const Ic = (d, c) => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={c || 'currentColor'} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" dangerouslySetInnerHTML={{__html: d}}/>;
        return (
        <div className="modal-overlay nc-overlay" onClick={close}>
          <div className="nc-modal" style={{width:460}} onClick={e=>e.stopPropagation()}>
            <div className="nc-head">
              <div className="sp-headl">
                <img src="/shopify-logo.svg" alt="Shopify" className="sp-logo"/>
                <div>
                  <div className="nc-title">Pedido {o.id}</div>
                  <div className="nc-sub">{o.product} · {fmtCOP(o.total)}</div>
                </div>
              </div>
              <button className="nc-x" onClick={close} aria-label="Cerrar"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>
            </div>
            <div className="nc-body">
              {!editable && <div className="nc-notice" style={{marginBottom:10}}>{Ic('<circle cx="12" cy="12" r="9"/><line x1="12" y1="11" x2="12" y2="16"/><line x1="12" y1="8" x2="12.01" y2="8"/>')}<div>Este pedido ya no se puede preparar ni cancelar desde Talku.</div></div>}
              {actionMsg && <div className={ok ? 'sp-ok' : 'nc-err'}>{actionMsg.replace(/^[✅❌]\s*/, '')}</div>}

              <div className="sp-acts">
                <button className="sp-act" disabled={!editable||actionLoading} onClick={()=>shopifyAction('fulfill',o.shopify_id)}>
                  <span className="sp-aic">{Ic('<path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/>')}</span>
                  <span className="sp-at"><b>Preparar pedido</b><span>Marca el pedido como preparado (fulfillment)</span></span>
                  {actionLoading && <div className="spinner" style={{width:13,height:13}}/>}
                </button>
                <button className="sp-act" disabled={paid||actionLoading} onClick={()=>shopifyAction('mark-paid',o.shopify_id)}>
                  <span className="sp-aic">{Ic('<rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/>')}</span>
                  <span className="sp-at"><b>Marcar como pagado</b><span>{paid ? 'Este pedido ya está pagado' : 'Registra el pago en Shopify'}</span></span>
                </button>
                {/^\d+$/.test(String(o.shopify_id)) && storeDomain && (
                  <button className="sp-act" onClick={()=>window.open(`https://${storeDomain}/admin/orders/${o.shopify_id}`,'_blank')}>
                    <span className="sp-aic">{Ic('<path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>')}</span>
                    <span className="sp-at"><b>Ver en Shopify</b><span>Abre este pedido en el administrador</span></span>
                  </button>
                )}
              </div>

              <div className="nc-step">Nota del pedido</div>
              {o.note && <div className="sp-cur">Nota actual: “{o.note}”</div>}
              <div className="sp-inl">
                <input className="nc-input" value={shopifyNoteInput} onChange={e=>setShopifyNoteInput(e.target.value)} placeholder="Escribe una nota interna…"/>
                <button className="sp-btn" disabled={!shopifyNoteInput.trim()||actionLoading} onClick={()=>shopifyAction('note',o.shopify_id,{note:shopifyNoteInput}).then(()=>setShopifyNoteInput(''))}>Guardar</button>
              </div>

              <div className="nc-step">Etiquetas</div>
              {o.tags && <div className="sp-cur">{String(o.tags).split(',').map(t=>t.trim()).filter(Boolean).map(t=><span key={t} className="sp-tag">{t}</span>)}</div>}
              <div className="sp-inl">
                <input className="nc-input" value={shopifyTagInput} onChange={e=>setShopifyTagInput(e.target.value)} placeholder="ej: urgente, cambio-talla"/>
                <button className="sp-btn" disabled={!shopifyTagInput.trim()||actionLoading} onClick={()=>shopifyAction('tags',o.shopify_id,{tags:shopifyTagInput}).then(()=>setShopifyTagInput(''))}>Agregar</button>
              </div>

              <div className="sp-acts" style={{marginTop:14}}>
                <button className="sp-act" onClick={()=>storeDomain && window.open(`https://${storeDomain}/admin/orders/new`,'_blank')}>
                  <span className="sp-aic">{Ic('<line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>')}</span>
                  <span className="sp-at"><b>Crear nuevo pedido</b><span>Abre el creador de pedidos de Shopify</span></span>
                </button>
                <button className="sp-act danger" disabled={!editable||actionLoading} onClick={()=>{ if(window.confirm(`¿Cancelar el pedido ${o.id}? Esta acción no se puede deshacer.`)) shopifyAction('cancel',o.shopify_id); }}>
                  <span className="sp-aic">{Ic('<circle cx="12" cy="12" r="9"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>')}</span>
                  <span className="sp-at"><b>Cancelar pedido</b><span>Acción irreversible</span></span>
                </button>
              </div>
            </div>
          </div>
        </div>
        );
      })()}

      {showAssign && (() => {
        const ROLE = { owner: 'Dueño', admin: 'Administrador', agent: 'Asesor' };
        const q = assignSearch.trim().toLowerCase();
        const members = teamMembers.filter(m => m.active !== false && (!q || (m.name || '').toLowerCase().includes(q) || (m.email || '').toLowerCase().includes(q)));
        const current = conv?.assigned_to;
        return (
        <div className="modal-overlay nc-overlay" onClick={()=>setShowAssign(false)}>
          <div className="nc-modal" style={{width:420}} onClick={e=>e.stopPropagation()}>
            <div className="nc-head">
              <div>
                <div className="nc-title">Asignar conversación</div>
                <div className="nc-sub">{conv?.contact_name || fmtPhone(conv?.contact_phone)}{current ? ` · Asignada a ${current}` : ' · Sin asignar'}</div>
              </div>
              <button className="nc-x" onClick={()=>setShowAssign(false)} aria-label="Cerrar">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div className="nc-body" style={{paddingBottom:12}}>
              {teamMembers.length > 6 && <input className="nc-input" autoFocus value={assignSearch} onChange={e=>setAssignSearch(e.target.value)} placeholder="Buscar miembro del equipo..." style={{marginBottom:8}}/>}
              {teamMembers.length === 0 ? (
                <div className="nc-notice" style={{marginTop:4}}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="9"/><line x1="12" y1="11" x2="12" y2="16"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
                  <div>Aún no hay miembros en tu equipo. Agrégalos en Configuración → Empresa.</div>
                </div>
              ) : (
                <div className="as-list">
                  {members.length === 0 && <div className="nc-empty">Nadie coincide con "{assignSearch}"</div>}
                  {members.map((m, i) => {
                    const nm = m.name || m.email;
                    const sel = current === nm;
                    return (
                      <button key={m.id} className={`as-item${sel?' sel':''}`} style={{animationDelay:`${Math.min(i,8)*25}ms`}} onClick={()=>assignAgent(nm)}>
                        <span className="as-av" style={{background:AVATAR_COLORS[(m.id||0)%AVATAR_COLORS.length]}}>{initials(nm)}</span>
                        <span style={{flex:1,minWidth:0}}>
                          <span className="as-name">{nm}</span>
                          <span className="as-meta">{ROLE[m.role] || m.role}{m.name ? ` · ${m.email}` : ''}</span>
                        </span>
                        {sel && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="2.4" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            {current && (
              <div className="nc-foot" style={{paddingTop:10,paddingBottom:14}}>
                <button className="nc-btn ghost" style={{width:'100%'}} onClick={()=>assignAgent(null)}>Quitar asignación</button>
              </div>
            )}
          </div>
        </div>
        );
      })()}


      {imgModal && (
        <div onClick={()=>setImgModal(null)} style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.92)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',cursor:'zoom-out'}}>
          {imgModal?.type === 'video' ?
            <video controls autoPlay src={imgModal.url} style={{maxWidth:'90vw',maxHeight:'90vh',borderRadius:12}} onClick={e=>e.stopPropagation()} /> :
            <img src={typeof imgModal === 'string' ? imgModal : imgModal?.url} alt="preview" style={{maxWidth:'90vw',maxHeight:'90vh',borderRadius:12,objectFit:'contain'}} onClick={e=>e.stopPropagation()} />
          }
          <div onClick={()=>setImgModal(null)} style={{position:'absolute',top:20,right:24,width:40,height:40,borderRadius:'50%',background:'rgba(255,255,255,0.15)',display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer',fontSize:22,color:'#fff',fontWeight:300}}>×</div>
        </div>
      )}

      {/* Toast notifications */}
      <style>{`
        @keyframes toastIn {
          from { transform: translateX(-110%); opacity: 0; }
          to   { transform: translateX(0);    opacity: 1; }
        }
        @keyframes toastOut {
          from { transform: translateX(0);    opacity: 1; }
          to   { transform: translateX(-110%); opacity: 0; }
        }
      `}</style>
      <div style={{position:'fixed',bottom:24,left:24,zIndex:9999,display:'flex',flexDirection:'column',gap:10,pointerEvents:'none'}}>
        {notifications.slice(-4).map(n => {
          const isOut = exitingNotifs.has(n.id);
          const avatarColor = AVATAR_COLORS[(n.convId || 0) % AVATAR_COLORS.length];
          return (
            <div key={n.id} style={{
              width:340,background:'#fff',borderRadius:18,
              boxShadow:'0 12px 48px rgba(0,0,0,0.14),0 2px 8px rgba(0,0,0,0.06)',
              padding:'14px 16px 14px 14px',pointerEvents:'all',
              animation:`${isOut?'toastOut 0.35s cubic-bezier(0.4,0,1,1)':'toastIn 0.42s cubic-bezier(0.16,1,0.3,1)'} forwards`,
            }}>
              {/* Header */}
              <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:9}}>
                <div style={{position:'relative',flexShrink:0}}>
                  <div style={{width:40,height:40,borderRadius:'50%',background:avatarColor,display:'flex',alignItems:'center',justifyContent:'center',fontSize:14,fontWeight:700,color:'#fff',letterSpacing:0.5}}>
                    {initials(n.contactName)}
                  </div>
                  <div style={{position:'absolute',bottom:1,right:1,width:10,height:10,borderRadius:'50%',background:'#22c55e',border:'2px solid #fff'}}/>
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:13,fontWeight:700,color:'#0a0a0a',lineHeight:1.2,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{n.contactName}</div>
                  <div style={{fontSize:11,color:'#aaa',marginTop:2}}>{fmtTime(n.timestamp)}</div>
                </div>
                <button onClick={()=>dismissNotification(n.id)} style={{background:'none',border:'none',cursor:'pointer',color:'#c0c0c0',padding:'2px 4px',fontSize:19,lineHeight:1,flexShrink:0,borderRadius:6,transition:'color 0.15s'}}
                  onMouseEnter={e=>e.target.style.color='#888'} onMouseLeave={e=>e.target.style.color='#c0c0c0'}>×</button>
              </div>
              {/* Message */}
              <div style={{fontSize:13,color:'#555',lineHeight:1.55,marginBottom:12,overflow:'hidden',display:'-webkit-box',WebkitLineClamp:2,WebkitBoxOrient:'vertical'}}>
                {n.message}
              </div>
              {/* Buttons */}
              <div style={{display:'flex',gap:8}}>
                <button onClick={()=>dismissNotification(n.id)} style={{flex:1,padding:'8px 0',borderRadius:10,border:'1.5px solid #e4e4e4',background:'#fff',color:'#555',fontSize:13,fontWeight:500,cursor:'pointer',transition:'background 0.15s'}}
                  onMouseEnter={e=>e.target.style.background='#f5f5f5'} onMouseLeave={e=>e.target.style.background='#fff'}>
                  Dismiss
                </button>
                <button onClick={()=>replyNotification(n)} style={{flex:1,padding:'8px 0',borderRadius:10,border:'none',background:'#4f46e5',color:'#fff',fontSize:13,fontWeight:700,cursor:'pointer',transition:'background 0.15s'}}
                  onMouseEnter={e=>e.target.style.background='#4338ca'} onMouseLeave={e=>e.target.style.background='#4f46e5'}>
                  Reply
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Modal: Nueva conversación ── */}
      {showNewConv && (() => {
        const tpl = newConvSelectedTemplate;
        const body = (tpl?.components || []).find(c => c.type === 'BODY')?.text || '';
        let preview = body; newConvVariables.forEach((v, i) => { preview = preview.split(`{{${i+1}}}`).join(v || `{{${i+1}}}`); });
        const typedPhone = normPhone(newConvContactSearch);
        const looksPhone = /^[+\d\s()-]{7,}$/.test(newConvContactSearch.trim());
        const tpls = newConvTemplates.filter(t => t.name.toLowerCase().includes(newConvTemplateSearch.toLowerCase()));
        return (
        <div className="modal-overlay nc-overlay" onClick={() => setShowNewConv(false)}>
          <div className="nc-modal" onClick={e => e.stopPropagation()}>
            <div className="nc-head">
              <div>
                <div className="nc-title">Nueva conversación</div>
                <div className="nc-sub">Envía una plantilla aprobada para iniciar el chat</div>
              </div>
              <button className="nc-x" onClick={() => setShowNewConv(false)} aria-label="Cerrar">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>

            <div className="nc-body">
              <div className="nc-notice">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="9"/><line x1="12" y1="11" x2="12" y2="16"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
                <div>Para escribir a un <b style={{fontWeight:600}}>número nuevo</b> o a un cliente que <b style={{fontWeight:600}}>no ha respondido en las últimas 24 horas</b>, WhatsApp solo permite enviar una plantilla aprobada. Cuando el cliente responda, podrás escribirle libremente durante 24 horas.</div>
              </div>
              {/* 1 · Para */}
              <div className="nc-step">Para</div>
              {newConvSelectedContact ? (
                <div className="nc-picked">
                  <div className="nc-av">{(newConvSelectedContact.contact_name || '?').trim().charAt(0).toUpperCase()}</div>
                  <div style={{flex:1,minWidth:0}}>
                    <div className="nc-pn">{newConvSelectedContact.contact_name || fmtPhone(newConvSelectedContact.contact_phone)}</div>
                    <div className="nc-pp">{fmtPhone(newConvSelectedContact.contact_phone)}</div>
                  </div>
                  <button className="nc-link" onClick={() => { setNewConvSelectedContact(null); setNewConvContactSearch(''); }}>Cambiar</button>
                </div>
              ) : (
                <>
                  <input className="nc-input" autoFocus value={newConvContactSearch} onChange={e => { setNewConvContactSearch(e.target.value); setNewConvError(''); }}
                    placeholder="Número (300 123 4567) o nombre del contacto" />
                  {newConvSuggest.length > 0 && (
                    <div className="nc-suggest">
                      {newConvSuggest.map((c, i) => (
                        <button key={c.id} className="nc-sug" style={{animationDelay:`${i*30}ms`}} onClick={() => { setNewConvSelectedContact(c); setNewConvSuggest([]); }}>
                          <div className="nc-av sm">{(c.contact_name || '?').trim().charAt(0).toUpperCase()}</div>
                          <span className="nc-sn">{c.contact_name || 'Sin nombre'}</span>
                          <span className="nc-sp">{fmtPhone(c.contact_phone)}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  {looksPhone && (
                    <>
                      <div className="nc-hint">Se enviará a <b>{fmtPhone(typedPhone)}</b></div>
                      <input className="nc-input" value={newConvName} onChange={e => setNewConvName(e.target.value)} placeholder="Nombre del cliente (opcional)" style={{marginTop:8}} />
                    </>
                  )}
                </>
              )}

              {/* 2 · Plantilla */}
              <div className="nc-step">Plantilla</div>
              {newConvTemplates.length > 4 && (
                <input className="nc-input" value={newConvTemplateSearch} onChange={e => setNewConvTemplateSearch(e.target.value)} placeholder="Buscar plantilla..." style={{marginBottom:8}} />
              )}
              <div className="nc-tpls">
                {newConvTemplates.length === 0 && <div className="nc-empty">No tienes plantillas aprobadas. Créalas en Configuración → Plantillas.</div>}
                {tpls.map((t, i) => {
                  const sel = tpl?.name === t.name;
                  const h = tplHeader(t);
                  return (
                    <button key={t.name} className={`nc-tpl${sel ? ' sel' : ''}`} style={{animationDelay:`${Math.min(i,8)*30}ms`}} onClick={() => selectNewConvTemplate(t)}>
                      <div className="nc-tn">{t.name.replace(/_/g, ' ')}{h?.format === 'IMAGE' && <span className="nc-tag">imagen</span>}</div>
                      <div className="nc-tb">{(t.components || []).find(c => c.type === 'BODY')?.text || ''}</div>
                    </button>
                  );
                })}
              </div>

              {/* 3 · Variables + vista previa */}
              {tpl && (
                <>
                  {newConvVariables.length > 0 && <div className="nc-step">Completa los datos</div>}
                  {newConvVariables.map((v, i) => (
                    <input key={i} className="nc-input" style={{marginBottom:8}} value={v} placeholder={`Valor para {{${i+1}}}`}
                      onChange={e => setNewConvVariables(prev => { const n = [...prev]; n[i] = e.target.value; return n; })} />
                  ))}
                  <div className="nc-preview">{preview}</div>
                </>
              )}
            </div>

            <div className="nc-foot">
              {newConvError && <div className="nc-err">{newConvError}</div>}
              <div style={{display:'flex',gap:10}}>
                <button className="nc-btn ghost" onClick={() => setShowNewConv(false)}>Cancelar</button>
                <button className="nc-btn primary" onClick={sendNewConversation} disabled={newConvSending}>
                  {newConvSending ? <><div className="spinner" style={{width:13,height:13,borderTopColor:'#fff',borderColor:'rgba(255,255,255,0.3)'}}/> Enviando…</> : 'Enviar plantilla'}
                </button>
              </div>
            </div>
          </div>
        </div>
        );
      })()}

      {/* ── Modal Nexus — Generar Guía (3 columnas) ─────────────────────── */}
      {showNexusModal && (
        <div className="modal-overlay" onClick={e=>e.stopPropagation()} style={{background:'rgba(15,15,20,0.45)'}}>
          <style>{`
            @keyframes nx2Up { from { opacity:0; transform:translateY(12px) scale(.985); } to { opacity:1; transform:none; } }
            @keyframes nx2In { from { opacity:0; transform:translateY(6px); } to { opacity:1; transform:none; } }
            .nx2 { zoom:1.1; width:min(1240px,88vw); max-height:84vh; background:#fff; border:1px solid #e5e5e5; border-radius:10px; box-shadow:0 20px 56px rgba(0,0,0,.18); display:flex; flex-direction:column; overflow:hidden; animation:nx2Up .3s cubic-bezier(.22,1,.36,1) both; }
            .nx2-head { display:flex; align-items:center; justify-content:space-between; padding:16px 22px; border-bottom:1px solid #eee; }
            .nx2-logo { width:34px; height:34px; border-radius:8px; background:#fff0f6; border:1px solid #fbd0e3; display:flex; align-items:center; justify-content:center; }
            .nx2-x { width:30px; height:30px; border-radius:6px; border:none; background:transparent; color:#777; display:flex; align-items:center; justify-content:center; cursor:pointer; transition:background .15s; }
            .nx2-x:hover { background:#f2f2f2; color:#111; }
            .nx2-body { overflow:auto; flex:1; padding:18px 22px 22px; }
            .nx2-sec { animation:nx2In .3s ease both; margin-top:18px; }
            .nx2-st { font-size:13px; font-weight:600; color:#111; margin-bottom:10px; display:flex; align-items:center; gap:8px; }
            .nx2-st small { font-size:12px; font-weight:400; color:#888; }
            .nx2-sum { border:1px solid #e5e5e5; border-radius:8px; padding:14px 16px; display:flex; justify-content:space-between; gap:12px; flex-wrap:wrap; animation:nx2In .3s ease both; }
            .nx2-ped { font-size:11.5px; color:#f90370; font-weight:500; }
            .nx2-cli { font-size:15px; font-weight:600; color:#111; margin-top:2px; }
            .nx2-city { font-size:12.5px; color:#666; margin-top:2px; }
            .nx2-total { font-size:26px; font-weight:600; color:#111; letter-spacing:-.02em; text-align:right; line-height:1; }
            .nx2-tl { font-size:11.5px; color:#888; text-align:right; margin-top:4px; }
            .nx2-pay { display:inline-block; margin-top:6px; font-size:11px; border-radius:4px; padding:2px 8px; }
            .nx2-guide { margin-top:12px; padding-top:12px; border-top:1px solid #f0f0f0; display:flex; align-items:center; justify-content:space-between; gap:12px; width:100%; }
            .nx2-pdf { height:30px; padding:0 12px; border-radius:6px; border:1px solid #dcdcdc; background:#fff; color:#111; font-size:12.5px; font-weight:400; cursor:pointer; font-family:inherit; display:flex; align-items:center; gap:6px; }
            .nx2-pdf:hover { background:#f5f5f5; }
            .nx2-items { border:1px solid #e5e5e5; border-radius:8px; overflow:hidden; }
            .nx2-item { display:flex; align-items:center; gap:12px; padding:9px 12px; border-bottom:1px solid #f1f1f1; }
            .nx2-item:last-child { border-bottom:none; }
            .nx2-img { width:40px; height:40px; border-radius:6px; object-fit:cover; background:#f0f0f0; flex-shrink:0; border:1px solid #eee; }
            .nx2-carriers { display:grid; grid-template-columns:1fr 1fr; gap:10px; }
            .nx2-mode { display:inline-flex; background:#f3f3f1; border-radius:8px; padding:3px; gap:2px; margin:0 0 10px; }
            .nx2-mode button { border:0; background:transparent; padding:6px 12px; font-size:12px; border-radius:6px; cursor:pointer; color:#666; font-weight:500; font-family:inherit; }
            .nx2-mode button.on { background:#fff; color:#0a0a0a; box-shadow:0 1px 2px rgba(0,0,0,.08); font-weight:600; }
            .nx2-mode-note { font-size:11.5px; color:#666; margin:0 0 10px; line-height:1.45; }
            .nx2-car { position:relative; text-align:left; border:1px solid #e3e3e3; border-radius:8px; background:#fff; padding:12px; cursor:pointer; font-family:inherit; transition:border-color .15s, box-shadow .15s; }
            .nx2-car:hover:not(:disabled) { border-color:#bdbdbd; }
            .nx2-car.on { border-color:#f90370; box-shadow:0 0 0 1px #f90370; background:#fff8fb; }
            .nx2-car:disabled { opacity:.45; cursor:not-allowed; }
            .nx2-car-top { display:flex; align-items:center; gap:10px; }
            .nx2-car-logo { width:34px; height:34px; border-radius:6px; border:1px solid #eee; background:#fff; display:flex; align-items:center; justify-content:center; overflow:hidden; flex-shrink:0; }
            .nx2-car-n { font-size:13px; font-weight:500; color:#111; flex:1; }
            .nx2-best { font-size:10.5px; color:#0f766e; background:#ecfdf5; border:1px solid #c7eedc; border-radius:4px; padding:1px 6px; }
            .nx2-car-g { display:grid; grid-template-columns:repeat(3,1fr); gap:6px; margin-top:10px; }
            .nx2-car-g span { display:block; font-size:10.5px; color:#888; }
            .nx2-car-g b { font-size:13px; font-weight:500; color:#111; }
            .nx2-car-g .dep { color:#15803d; }
            .nx2-check { position:absolute; top:10px; right:10px; width:18px; height:18px; border-radius:50%; background:#f90370; display:flex; align-items:center; justify-content:center; }
            .nx2-toggle { width:100%; display:flex; align-items:center; justify-content:space-between; border:1px solid #e5e5e5; border-radius:8px; background:#fafafa; padding:11px 14px; cursor:pointer; font-family:inherit; font-size:13px; color:#111; }
            .nx2-toggle:hover { background:#f4f4f4; }
            .nx2-toggle .warn { font-size:11.5px; color:#b45309; margin-left:8px; }
            .nx2-form { display:grid; grid-template-columns:1fr 1fr; gap:10px; animation:nx2In .25s ease both; }
            .nx2-cols { display:grid; grid-template-columns:1fr 1.15fr 1fr; gap:0; align-items:stretch; margin:-18px -22px -22px; }
            .nx2-c { padding:18px 22px 22px; min-width:0; }
            .nx2-c + .nx2-c { border-left:1px solid #eee; }
            .nx2-first { margin-top:0 !important; }
            .nx2-warn { font-size:11px; font-weight:400; color:#b45309; background:#fff8eb; border:1px solid #f5dcaa; border-radius:4px; padding:1px 6px; }
            @media (max-width:1100px) { .nx2-cols { grid-template-columns:1fr 1fr; } .nx2-c:nth-child(3) { grid-column:1 / -1; border-left:none; border-top:1px solid #eee; } }
            @media (max-width:760px) { .nx2-cols { grid-template-columns:1fr; } .nx2-c + .nx2-c { border-left:none; border-top:1px solid #eee; } }
            .nx2-form .full { grid-column:1 / -1; }
            .nx-input { width:100%; padding:9px 11px; border:1px solid #dcdcdc; border-radius:6px; font-size:13px; font-family:inherit; outline:none; box-sizing:border-box; background:#fff; color:#111; transition:border-color .15s, box-shadow .15s; }
            .nx-input:focus { border-color:#f90370; box-shadow:0 0 0 2px rgba(249,3,112,.12); }
            .nx-label { font-size:11.5px; font-weight:500; color:#f90370; display:block; margin-bottom:5px; }
            .nx2-foot { border-top:1px solid #eee; padding:12px 22px 14px; display:flex; align-items:center; gap:14px; background:#fff; }
            .nx2-fs { flex:1; font-size:12px; color:#666; line-height:1.5; }
            .nx2-fs b { color:#111; font-weight:500; }
            .nx2-btn { height:38px; padding:0 16px; border-radius:6px; font-family:inherit; font-size:13.5px; font-weight:400; cursor:pointer; display:flex; align-items:center; gap:8px; transition:background .15s; }
            .nx2-btn.ghost { border:1px solid #dcdcdc; background:#fff; color:#111; } .nx2-btn.ghost:hover { background:#f5f5f5; }
            .nx2-btn.gen { border:none; background:#f90370; color:#fff; } .nx2-btn.gen:hover { background:#e00265; } .nx2-btn.gen:disabled { background:#d0d0d0; cursor:not-allowed; }
            .nx2-confirm { background:#fff8fb; border-top:1px solid #fbd0e3; }
            @media (max-width:640px) { .nx2-carriers, .nx2-form { grid-template-columns:1fr; } .nx2-foot { flex-wrap:wrap; } }
          `}</style>
          <div className="nx2" onClick={e=>e.stopPropagation()}>
            <div className="nx2-head">
              <div style={{display:'flex',alignItems:'center',gap:12}}>
                <div className="nx2-logo"><img src="/nexus-logo.png" alt="Nexus" style={{width:20,height:20,objectFit:'contain'}}/></div>
                <div>
                  <div style={{fontSize:16,fontWeight:600,color:'#111'}}>Generar guía</div>
                  <div style={{fontSize:12,color:'#888',marginTop:1}}>
                    {nexusOrderInfo?.tracking_number ? `Guía actual: ${nexusOrderInfo.tracking_number}` : nexusOrderInfo?.has_nexus ? 'Nexus · Sin guía generada' : 'Pedido no vinculado a Nexus'}
                  </div>
                </div>
              </div>
              <button className="nx2-x" onClick={()=>{ setShowNexusModal(false); setNexusMsg(''); setNxConfirm(false); }} aria-label="Cerrar"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>
            </div>

            <div className="nx2-body">
            {nexusMsg && (
              <div style={{margin:'0 0 12px',background:nexusMsg.includes('✅')?'#f0fdf4':nexusMsg.includes('⚠️')?'#fffbeb':'#fef2f2',border:`1px solid ${nexusMsg.includes('✅')?'#bbf7d0':nexusMsg.includes('⚠️')?'#fde68a':'#fee2e2'}`,borderRadius:8,padding:'11px 14px',fontSize:13,color:nexusMsg.includes('✅')?'#16a34a':nexusMsg.includes('⚠️')?'#92400e':'#ef4444',flexShrink:0,animation:'nxFadeCol .3s ease both'}}>{nexusMsg}</div>
            )}

            {nexusLoading && !nexusMsg && (
              <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',padding:'56px 0',flex:1,gap:12}}>
                <div className="spinner" style={{width:24,height:24,borderWidth:3}}/>
                <span style={{fontSize:13,color:'#aaa'}}>Cargando datos del pedido...</span>
              </div>
            )}

            {nexusLoading && !nexusMsg && (
              <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',padding:'48px 0',gap:12}}>
                <div className="spinner" style={{width:22,height:22}}/>
                <span style={{fontSize:13,color:'#999'}}>Cargando datos del pedido…</span>
              </div>
            )}

            {!nexusOrderInfo?.has_nexus && !nexusLoading && (
              <div style={{margin:'0',background:'#fffbeb',border:'1px solid #fde68a',borderRadius:8,padding:'14px 16px',fontSize:13,color:'#92400e',display:'flex',alignItems:'center',gap:10,animation:'nxFadeCol .3s ease both'}}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                Este pedido aún no está vinculado a Nexus. Espera a que llegue el webhook de <strong>nueva_orden</strong>.
              </div>
            )}

            {nexusOrderInfo?.has_nexus && !nexusLoading && (() => {
              const ord = shopifyOrders.find(o=>o.fulfillment_status!=='fulfilled') || shopifyOrders[0];
              const isPaid = ord?.financial_status === 'paid' || (ord?.metodo && ord.metodo !== 'COD' && ord.metodo !== 'cash_on_delivery' && ord.metodo !== '—');
              const imgSrc = nexusProductImg || null;
              const productName = nexusProductTitle || ord?.product || 'Producto';
              // Todos los productos de TODOS los pedidos a despachar (no cumplidos).
              const _guideOrders = (() => {
                const pend = shopifyOrders.filter(o => o.fulfillment_status !== 'fulfilled');
                return pend.length ? pend : (shopifyOrders.length ? [shopifyOrders[0]] : []);
              })();
              const _guideName = (s) => { const t = (s == null ? '' : String(s)); const tt = t.trim(); return (tt !== '' && tt !== '—' && tt !== '–') ? t : ''; };
              const guideItems = _guideOrders.flatMap(o =>
                (o.items_detalle && o.items_detalle.length > 0
                  ? o.items_detalle
                  : [{ producto: o.product, variante: o.variant, cantidad: 1 }]
                ).map(it => ({
                  producto: _guideName(it.producto) || _guideName(it.title) || _guideName(o.product) || 'Producto',
                  variante: it.variante || it.variant_title || o.variant || '',
                  cantidad: it.cantidad || it.quantity || 1,
                }))
              );
              const nf = (key) => nexusForm[key] || '';
              const setNf = (key,val) => setNexusForm(p=>({...p,[key]:val}));
              const CARRIERS = GUIDE_CARRIERS.filter(id => !(sinRecaudo && id === 'coordinadora')).map(id=>({id,...CARRIERS_MAP[id]}));
              const sel = shippingQuotes.find(q => q.carrier === selectedCarrier);
              const quotesFor = (id) => usableQuote(shippingQuotes, failedCarriers, id, sinRecaudo);
              const _usable = CARRIERS.map(c => quotesFor(c.id)).filter(Boolean);
              const cheapest = _usable.length ? _usable.reduce((m, q) => (Number(q.total||0) < Number(m.total||0) ? q : m)).carrier : null;
              const money = (v) => `$${Number(v||0).toLocaleString('es-CO')}`;
              const missing = !nexusForm.city_code || !String(nf('shipping_address')).trim() || !String(nf('customer_name')).trim();
              return (
                <div className="nx2-cols">
                  <div className="nx2-c">
                  {/* 1 · Resumen */}
                  <div className="nx2-sum">
                    <div style={{minWidth:0}}>
                      <div className="nx2-ped">Pedido #{ord?.order_number || nexusOrderInfo?.shopify_order_id || '—'}</div>
                      <div className="nx2-cli">{nf('customer_name') || '—'}</div>
                      <div className="nx2-city">{(nf('city') || cityQuery || 'Ciudad sin definir')}{nf('state') ? `, ${nf('state')}` : ''} · {nf('customer_phone') || 'sin teléfono'}</div>
                    </div>
                    <div>
                      <div className="nx2-total">{money(nexusForm.total_amount||ord?.total)}</div>
                      <div className="nx2-tl">Total a recaudar</div>
                      <div style={{textAlign:'right'}}><span className="nx2-pay" style={{background:isPaid?'#effaf4':'#fff8eb',color:isPaid?'#15803d':'#b45309',border:`1px solid ${isPaid?'#c9ead8':'#f5dcaa'}`}}>{`${ord?.metodo && ord.metodo!=='—' ? ord.metodo+' · ' : ''}${isPaid?'Pagado':'Pendiente de pago'}`}</span></div>
                    </div>
                    {nexusOrderInfo?.tracking_number && (
                      <div className="nx2-guide">
                        <div style={{fontSize:12.5,color:'#555'}}>
                          Guía actual <a href={`https://www.coordinadora.com/portafolio-de-servicios/servicios-en-linea/rastrear-guias/?guia=${nexusOrderInfo.tracking_number}`} target="_blank" rel="noreferrer" style={{color:'#f90370',textDecoration:'none',fontWeight:500,marginLeft:4}}>{nexusOrderInfo.tracking_number}</a>
                          {(nexusOrderInfo?.carrier || nexusOrderInfo?.carrier_status) && <span style={{color:'#888'}}> · {CARRIERS_MAP[nexusOrderInfo?.carrier]?.label || nexusOrderInfo?.carrier || ''}{nexusOrderInfo?.carrier_status ? ` · ${nexusOrderInfo.carrier_status}` : ''}</span>}
                        </div>
                        <button onClick={async()=>{
                          try {
                            const r = await fetch(`${API}/nexus/orders/${nexusOrderInfo.shopify_order_id}/label`,{headers:authH()});
                            const d = await r.json();
                            if (d.pdf_b64) { const w=window.open(''); w.document.write(`<iframe src="data:application/pdf;base64,${d.pdf_b64}" style="width:100%;height:100vh;border:none;"></iframe>`); }
                            else alert('PDF no disponible aún');
                          } catch { alert('Error al obtener el PDF'); }
                        }} className="nx2-pdf">
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="15" y2="15"/></svg>
                          PDF
                        </button>
                      </div>
                    )}
                  </div>

                  {/* 2 · Productos */}
                  <div className="nx2-sec">
                    <div className="nx2-st">Productos <small>{guideItems.length} {guideItems.length===1?'producto':'productos'}</small></div>
                    {nxVariants ? (() => {
                      const editable = nxVariants.editable !== false && !nexusOrderInfo?.tracking_number;
                      const pend = nxVarPending();
                      return (
                        <>
                          <div className="nx2-items">
                            {nxVariants.items.map(it => {
                              const vars = it.variants || [];
                              const prs = nxPairs(it);
                              const selOf = (p) => nxVarSel[`${it.item_id}:${p.pair}`] ?? p.current_variant_id;
                              const varOf = (id) => vars.find(v => Number(v.variant_id) === Number(id));
                              const img = varOf(selOf(prs[0]))?.image_url || it.product_image;
                              return (
                                <div key={it.item_id} className="nx2-item" style={{alignItems:'flex-start'}}>
                                  {img ? <img src={img} alt="" className="nx2-img" onError={e=>e.target.style.display='none'}/> : <div className="nx2-img"/>}
                                  <div style={{flex:1,minWidth:0}}>
                                    <div style={{fontSize:13,color:'#111'}}>{it.product_name}</div>
                                    {vars.length > 0 ? prs.map(p => {
                                      const sid = selOf(p);
                                      return (
                                        <div key={p.pair} style={{display:'flex',alignItems:'center',gap:8,marginTop:6}}>
                                          {it.is_combo && <span style={{fontSize:11.5,color:'#666',whiteSpace:'nowrap',minWidth:38}}>Par {p.pair}</span>}
                                          <select className="nx-input" style={{padding:'7px 9px'}} disabled={!editable || nxVarSaving} value={sid ?? ''}
                                            onChange={e => setNxVarSel(s => ({ ...s, [`${it.item_id}:${p.pair}`]: Number(e.target.value) }))}>
                                            {!varOf(sid) && <option value="">{p.current_variant || 'Elige color / talla'}</option>}
                                            {vars.map(v => <option key={v.variant_id} value={v.variant_id} disabled={v.available === false}>{v.label}{v.available === false ? ' (no disponible)' : ''}</option>)}
                                          </select>
                                        </div>
                                      );
                                    }) : (it.current_variant ? <div style={{fontSize:11.5,color:'#888',marginTop:1}}>{it.current_variant}</div> : null)}
                                  </div>
                                  <div style={{fontSize:12,color:'#666'}}>x{it.quantity || 1}</div>
                                </div>
                              );
                            })}
                          </div>
                          {!editable && (
                            <div style={{marginTop:8,fontSize:12,color:'#92400e',background:'#fffbeb',border:'1px solid #fde68a',borderRadius:6,padding:'7px 10px'}}>
                              {nxVariants.blocked_reason || 'Anula la guía para cambiar la talla o el color.'}
                            </div>
                          )}
                          {editable && pend.length > 0 && (
                            <div style={{marginTop:8,display:'flex',alignItems:'center',justifyContent:'space-between',gap:10,fontSize:12,color:'#555'}}>
                              <span>Cambiaste {pend.length === 1 ? 'una talla/color' : `${pend.length} tallas/colores`}. Se guarda al generar la guía.</span>
                              <button className="nx2-btn ghost" style={{padding:'5px 10px',fontSize:12}} disabled={nxVarSaving} onClick={saveNexusVariants}>{nxVarSaving ? 'Guardando…' : 'Guardar ahora'}</button>
                            </div>
                          )}
                          <div style={{marginTop:8,fontSize:11.5,color:'#999'}}>El contenido de la guía lo arma Nexus con el producto y la variante elegida.</div>
                        </>
                      );
                    })() : (<>
                    <div className="nx2-items">
                      {guideItems.map((it, idx) => (
                        <div key={idx} className="nx2-item">
                          {idx===0 && imgSrc ? <img src={imgSrc} alt="" className="nx2-img" onError={e=>e.target.style.display='none'}/> : <div className="nx2-img"/>}
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{fontSize:13,color:'#111'}}>{it.producto}</div>
                            {it.variante && it.variante !== '—' && <div style={{fontSize:11.5,color:'#888',marginTop:1}}>{it.variante}</div>}
                          </div>
                          <div style={{fontSize:12,color:'#666'}}>x{it.cantidad}</div>
                        </div>
                      ))}
                    </div>
                    <div style={{marginTop:10}}>
                      <label className="nx-label">Variante / contenido de la guía</label>
                      <input className="nx-input" value={nf('content_description')} onChange={e=>setNf('content_description',e.target.value)} placeholder="Ej: MARRON / 43"/>
                    </div>
                    </>)}
                  </div>

                  </div>
                  <div className="nx2-c">
                  {/* 4 · Datos de envío */}
                  <div className="nx2-sec nx2-first">
                    <div className="nx2-st">Datos de envío{missing && <span className="nx2-warn">Faltan datos</span>}</div>
                    {(
                      <div className="nx2-form">
                        <div><label className="nx-label">Nombre completo</label><input className="nx-input" value={nf('customer_name')} onChange={e=>setNf('customer_name',e.target.value)}/></div>
                        <div><label className="nx-label">Teléfono</label><input className="nx-input" value={nf('customer_phone')} onChange={e=>setNf('customer_phone',e.target.value)}/></div>
                        <div style={{position:'relative'}}>
                          <label className="nx-label">Ciudad</label>
                          <input className="nx-input"
                            value={cityQuery}
                            onChange={e => {
                              const v = e.target.value;
                              setCityQuery(v);
                              setNf('city',''); setNf('city_code',''); setNf('state','');
                              if (cityTimeoutRef.current) clearTimeout(cityTimeoutRef.current);
                              if (v.length >= 2) {
                                setCitySearching(true);
                                cityTimeoutRef.current = setTimeout(async () => {
                                  try {
                                    const r = await fetch(`${API}/nexus/cities?q=${encodeURIComponent(v)}`, { headers: authH() });
                                    if (r.ok) { const d = await r.json(); setCitySuggestions(Array.isArray(d) ? d : []); }
                                  } catch(_) {}
                                  setCitySearching(false);
                                }, 350);
                              } else { setCitySuggestions([]); setCitySearching(false); }
                            }}
                            placeholder="Escribe para buscar ciudad..."
                            style={{borderColor: nexusForm.city_code ? '#6ee7b7' : undefined, background: nexusForm.city_code ? '#f0fdf4' : undefined}}
                            onFocus={e=>{ if (!nexusForm.city_code) e.target.style.borderColor='#6ee7b7'; }}
                            onBlur={e=>{ setTimeout(()=>setCitySuggestions([]),200); if (!nexusForm.city_code) e.target.style.borderColor=''; }}
                          />
                          {nexusForm.city_code && <div style={{fontSize:10,color:'#16a34a',marginTop:3,fontWeight:600}}>DANE: {nexusForm.city_code}</div>}
                          {citySearching && <div style={{fontSize:11,color:'#999',marginTop:3}}>Buscando...</div>}
                          {citySuggestions.length > 0 && (
                            <div style={{position:'absolute',top:'100%',left:0,right:0,background:'#fff',border:'1.5px solid #e8e8e6',borderRadius:10,marginTop:4,maxHeight:180,overflowY:'auto',zIndex:100,boxShadow:'0 8px 24px rgba(0,0,0,0.1)'}}>
                              {citySuggestions.map((c, i) => (
                                <div key={i}
                                  onMouseDown={e => {
                                    e.preventDefault();
                                    const nombre = c.nombre || c.city || '';
                                    const depto = c.departamento || c.department || '';
                                    const code = c.codigo_dane || c.city_code || '';
                                    setNf('city', nombre); setNf('state', depto); setNf('city_code', code);
                                    setCityQuery(`${nombre}, ${depto}`);
                                    setCitySuggestions([]);
                                    if (code) {
                                      const total = parseInt(nexusForm.total_amount) || 0;
                                      if (total > 0) {
                                        setQuotesLoading(true); setShippingQuotes([]); setFailedCarriers({});
                                        fetch(`${API}/nexus/quote-by-city`, { method:'POST', headers:authH({'Content-Type':'application/json'}), body:JSON.stringify(sinRecaudo ? {city_code:code,total,sin_recaudo:true} : {city_code:code,total}) })
                                          .then(r=>r.ok?r.json():null).then(d=>{ if(d?.quotes) setShippingQuotes(d.quotes); }).catch(()=>{}).finally(()=>setQuotesLoading(false));
                                      }
                                    }
                                  }}
                                  style={{padding:'9px 14px',fontSize:12,cursor:'pointer',borderBottom:i<citySuggestions.length-1?'1px solid #f0f0ee':'none',transition:'background 0.15s'}}
                                  onMouseEnter={e=>e.target.style.background='#f8f8f6'}
                                  onMouseLeave={e=>e.target.style.background='#fff'}
                                >
                                  <span style={{fontWeight:600,color:'#0a0a0a'}}>{c.nombre || c.city}</span>
                                  <span style={{color:'#999',marginLeft:6,fontSize:11}}>{c.departamento || c.department}</span>
                                  <span style={{color:'#bbb',marginLeft:4,fontSize:10}}>({c.codigo_dane || c.city_code})</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        <div>
                          <label className="nx-label">Departamento</label>
                          <input className="nx-input" value={nf('state')} readOnly placeholder="Se llena al elegir la ciudad" style={{background:'#f6f6f6',color:'#666'}}/>
                        </div>
                        <div className="full"><label className="nx-label">Dirección y complementos</label><input className="nx-input" value={nf('shipping_address')} onChange={e=>setNf('shipping_address',e.target.value)}/></div>
                        <div><label className="nx-label">Correo electrónico</label><input className="nx-input" value={nf('customer_email')} onChange={e=>setNf('customer_email',e.target.value)} placeholder="opcional@email.com"/></div>
                        <div><label className="nx-label">Total a recaudar (COD)</label><input className="nx-input" value={nf('total_amount')} onChange={e=>setNf('total_amount',e.target.value.replace(/\D/g,''))}/></div>
                      </div>
                    )}
                  </div>
                  </div>
                  <div className="nx2-c">
                  {/* 3 · Transportadora */}
                  <div className="nx2-sec nx2-first">
                    <div className="nx2-st">Transportadora {quotesLoading && <small style={{display:'flex',alignItems:'center',gap:6}}><span className="spinner" style={{width:11,height:11}}/> Cotizando…</small>}</div>
                    <div className="nx2-mode">
                      <button className={!sinRecaudo ? 'on' : ''} disabled={quotesLoading || srForced} title={srForced ? 'El pedido ya está pagado: va sin recaudo' : undefined} onClick={()=>{ if (sinRecaudo) { setSinRecaudo(false); requoteOrder(false); } }}>Con recaudo</button>
                      <button className={sinRecaudo ? 'on' : ''} disabled={quotesLoading} onClick={()=>{ if (!sinRecaudo) { setSinRecaudo(true); requoteOrder(true); } }}>Sin recaudo</button>
                    </div>
                    {sinRecaudo && <div className="nx2-mode-note">{srForced ? 'Pedido ya pagado: va sin recaudo. ' : ''}El cliente no paga al recibir. Nexus descuenta producto + envío de tu billetera. Coordinadora no aplica sin recaudo.</div>}
                    <div className="nx2-carriers">
                      {CARRIERS.map(c => {
                        const q = quotesFor(c.id); const active = selectedCarrier === c.id;
                        return (
                          <button key={c.id} className={`nx2-car${active?' on':''}`} disabled={!q} onClick={()=> q && setSelectedCarrier(c.id)}>
                            <div className="nx2-car-top">
                              <div className="nx2-car-logo">{c.img ? <img src={c.img} alt="" style={{width:28,height:28,objectFit:'contain'}}/> : <span style={{fontSize:10,color:c.color}}>{c.label?.slice(0,3)}</span>}</div>
                              <span className="nx2-car-n">{c.label}</span>
                              {q && cheapest === c.id && !active && <span className="nx2-best">Más económica</span>}
                            </div>
                            {q ? (
                              <div className="nx2-car-g">
                                <div><span>Flete</span><b>{money(q.total)}</b></div>
                                <div><span>Tiempo</span><b>{q.delivery_time} día{String(q.delivery_time)!=='1'?'s':''}</b></div>
                                {sinRecaudo
                                  ? <div><span>Se descuenta</span><b>{q.wallet_cost ? money(q.wallet_cost) : 'Al generar'}</b></div>
                                  : <div><span>Se deposita</span><b className="dep">{money(q.value_deposited)}</b></div>}
                              </div>
                            ) : <div style={{fontSize:11.5,color:failedCarriers[c.id] ? '#b45309' : '#aaa',marginTop:10,lineHeight:1.35}}>{quotesLoading ? 'Cotizando…' : failedCarriers[c.id] ? `No disponible: ${failedCarriers[c.id]}` : shippingQuotes.length ? 'No disponible para esta ciudad' : 'Sin cotización'}</div>}
                            {active && <span className="nx2-check"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg></span>}
                          </button>
                        );
                      })}
                    </div>
                    {!quotesLoading && shippingQuotes.length === 0 && <div style={{fontSize:12,color:'#999',marginTop:8}}>No hay cotización todavía. Revisa la ciudad en los datos de envío.</div>}
                  </div>

                  </div>
                </div>
              );
            })()}
            </div>

            {nexusOrderInfo?.has_nexus && !nexusLoading && (() => {
              const sel = shippingQuotes.find(q => q.carrier === selectedCarrier);
              const money = (v) => `$${Number(v||0).toLocaleString('es-CO')}`;
              const carrierName = CARRIERS_MAP[selectedCarrier]?.label || selectedCarrier || 'la transportadora';
              return nxConfirm ? (
                <div className="nx2-foot nx2-confirm">
                  <div className="nx2-fs">¿Generar la guía con <b>{carrierName}</b>{sel ? <> por <b>{money(sel.total)}</b></> : null}? Esto crea un <b>envío real</b> en Nexus{sinRecaudo ? <> y descuenta {sel?.wallet_cost ? <b>{money(sel.wallet_cost)}</b> : 'el valor'} de tu billetera (sin recaudo)</> : null}.</div>
                  <button className="nx2-btn ghost" onClick={()=>setNxConfirm(false)}>Volver</button>
                  <button className="nx2-btn gen" disabled={nexusLoading || !selectedCarrier} onClick={()=>{ setNxConfirm(false); nexusGenerateGuide(); }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="3" width="15" height="13" rx="2"/><path d="M16 8h4l3 3v5a2 2 0 0 1-2 2h-1"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg> Sí, generar guía</button>
                </div>
              ) : (
                <div className="nx2-foot">
                  <div className="nx2-fs">{sel ? (sinRecaudo
                    ? <>{carrierName} · Sin recaudo · Envío <b>{money(sel.total)}</b>{sel.wallet_cost ? <> · Se descuenta <b>{money(sel.wallet_cost)}</b></> : null}</>
                    : <>{carrierName} · Envío <b>{money(sel.total)}</b> · Se deposita <b style={{color:'#15803d'}}>{money(sel.value_deposited)}</b></>) : 'Elige una transportadora'}</div>
                  <button className="nx2-btn ghost" onClick={()=>{ setShowNexusModal(false); setNexusMsg(''); }}>Cancelar</button>
                  <button className="nx2-btn gen" disabled={nexusLoading || !selectedCarrier} onClick={()=>setNxConfirm(true)}>
                    {nexusLoading ? <><div className="spinner" style={{width:13,height:13,borderTopColor:'#fff',borderColor:'rgba(255,255,255,0.3)'}}/> Generando…</> : <><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="3" width="15" height="13" rx="2"/><path d="M16 8h4l3 3v5a2 2 0 0 1-2 2h-1"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg> Generar guía</>}
                  </button>
                </div>
              );
            })()}
          </div>
        </div>
      )}
    </>
  );
}
